<template>
  <!-- <Layout> -->
  <div v-if="!pageLoading && parentDataLoaded">
    <KanbanSearch ref="kanbanSearch" :units="units" :repairs="repairsData" @filter-data="updateSearchedData">
    </KanbanSearch>

    <div class="row mt-3 mb-4">
      <div class="d-flex col-12 mb-2">
        <b-button variant="primary" class="btn btn-label btn-primary" v-b-modal.modal_addRepairs><i
          class="bx bx-plus label-icon"></i>Add Issues</b-button>
        <b-button v-b-toggle.collapse_columnView variant="primary" class="remove-view-button-title ml-2">Column Display</b-button>
        <b-button variant="primary" class="ml-2" :disabled="emptyWorkOrders.length === 0" v-b-modal.modal_emptyWorkOrders>
          Empty Work Orders ({{ emptyWorkOrders.length }})
        </b-button>
        <b-collapse id="collapse_columnView" style="z-index: 1010; position: absolute; left: 9.5rem; top: 2.3rem" class="mt-1 mb-2 ml-2">
          <div class="row">
            <div class="border border-primary rounded-lg" style="background-color: white;">
              <label class="mx-2">Columns Per Screen</label>
              <v-select @input="handleSelectedColumnWidth()" v-model="selectedColumnWidth" :options="['2', '3']" class="mb-3 mx-2" :clearable="false" />
              <label class="mx-2">Columns Visible</label>
              <b-form-checkbox
                v-model="visibleColumns.all"
                :value="true"
                :unchecked-value="false"
                class="mb-2 mx-2 checkbox-border"
                @change="(value) => columnDisplayCheckState('all', value)"
              >
                All Columns
              </b-form-checkbox>
              <b-form-checkbox
                v-model="visibleColumns.issues"
                :value="true"
                :unchecked-value="false"
                class="mb-2 mx-2"
                @change="(value) => columnDisplayCheckState('issues', value)"
              >
                Issues
              </b-form-checkbox>
              <b-form-checkbox
                v-model="visibleColumns.waitingToStart"
                :value="true"
                :unchecked-value="false"
                class="mb-2 mx-2"
                @change="(value) => columnDisplayCheckState('waitingToStart', value)"
              >
                Waiting to Start
              </b-form-checkbox>
              <b-form-checkbox
                v-model="visibleColumns.inProgress"
                :value="true"
                :unchecked-value="false"
                class="mb-2 mx-2"
                @change="(value) => columnDisplayCheckState('inProgress', value)"
              >
                In Progress
              </b-form-checkbox>
              <b-form-checkbox
                v-model="visibleColumns.waitingForDocs"
                :value="true"
                :unchecked-value="false"
                class="mb-2 mx-2"
                @change="(value) => columnDisplayCheckState('waitingForDocs', value)"
              >
                Waiting for Docs
              </b-form-checkbox>
              <b-form-checkbox
                v-model="visibleColumns.completed"
                :value="true"
                :unchecked-value="false"
                class="mb-2 mx-2"
                @change="(value) => columnDisplayCheckState('completed', value)"
              >
                Completed
              </b-form-checkbox>
              <b-form-checkbox
                v-model="visibleColumns.changesRequested"
                :value="true"
                :unchecked-value="false"
                class="mb-2 mx-2"
                @change="(value) => columnDisplayCheckState('changesRequested', value)"
              >
                Changes Requested
              </b-form-checkbox>
              <b-form-checkbox
                v-model="visibleColumns.submitted"
                :value="true"
                :unchecked-value="false"
                class="mb-2 mx-2"
                @change="(value) => columnDisplayCheckState('submitted', value)"
              >
                Submitted
              </b-form-checkbox>
            </div>
          </div>
        </b-collapse>
      </div>
      <div class="d-flex col-12">
        <b-button :disabled="selectedRepairs.length <= 0" @click="handleBulkDeleteIssues()" variant="primary"
          class="btn btn-danger mr-2">
          <i class="mdi mdi-delete-outline mr-1"></i> Delete Issues ({{ selectedRepairs.length }})
        </b-button>
        <b-button :disabled="selectedRepairs.length <= 0 || !selectedRepairsUnitsConsistent" variant="primary" v-b-modal.modal_addIssueToExistingWorkOrder
          class="btn btn-success mr-2">
          <i class="mdi mdi-plus mr-1"></i> Add to Existing WO ({{ selectedRepairs.length }})
        </b-button>
        <b-button :disabled="selectedRepairs.length <= 0" variant="primary" @click="handleCreateWorkOrderModalOpen()" v-b-modal.modal_createWorkOrder
          class="btn btn-success mr-2">
          <i class="mdi mdi-plus mr-1"></i> Create Work Order ({{ selectedRepairs.length }})
        </b-button>
        <NewIssuePrint
          :selectedIssues="selectedRepairs"
          :units="units"
        ></NewIssuePrint>
      </div>
    </div>

    <div v-if="workOrdersNotRendered.length > 0 && selectedIssuesColumnUnit === null && selectedInProgressUnit === null && selectedChangeRequestedUnit === null && selectedWaitingOnDocsUnit === null && selectedNotStartedUnit === null && selectedReadyForSubmitUnit === null && selectedCompletedUnit === null" class="row">
      <div v-for="wo in workOrdersNotRendered" :key="wo.view">
        <p class="ml-3"><span class="font-weight-bold text-danger text-underline" style="text-decoration: underline;" @click="populateWorkOrder(wo)"><i class="bx bxs-info-circle"></i>#{{wo.number}}</span> is not displayed in a column. You can view/edit this work order by clicking the link.</p>
      </div>
    </div>

    <!-- issues column -->
    <div class="scroll-container-wrapper">
      <div class="drag-container scroll-container">
        <div class="drag-list">
          <div v-if="visibleColumns.issues" :class="`drag-column scroll-column ${selectedColumnWidth == 3 ? 'column-width-3' : 'column-width-2'}`">
            <div class="card">
              <div class="card-body">
                <b-form-select class="float-right w-25" v-model="selectedIssuesColumnUnit" :options="unitListWithExtraOption"
                  value-field="UID" text-field="name">
                </b-form-select>
                <span class="drag-column-header">
                  <h4 class="card-title mb-4 pb-1">New Issues</h4>
                </span>
                <div class="wo-column-card-container">
                  <div v-for="repair in filteredRepairs" :key="repair.id">
                    <div v-if="repair.isVisible" class="card task-box mb-0">
                      <div class="card-body" :class="repair.selected ? 'bg-success' : ''">
                        <div class="float-right ml-2">
                          <b-dropdown right variant="white" class="float-right" toggle-class="p-0">
                            <template slot="button-content">
                              <i class="mdi mdi-dots-vertical m-0 text-muted h5"></i>
                            </template>
                            <b-dropdown-item v-b-modal.modal_editNewRepair
                              @click="handleEditNewRepair(repair)">Edit</b-dropdown-item>
                            <b-dropdown-item @click="deleteNewRepair(repair.id)">Delete</b-dropdown-item>
                          </b-dropdown>
                          <b-form-checkbox size="lg" class="float-right" v-model="repair.selected"
                            @change="spliceRepairs(repair)"></b-form-checkbox>
                        </div>
                        <div class="text-wrap">
                          <h5 class="font-size-15">
                            <p :class="`${repair.selected ? 'text-white' : 'text-dark'} text-wrap`" v-html="repair.issue ? repair.issue.replace(/\n/g, '<br>') : ''"></p>
                          </h5>
                        </div>
                        <p :class="`${repair.selected ? 'text-white' : 'text-muted'} mb-4`">{{ repair.date.seconds ? momentInstance(repair.date.toDate()).format('ll') :
                          momentInstance(repair.date).format('ll') }}</p>
                          <div class="d-flex w-100">
                            <p :class="`${repair.selected ? 'text-white' : 'text-muted'}`">Unit</p>
                            <h6>
                              <b-badge v-for="unit in repair.units" :key="unit" :variant="'primary'" class="ml-2">{{
                                getUpToDateUnitName(unit) !== null ? getUpToDateUnitName(unit) : `` }}</b-badge>
                            </h6>
                          </div>
                          <div v-if="repair.submitted">
                            <i class="bx bxs-archive-in" style="color: rgb(182 182 182); vertical-align: middle;"></i> Submitted By
                            {{ repair.submitted.name ? repair.submitted.name : ''}} on
                            {{ repair.submitted.time ? momentInstance(repair.submitted.time).format('lll') : '' }}
                          </div>
                      </div>
                    </div>
                  </div>
                </div>
                <!-- </draggable> -->
              </div>
            </div>
          </div>
          <!-- end col-->

          <!-- waiting to start column -->
          <WorkOrderColumn
            v-if="visibleColumns.waitingToStart"
            :isLoading="isLoadingWorkOrders"
            :columnTitle="'Waiting to Start'"
            :selectedUnitDataProp="'selectedNotStartedUnit'"
            :unitListWithExtraOption="unitListWithExtraOption"
            :allUnits="units"
            :workOrders="notStartedWorkOrders"
            :selectedColumnWidth="selectedColumnWidth"
            @populate-work-order="populateWorkOrder"
            @handle-show-work-order-issue-details="handleShowWorkOrderIssueDetails"
            @update-column-unit-filter="updateColumnUnitFilter"
          />
          <!-- end col-->

          <!-- in progress column -->
          <WorkOrderColumn
            v-if="visibleColumns.inProgress"
            :isLoading="isLoadingWorkOrders"
            :columnTitle="'In Progress'"
            :selectedUnitDataProp="'selectedInProgressUnit'"
            :unitListWithExtraOption="unitListWithExtraOption"
            :allUnits="units"
            :workOrders="inProgressWorkOrders"
            :selectedColumnWidth="selectedColumnWidth"
            @populate-work-order="populateWorkOrder"
            @handle-show-work-order-issue-details="handleShowWorkOrderIssueDetails"
            @update-column-unit-filter="updateColumnUnitFilter"
          />
          <!-- end col-->

          <!-- Waiting on Docs column -->
          <WorkOrderColumn
            v-if="visibleColumns.waitingForDocs"
            :isLoading="isLoadingWorkOrders"
            :columnTitle="'Waiting for Docs'"
            :selectedUnitDataProp="'selectedWaitingOnDocsUnit'"
            :unitListWithExtraOption="unitListWithExtraOption"
            :allUnits="units"
            :workOrders="waitingOnDocsWorkOrders"
            :selectedColumnWidth="selectedColumnWidth"
            @populate-work-order="populateWorkOrder"
            @handle-show-work-order-issue-details="handleShowWorkOrderIssueDetails"
            @update-column-unit-filter="updateColumnUnitFilter"
          />
          <!-- end col-->

          <!-- Completed column -->
          <WorkOrderColumn
            v-if="visibleColumns.completed"
            :columnTitle="'Completed'"
            :selectedUnitDataProp="'selectedReadyForSubmitUnit'"
            :unitListWithExtraOption="unitListWithExtraOption"
            :allUnits="units"
            :workOrders="completedWorkOrders"
            :selectedColumnWidth="selectedColumnWidth"
            :isLoading="isLoadingWorkOrders"
            @populate-work-order="populateWorkOrder"
            @handle-show-work-order-issue-details="handleShowWorkOrderIssueDetails"
            @update-column-unit-filter="updateColumnUnitFilter"
          />
          <!-- end col-->

          <!-- changes requested column -->
          <WorkOrderColumn
            v-if="visibleColumns.changesRequested"
            :isLoading="isLoadingWorkOrders"
            :columnTitle="'Changes Requested'"
            :selectedUnitDataProp="'selectedChangeRequestedUnit'"
            :unitListWithExtraOption="unitListWithExtraOption"
            :allUnits="units"
            :workOrders="changesRequestedWorkOrders"
            :selectedColumnWidth="selectedColumnWidth"
            @populate-work-order="populateWorkOrder"
            @handle-show-work-order-issue-details="handleShowWorkOrderIssueDetails"
            @update-column-unit-filter="updateColumnUnitFilter"
          />
          <!-- end col-->

          <!-- submitted column -->
          <WorkOrderColumn
            v-if="visibleColumns.submitted"
            :isLoading="isLoadingWorkOrders"
            :columnTitle="'Submitted'"
            :selectedUnitDataProp="'selectedChangeRequestedUnit'"
            :unitListWithExtraOption="unitListWithExtraOption"
            :allUnits="units"
            :workOrders="submittedWorkOrders"
            :selectedColumnWidth="selectedColumnWidth"
            @populate-work-order="populateWorkOrder"
            @handle-show-work-order-issue-details="handleShowWorkOrderIssueDetails"
            @update-column-unit-filter="updateColumnUnitFilter"
            @update-submitted-column-filter="updateSubmittedColumnFilter"
          />
          <!-- end col-->
        </div>
      </div>
    </div>

    <!-- Add issues Modal -->
    <b-modal id="modal_addRepairs" title="Add Issues" title-class="font-18" ok-title="Submit" size="lg" @ok="addRepairs">
      <div>
        <div class="mb-4">
          <div class="form-group">
            <label>Date</label>
            <date-picker v-model="selectedWorkOrder.date" lang="en" :clearable="false"
              :class="{ 'is-invalid-datepicker': submitErrorDate === false }"></date-picker>
            <small v-if="submitErrorDate === false" class="text-danger d-block mt-1">{{ dateErrorMessage }}</small>
          </div>
          <div class="form-group">
            <label id="lbl_addWorkRequest_unit">Unit <i class="bx bxs-help-circle"></i></label>
            <b-tooltip target="lbl_addWorkRequest_unit">Multiple units can be selected.</b-tooltip>
            <v-select :options="activeUnits" :reduce="unit => unit.UID" label="name"
              v-model="selectedWorkOrder.units" multiple
              :class="{ 'is-invalid-vselect': submitErrorUnit === false }"></v-select>
            <small v-if="submitErrorUnit === false" class="text-danger">{{ unitErrorMessage }}</small>
          </div>
          <div class="form-group">
            <label>Odometer</label>
            <b-form-input placeholder="Odometer..." v-model.trim="selectedWorkOrder.odometer"></b-form-input>
            <!-- <b-form-invalid-feedback id="input-live-feedback">
              {{ odometerIssueErrorMessage }}
            </b-form-invalid-feedback> -->
          </div>
          <div class="form-group">
            <label>Hour Meter</label>
            <b-form-input placeholder="Hour Meter..." v-model.trim="selectedWorkOrder.hourMeter"></b-form-input>
            <!-- <b-form-invalid-feedback id="input-live-feedback">
              {{ hourMeterIssueErrorMessage }}
            </b-form-invalid-feedback> -->
          </div>
        </div>

        <b-table :items="newWorkOrderRepairs" :fields="newRepairFields" bordered>
          <template #table-colgroup="scope">
            <col v-for="field in scope.fields" :key="field.key"
              :style="{ width: field.key === 'index' ? '10%' : '90%' }" />
          </template>
          <template v-slot:cell(index)="data">
            <b-button size="sm" variant="danger" @click="() => {
              newWorkOrderRepairs.splice(data.index, 1);
            }
              ">Delete</b-button>
          </template>
        </b-table>
        <div class="form-group">
          <label>Issue</label>
          <b-form-textarea id="textarea" v-model="newIssue" placeholder="Enter the issue..." cols="10" rows="3"
            max-rows="6" wrap="hard" :state="submitErrorIssue === null
              ? validateEntry('issue')
              : submitErrorIssue
              "></b-form-textarea>
          <b-form-invalid-feedback id="input-live-feedback">
            {{ issueErrorMessage }}
          </b-form-invalid-feedback>
        </div>
        <b-button :disabled="newIssue === null || newIssue.trim() === ''" variant="primary" @click="() => {
          let index = newWorkOrderRepairs.push({ issue: newIssue });
          newWorkOrderRepairs[index - 1] = {
            index: index - 1,
            issue: newIssue,
          };
          newIssue = null;
        }
          ">Add Issue</b-button>
        <small v-if="submitErrorAddIssue === false" class="text-danger d-block mt-1">Please add issue</small>
      </div>
    </b-modal>
    <!-- //Add issues Modal -->

    <!-- Edit Issue Modal -->
    <b-modal id="modal_editNewRepair" title="Edit Issue" title-class="font-18" ok-title="Submit" size="lg"
      @ok="editNewRepair" :ok-disabled="!repairToEdit.units || repairToEdit.units.length === 0">
      <div>
        <div class="mb-4">
          <div class="form-group">
            <label>Date</label>
            <date-picker v-model="repairToEdit.date" lang="en" :clearable="false"></date-picker>
          </div>
          <div class="form-group">
            <label id="lbl_addWorkRequest_unit">Unit <i class="bx bxs-help-circle"></i></label>
            <b-tooltip target="lbl_addWorkRequest_unit">Multiple units can be selected.</b-tooltip>
            <v-select :options="units" :reduce="unit => unit.UID" label="name" v-model="repairToEdit.units" multiple
              :class="{ 'is-invalid-vselect': !repairToEdit.units || repairToEdit.units.length === 0 }">
              <template slot="selected-option" slot-scope="option">
                <span>{{ !option.dateDeactivated ? getUpToDateUnitName(option.UID) : `${getUpToDateUnitName(option.UID)} (Deactivated)` }}</span>
              </template>
              <template slot="option" slot-scope="option">
                <span v-if="!option.dateDeactivated">
                  {{ getUpToDateUnitName(option.UID) }}
                </span>
                <!-- don't render deactivated unit as an option -->
                <span v-else class="deactivated-option"></span>
              </template>
            </v-select>
            <small v-if="!repairToEdit.units || repairToEdit.units.length === 0" class="text-danger">
              Please enter at least one unit.
            </small>
          </div>
          <div class="form-group">
            <label>Odometer</label>
            <b-form-input placeholder="Odometer..." v-model.trim="repairToEdit.odometer" :state="submitErrorEditOdometerIssue === null
              ? validateEntry('odometer-issue-edit')
              : submitErrorEditOdometerIssue
              "></b-form-input>
            <b-form-invalid-feedback id="input-live-feedback">
              {{ odometerIssueEditErrorMessage }}
            </b-form-invalid-feedback>
          </div>
          <div class="form-group">
            <label>Hour Meter</label>
            <b-form-input placeholder="Hour Meter..." v-model.trim="repairToEdit.hourMeter" :state="submitErrorEditHourMeterIssue === null
              ? validateEntry('hourMeter-issue-edit')
              : submitErrorEditHourMeterIssue
              "></b-form-input>
            <b-form-invalid-feedback id="input-live-feedback">
              {{ hourMeterEditIssueErrorMessage }}
            </b-form-invalid-feedback>
          </div>
        </div>
        <div class="form-group">
          <label>Issue</label>
          <b-form-textarea id="textarea" v-model="repairToEdit.issue" :placeholder="repairToEdit.issue" cols="10" rows="3"
            max-rows="6" wrap="hard" :state="submitErrorIssue === null
              ? validateEntry('editNewIssue')
              : submitErrorIssue
              "></b-form-textarea>
          <b-form-invalid-feedback :state="submitErrorIssue === null
            ? validateEntry('editNewIssue')
            : submitErrorIssue
            ">
            {{ editIssueErrorMessage }}
          </b-form-invalid-feedback>
        </div>
      </div>
    </b-modal>
    <!-- /Edit Issue Modal -->

    <!-- Create Work Order Modal -->
    <b-modal id="modal_createWorkOrder" title="Create Work Order" title-class="font-18" ok-title="Submit" size="lg"
      @ok="createWorkOrder">
      <div>
        <div class="mb-4 mt-4">
          <div class="form-group">
            <label>Date</label>
            <date-picker v-model="selectedWorkOrder.date" lang="en" :clearable="false"></date-picker>
          </div>
        </div>

        <div class="form-group">
          <label>Odometer</label>
          <b-form-input placeholder="Odometer..." v-model="selectedWorkOrder.odometer" :state="odometerValid"></b-form-input>
          <b-form-invalid-feedback id="input-live-feedback">
            {{ odometerErrorMessage }}
          </b-form-invalid-feedback>
        </div>
        <div class="form-group">
          <label>Hour Meter</label>
          <b-form-input placeholder="Hour Meter..." v-model="selectedWorkOrder.hourMeter" :state="hourMeterValid"></b-form-input>
          <b-form-invalid-feedback id="input-live-feedback">
            {{ hourMeterErrorMessage }}
          </b-form-invalid-feedback>
        </div>
        <div class="form-group">
          <label>Pre-Trip Deficiency</label>
          <b-form-checkbox v-model="selectedWorkOrder.preTripDeficiency"></b-form-checkbox>
        </div>
        <div class="form-group">
          <label>Notes</label>
          <b-form-textarea v-model.trim="selectedWorkOrder.generalNotes"
            placeholder="General notes..." rows="3"
            maxlength="250"
          ></b-form-textarea>
        </div>
        <b-table :items="selectedRepairs" :fields="newRepairFields" bordered>
          <template #table-colgroup="scope">
            <col v-for="field in scope.fields" :key="field.key"
              :style="{ width: field.key === 'index' ? '10%' : '90%' }" />
          </template>
          <template v-slot:cell(index)="data">
            <b-button size="sm" variant="danger" @click="() => {
              selectedRepairs[data.index].selected = false;
            }
              ">Delete</b-button>
          </template>
        </b-table>
      </div>
    </b-modal>
    <!-- /Create Work Order Modal -->

    <!-- View Work Order Modal -->
    <b-modal id="modal_viewWorkOrder" :title="workOrderModalLoading
      ? 'Loading…'
      : (selectedWorkOrder.accepted
        ? (selectedWorkOrder.accepted.value
          ? `Work Order #${selectedWorkOrder.number}`
          : 'Work Request')
        : null)
      " title-class="font-18" ok-title="Submit" size="xxl" @hidden="clearRepairs" @hide="checkIfWorkOrderIsUpdating">

      <!-- spinner while the fresh WO loads -->
      <div v-if="workOrderModalLoading" class="d-flex justify-content-center align-items-center" style="height: 300px;">
        <b-spinner variant="primary" style="width: 3rem; height: 3rem;" label="Loading work order"></b-spinner>
      </div>
      <div v-else-if="selectedWorkOrder.createdDate">
        <!-- Editing Warning Banner with Take Over Button -->
        <div v-if="isWorkOrderBeingEdited"
             class="alert alert-warning mb-3 d-flex justify-content-between align-items-center">
          <div>
            <i class="bx bxs-warning"></i>
            <strong>Currently Being Edited:</strong> This work order is being edited by
            <strong>{{ workOrderEditingStatus.editedBy.name }}</strong>
            <small class="ml-2 text-muted">
              ({{ workOrderEditingStatus.editedBy.timestamp?.toDate ? new Date(workOrderEditingStatus.editedBy.timestamp.toDate()).toLocaleTimeString() : '' }})
            </small>
          </div>
          <b-button
            variant="danger"
            size="sm"
            @click="showTakeOverModal">
            Take Over Editing
          </b-button>
        </div>

        <div class="d-flex justify-content-between">
        <div>
          <div class="row">
            <div class="mt-4 mt-lg-0 mb-4 col-12">
              <strong>Date: </strong>{{ momentInstance(selectedWorkOrder.date).format('ll') }}
            </div>
          </div>

          <div class="mt-4 mt-lg-0 mb-4 d-inline-flex">
            <strong>Unit #: </strong>
            <div v-for="unit in selectedWorkOrder.units" :key="unit">
              <h5>
                <b-badge :variant="'primary'" class="ml-2">{{ getUpToDateUnitName(unit) !== null ? getUpToDateUnitName(unit)
                  : `` }}
                </b-badge>
              </h5>
            </div>
          </div>
          <div class="mt-4 mt-lg-0 mb-4">
            <strong>Submitted By: </strong>{{ selectedWorkOrder.accepted.name }}
          </div>
          <div class="mt-4 mt-lg-0 mb-4">
            <strong>Odometer: </strong>{{ selectedWorkOrder.odometer }}
          </div>
          <div class="mt-4 mt-lg-0 mb-4">
            <strong>Hour Meter: </strong>{{ selectedWorkOrder.hourMeter }}
          </div>
          <div class="mt-4 mt-lg-0 mb-3">
            <h6 style="display: inline-block">
              <strong>Pre-trip deficiency: </strong>
            </h6>

            <h5 style="display: inline-block" class="ml-2">
              <b-badge :variant="selectedWorkOrder.preTripDeficiency ? 'success' : 'danger'
                ">{{ selectedWorkOrder.preTripDeficiency ? 'Yes' : 'No' }}</b-badge>
            </h5>
          </div>
        </div>

        <div>
          <div v-if="!selectedWorkOrderNoteEditMode" class="workorder-notes">
            <div class="d-flex justify-content-between">
              <strong>Note: </strong>
              <i v-if="!isWorkOrderBeingEdited"
                ref="notePencilIcon"
                @click="selectedWorkOrderNoteEditMode = true; selectedWorkOrderNoteEdit = selectedWorkOrder.generalNotes ? selectedWorkOrder.generalNotes : '';"
                @keydown.enter.space.prevent="selectedWorkOrderNoteEditMode = true; selectedWorkOrderNoteEdit = selectedWorkOrder.generalNotes ? selectedWorkOrder.generalNotes : '';"
                class="mdi mdi-pencil edit-note-icon wo-table-cell"
                tabindex="0"
                role="button"
                aria-label="Edit note">
              </i>
            </div>
            <div class="mt-2" style="min-width: 10rem; max-width: 15rem;" v-html="selectedWorkOrder.generalNotes ? selectedWorkOrder.generalNotes.replace(/\n/g, '<br>') : ''">
            </div>
          </div>
          <div v-else class="workorder-notes form-group">
            <textarea v-model="selectedWorkOrderNoteEdit" class="form-control" rows="5" maxlength="250" @keydown.enter.stop @keydown.space.stop></textarea>
            <div class="d-flex justify-content-center">
              <i @click="selectedWorkOrderNoteEditMode = false; $nextTick(() => $refs.notePencilIcon && $refs.notePencilIcon.focus())" @keydown.enter.space.prevent="selectedWorkOrderNoteEditMode = false; $nextTick(() => $refs.notePencilIcon && $refs.notePencilIcon.focus())" class="mdi mdi-close mr-1 edit-note-icon wo-table-cell" tabindex="0" role="button" aria-label="Cancel edit"></i>
              <i @click="handleEditWorkOrderNote(); $nextTick(() => $refs.notePencilIcon && $refs.notePencilIcon.focus())" @keydown.enter.space.prevent="handleEditWorkOrderNote(); $nextTick(() => $refs.notePencilIcon && $refs.notePencilIcon.focus())" class="mdi mdi-check ml-1 edit-note-icon wo-table-cell" tabindex="0" role="button" aria-label="Save note"></i>
            </div>
          </div>
          <div v-if="selectedWorkOrder.changeNotes.length >= 1" class="mt-2">
            <b-button size="sm" v-b-modal.modal_showRequestChanges class="wo-footer-btn" :variant="'primary'">
              View Change {{"\&"}} Resubmit Notes
            </b-button>
          </div>
        </div>
      </div>

        <div class="mt-4 mt-lg-0 mb-4">
          <b-table :items="selectedWorkOrder.repairs" :fields="repairFields" responsive="xl" small
            table-class="wo-repairs-table">
            <template #table-colgroup="scope">
              <col v-for="field in scope.fields" :key="field.key" :style="field.style" />
            </template>
            <template #head(repairCompleted)>
              <span style="white-space: nowrap;">
                <span>RC</span>
                <span id="rc-header-tooltip-icon" class="ml-1" style="cursor: pointer;">
                  <i class="bx bxs-info-circle" style="font-size: 0.85rem; color: #556ee6;"></i>
                </span>
              </span>
              <b-tooltip target="rc-header-tooltip-icon" triggers="hover" placement="top">
                Repairs Completed
              </b-tooltip>
            </template>
            <template v-slot:cell(repairLetter)="data">
              <span>{{ getAlphabeticalLetter(data.index) }}</span>
            </template>
            <template v-slot:cell(itemNumber)="data">
              <div v-if="!isWorkOrderBeingEdited" :id="`disabled-wrapper-${data.index}`"
                class="d-flex align-items-center flex-nowrap">
                <!-- <b-tooltip
                  :target="`disabled-wrapper-${data.index}`"
                  :disabled="!data.item.completed"
                  >Completed repairs cannot be edited or deleted</b-tooltip
                > -->
                <b-tooltip :target="`tooltip-target-${data.index}`" triggers="hover"
                    :disabled="canMarkAsComplete(data.index)">
                    {{ selectedWorkOrder.repairsApproved && selectedWorkOrder.repairsApproved.value === false ? "The issue must have a repair and at least one repair person before it can be completed" : "You cannot unapprove issues on a submitted / approved workorder"}}
                  </b-tooltip>
                <!-- View Full Details is read-only, so it stays available even when the
                     work order is submitted/approved (which disables the actions dropdown). -->
                <b-button v-b-modal.modal_issueFullDetails variant="outline-primary" size="sm"
                  class="wo-action-btn mr-1" :id="`view-details-btn-${data.index}`" aria-label="View Full Details"
                  @click="populateRepair(data.item, data.index)">
                  <i class="bx bx-detail"></i>
                </b-button>
                <b-tooltip :target="`view-details-btn-${data.index}`" triggers="hover">View Full Details</b-tooltip>
                <b-dropdown :disabled="selectedWorkOrder.repairsApproved
                    ? selectedWorkOrder.repairsApproved.value !== false
                      ? true
                      : false
                    : false
                  " variant="primary" size="sm" no-caret toggle-class="wo-footer-btn wo-action-btn">
                  <template v-slot:button-content>
                    <i class="mdi mdi-chevron-down"></i>
                  </template>
                  <b-dropdown-item :disabled="data.item.completed" @click="handleRepairRowEditButton(data.item, data.index)"><i class="bx bx-pencil mr-2 tableIcons"></i>Edit</b-dropdown-item>

                  <b-tooltip :target="`tooltip-target3-${data.index}`" triggers="hover" :disabled="((selectedWorkOrder.repairs[data.index].documents && selectedWorkOrder.repairs[data.index].documents.length === 0) || !selectedWorkOrder.repairs[data.index].documents) && !selectedWorkOrder.repairs[data.index].completed">
                    The repair must not be marked as Completed and have no documents attached.
                  </b-tooltip>
                  <div :id="`tooltip-target3-${data.index}`">
                    <b-dropdown-item :disabled="(selectedWorkOrder.repairs[data.index].documents && selectedWorkOrder.repairs[data.index].documents.length > 0) || selectedWorkOrder.repairs[data.index].completed" @click="removeIssueFromWorkOrder(data.item, data.index)"><i class="bx bx-arrow-back mr-2 tableIcons"></i>Send Back to New Issues</b-dropdown-item>
                  </div>

                  <div :id="`tooltip-target-${data.index}`">
                    <b-dropdown-item @click="changeCompletionStatus(data.index)"
                      :disabled="!canMarkAsComplete(data.index)"><i class="bx bx-list-check mr-2 tableIcons"></i>{{
                        selectedWorkOrder.repairs.length > 0 && selectedWorkOrder.repairs[data.index]
                        ? selectedWorkOrder.repairs[data.index].completed
                          ? 'Mark as Incomplete'
                          : 'Mark as Complete'
                        : null
                      }}</b-dropdown-item>
                  </div>
                  <hr role="separator" aria-orientation="horizontal" class="dropdown-divider">
                  <b-dropdown-item :disabled="data.item.completed" @click="deleteRepair(data.index)"><i
                    class="bx bx-trash mr-2 tableIcons"></i>Delete</b-dropdown-item>
                </b-dropdown>
              </div>
            </template>
            <template v-slot:cell(issue)="data">
              <div style="height: 100%" :style="isWorkOrderBeingEdited ? 'cursor:not-allowed' : (data.item.completed ? '' : 'cursor:pointer')"
                :tabindex="isWorkOrderBeingEdited || data.item.completed ? -1 : 0"
                class="wo-table-cell"
                @click="repairRowClicked(data.item, data.index, 'issue', 0)"
                @keydown.enter.space.prevent="!isWorkOrderBeingEdited && !data.item.completed && repairRowClicked(data.item, data.index, 'issue', 0)">
                <div v-show="selectedWorkOrder.repairs[data.index].editEnabled">
                  <b-form-textarea :id="'txt_issue_' + data.index" v-model="selectedRepair.issue"
                    placeholder="Enter the issue..." rows="1" max-rows="20" no-resize style="min-width: 250px; overflow: visible;" :state="submitErrorEditIssue === null
                      ? validateEntry('editIssue')
                      : submitErrorEditIssue
                      " @blur="hideAllEdits" @keydown.enter.stop @keydown.space.stop></b-form-textarea>
                  <b-form-invalid-feedback id="input-live-feedback">
                    {{ editIssueErrorMessage }}
                  </b-form-invalid-feedback>
                </div>
                <div v-show="!selectedWorkOrder.repairs[data.index].editEnabled"
                  @click="repairRowClicked(data.item, data.index, 'issue', 0)" v-html="data.item.issue ? data.item.issue.replace(/\n/g, '<br>') : ''"
                  style="min-height: 57px; min-width: 250px;">
                </div>
              </div>
            </template>
            <template v-slot:cell(repair)="data">
              <div style="height: 100%" :style="isWorkOrderBeingEdited ? 'cursor:not-allowed' : (data.item.completed ? '' : 'cursor:pointer')"
                :tabindex="isWorkOrderBeingEdited || data.item.completed ? -1 : 0"
                class="wo-table-cell"
                @click="repairRowClicked(data.item, data.index, 'repair')"
                @keydown.enter.space.prevent="!isWorkOrderBeingEdited && !data.item.completed && repairRowClicked(data.item, data.index, 'repair')">
                <div v-show="selectedWorkOrder.repairs[data.index].editEnabled">
                  <b-form-textarea :id="'txt_repair_' + data.index" v-model="selectedRepair.repair"
                    placeholder="Enter the repair done..." rows="1" max-rows="20" no-resize style="min-width: 250px;" @blur="hideAllEdits" @keydown.enter.stop @keydown.space.stop></b-form-textarea>
                </div>
                <div v-show="!selectedWorkOrder.repairs[data.index].editEnabled"
                  @click="repairRowClicked(data.item, data.index, 'repair')" v-html="data.item.repair ? data.item.repair.replace(/\n/g, '<br>') : ''"
                  style="min-height: 57px; min-width: 250px;">
                </div>
              </div>
            </template>
            <template v-slot:cell(repairedBy)="data">
              <div>
                <div v-if="selectedWorkOrder.repairs[data.index].repairedBy[0].name
                  ">
                  <p v-for="(repairman, index) in selectedWorkOrder.repairs[
                    data.index
                  ].repairedBy" :key="`${data.index}-${index}`" class="card-text" style="font-size: 100%">
                    <i v-if="!selectedWorkOrder.repairs[data.index].completed" class="bx bx-x-circle mr-1 wo-table-cell"
                      style="color: red; font-size: 90%; cursor: pointer" tabindex="0"
                      @click="deleteRepairman(data.item, data.index, index)"
                      @keydown.enter.space.prevent="deleteRepairman(data.item, data.index, index)"
                      role="button" :aria-label="`Remove ${repairman.name}`"></i>
                    <b-badge variant="info">{{ repairman.name }}</b-badge>
                    <br>
                    <b-badge variant="light">
                      {{ repairman.date ? momentInstance(repairman.date).format('ll') : null }}
                    </b-badge>
                  </p>
                </div>

                <!-- Add repair person button -->
                <div class="mt-2" v-if="!selectedWorkOrder.repairs[data.index].completed && !isWorkOrderBeingEdited">
                  <b-button :id="`btn_addRepairman_${data.index}`" class="mb-2 wo-table-cell" size="sm" :variant="selectedWorkOrder.repairs[data.index].addRepairmanVisible
                    ? 'outline-danger'
                    : 'outline-success'
                    " v-if="!selectedWorkOrder.repairs[data.index].completed" @click="showAddRepairPersonFields(data)">
                    <i :class="selectedWorkOrder.repairs[data.index].addRepairmanVisible
                      ? 'bx bx-user-x'
                      : 'bx bx-user-plus'
                      " style="cursor: pointer; font-size: 16px" :style="selectedWorkOrder.repairs[data.index].addRepairmanVisible
                      ? 'color: red'
                      : 'color: green'
                    "></i>
                  </b-button>
                  <b-tooltip :target="`btn_addRepairman_${data.index}`" triggers="hover">
                    {{ selectedWorkOrder.repairs[data.index].addRepairmanVisible ? 'Cancel' : 'Add Repair Person' }}
                  </b-tooltip>
                </div>
                <!-- /Add repair person button-->
                <v-select :id="`ddl_repairPerson_${data.index}`" :ref="`ddl_repairPerson_${data.index}`" v-show="selectedWorkOrder.repairs[data.index].addRepairmanVisible
                  " label="name" :options="employees" size="sm" class="text-left" v-model="selectedWorkOrder.repairs[data.index].newRepairman.id
    "></v-select>

                <date-picker :ref="`test_${data.index}`" v-if="selectedWorkOrder.repairs[data.index].addRepairmanVisible
                  " v-model="selectedWorkOrder.repairs[data.index].newRepairman.date
    " lang="en" :clearable="false" class="mb-1 mt-2 sm"></date-picker>
                <b-button v-if="selectedWorkOrder.repairs[data.index].newRepairman.date &&
                  selectedWorkOrder.repairs[data.index].newRepairman.id
                  " size="sm" variant="success" class="wo-table-cell" @click="addRepairman(data.index)">Add</b-button>
              </div>
            </template>
            <template v-slot:cell(repair_hours)="data">
              <div style="height: 100%" :style="isWorkOrderBeingEdited ? 'cursor:not-allowed' : (data.item.completed ? '' : 'cursor:pointer')"
                :tabindex="isWorkOrderBeingEdited || data.item.completed ? -1 : 0"
                class="wo-table-cell"
                @click="repairRowClicked(data.item, data.index, 'repair_hours')"
                @keydown.enter.space.prevent="!isWorkOrderBeingEdited && !data.item.completed && repairRowClicked(data.item, data.index, 'repair_hours')">
                <div v-show="selectedWorkOrder.repairs[data.index].editEnabled">
                  <b-form-textarea :id="'txt_repair_hours_' + data.index" v-model="selectedRepair.repairHours"
                    placeholder="Enter labour cost..." rows="1" max-rows="1" :state="validateEntry('repairHours')"
                    @blur="hideAllEdits" @keydown.enter.stop @keydown.space.stop></b-form-textarea>
                  <b-form-invalid-feedback id="input-live-feedback">
                    {{ editRepairHoursErrorMessage }}
                  </b-form-invalid-feedback>
                </div>
                <span v-show="!selectedWorkOrder.repairs[data.index].editEnabled" @click="
                  repairRowClicked(data.item, data.index, 'repair_hours')
                  ">
                  {{ data.item.repairHours }}
                </span>
              </div>
            </template>
            <template v-slot:cell(labour_hours)="data">
              <div style="height: 100%" :style="isWorkOrderBeingEdited ? 'cursor:not-allowed' : (data.item.completed ? '' : 'cursor:pointer')"
                :tabindex="isWorkOrderBeingEdited || data.item.completed ? -1 : 0"
                class="wo-table-cell"
                @click="repairRowClicked(data.item, data.index, 'labour_hours')"
                @keydown.enter.space.prevent="!isWorkOrderBeingEdited && !data.item.completed && repairRowClicked(data.item, data.index, 'labour_hours')">
                <div v-show="selectedWorkOrder.repairs[data.index].editEnabled">
                  <b-form-textarea :id="'txt_labour_hours_' + data.index" v-model="selectedRepair.labourHours"
                    placeholder="Hours" rows="1" max-rows="1" :state="validateEntry('labourHours')"
                    @blur="hideAllEdits" @keydown.enter.stop @keydown.space.stop></b-form-textarea>
                  <b-form-invalid-feedback id="input-live-feedback">
                    {{ editLabourHoursErrorMessage }}
                  </b-form-invalid-feedback>
                </div>
                <span v-show="!selectedWorkOrder.repairs[data.index].editEnabled" @click="
                  repairRowClicked(data.item, data.index, 'labour_hours')
                  ">
                  {{ data.item.labourHours }}
                </span>
              </div>
            </template>
            <template v-slot:cell(repair_cost)="data">
              <div style="height: 100%; width: 100%" :style="isWorkOrderBeingEdited ? 'cursor:not-allowed' : (data.item.completed ? '' : 'cursor:pointer')"
                :tabindex="isWorkOrderBeingEdited || data.item.completed ? -1 : 0"
                class="wo-table-cell"
                @click="repairRowClicked(data.item, data.index, 'repair_cost')"
                @keydown.enter.space.prevent="!isWorkOrderBeingEdited && !data.item.completed && repairRowClicked(data.item, data.index, 'repair_cost')">
                <div v-show="selectedWorkOrder.repairs[data.index].editEnabled">
                  <b-form-textarea :id="'txt_repair_cost_' + data.index" v-model="selectedRepair.repairCost"
                    placeholder="Enter part cost..." rows="1" max-rows="1" :state="validateEntry('repairCost')"
                    @blur="hideAllEdits" @keydown.enter.stop @keydown.space.stop></b-form-textarea>
                  <b-form-invalid-feedback id="input-live-feedback">
                    {{ editRepairCostErrorMessage }}
                  </b-form-invalid-feedback>
                </div>
                <span v-show="!selectedWorkOrder.repairs[data.index].editEnabled" @click="
                  repairRowClicked(data.item, data.index, 'repair_cost')
                  ">
                  {{ data.item.repairCost }}
                </span>
              </div>
            </template>
            <template v-slot:cell(sourceOfParts)="data">
              <div style="height: 100%" :style="isWorkOrderBeingEdited ? 'cursor:not-allowed' : (data.item.completed ? '' : 'cursor:pointer')"
                :tabindex="isWorkOrderBeingEdited || data.item.completed ? -1 : 0"
                class="wo-table-cell"
                @click="repairRowClicked(data.item, data.index, 'source')"
                @keydown.enter.space.prevent="!isWorkOrderBeingEdited && !data.item.completed && repairRowClicked(data.item, data.index, 'source')">
                <div v-show="selectedWorkOrder.repairs[data.index].editEnabled">
                  <b-form-textarea :id="'txt_source_' + data.index" v-model="selectedRepair.sourceOfParts"
                    placeholder="Enter the source of parts..." rows="1" max-rows="20" no-resize style="min-width: 150px;"
                    @blur="hideAllEdits" @keydown.enter.stop @keydown.space.stop></b-form-textarea>
                </div>
                <div v-show="!selectedWorkOrder.repairs[data.index].editEnabled"
                  @click="repairRowClicked(data.item, data.index, 'source')" v-html="data.item.sourceOfParts ? data.item.sourceOfParts.replace(/\n/g, '<br>') : ''"
                  style="min-height: 57px; min-width: 150px;">
                </div>
              </div>
            </template>
            <template v-slot:cell(repairCompleted)="data">
              <h3 :id="`tooltip-target4-${data.index}`" style="text-align: start"
                :style="canMarkAsComplete(data.index) ? 'cursor: pointer' : ''">
                <b-tooltip v-if="selectedWorkOrder.repairs[data.index].completed" :target="`tooltip-target4-${data.index}`" triggers="hover">
                  <b>Completed By:</b>
                  {{
                    selectedWorkOrder.repairs[data.index].completedBy
                    ? selectedWorkOrder.repairs[data.index].completedBy.name
                    : null
                  }}
                  on
                  {{
                    selectedWorkOrder.repairs[data.index].completedBy.date
                    ? selectedWorkOrder.repairs[data.index].completedBy.date
                      .toString()
                      .substring(0, 16)
                    : null
                  }}
                </b-tooltip>
                <b-tooltip :target="`tooltip-target4-${data.index}`" triggers="hover"
                  :disabled="canMarkAsComplete(data.index)">
                  {{ selectedWorkOrder.repairsApproved && selectedWorkOrder.repairsApproved.value === false ? "The issue must have a repair and at least one repair person before it can be completed" : "You cannot unapprove issues on a submitted / approved workorder"}}
                </b-tooltip>
                <i :class="selectedWorkOrder.repairs[data.index].completed
                    ? 'bx bxs-check-square'
                    : 'bx bxs-x-square'
                  " :style="selectedWorkOrder.repairs[data.index].completed
                  ? 'color : green'
                  : 'color:red'
                  "
                  :tabindex="canMarkAsComplete(data.index) ? 0 : -1"
                  class="wo-table-cell"
                  role="button"
                  :aria-label="selectedWorkOrder.repairs[data.index].completed ? 'Mark as Incomplete' : 'Mark as Complete'"
                  @click="canMarkAsComplete(data.index) ? changeCompletionStatus(data.index) : null"
                  @keydown.enter.space.prevent="canMarkAsComplete(data.index) ? changeCompletionStatus(data.index) : null"
                ></i>
              </h3>
            </template>
            <template v-slot:cell(documents)="row">
              <div class="mr-1">
                <div class="d-flex flex-column justify-content-center align-items-center" style="max-width: 200px">
                  <b-form-select v-model="selectedWorkOrder.repairs[row.index].documentStatus" :disabled="isWorkOrderBeingEdited || selectedWorkOrder.repairsApproved.value !== false" size="sm"
                    class="wo-table-cell"
                    @change="changeDocumentStatus(row.item, true)">
                    <b-form-select-option v-for="(status, index) in documentStatuses" :key="index" :value="status.value" :title="status.title">{{
                      status.text }}
                    </b-form-select-option>
                  </b-form-select>
                  <b-button size="sm" @click="row.toggleDetails" class="mt-1 wo-table-cell" style="max-width: 150px" v-if="selectedWorkOrder.repairs[row.index].documentStatus ===
                    true ||
                    selectedWorkOrder.repairs[row.index].documentStatus === null
                    " :variant="getDocumentButtonVariant(row)">
                    {{ row.detailsShowing ? 'Hide' : 'View' }}
                  </b-button>
                </div>
              </div>
            </template>
            <template #row-details="row">
              <div v-if="row.item.documents !== null" class="d-flex flex-wrap">
                <div v-for="(document, index) in row.item.documents" :key="index" style="text-align: center"
                  class="ml-4 mr-4 mb-4 mt-4">
                  <div class="d-flex justify-content-center">
                    <b-img v-if="document.file.name && document.file.type !== 'application/pdf'" thumbnail class="data-dz-thumbnail mb-1 mt-4 document wo-table-cell"
                      :src="document.url" :alt="document.file.name" style="
                        width: 160px;
                        height: 200px;
                        object-fit: contain;
                        cursor: pointer;
                      "
                      tabindex="0"
                      role="button"
                      :aria-label="`View ${document.file.name}`"
                      @click="populateImage(document)"
                      @keydown.enter.space.prevent="populateImage(document)" />

                      <div
                        v-else-if="document.file.name && document.file.type == 'application/pdf'"
                        @click="populateImage(document)"
                        @keydown.enter.space.prevent="populateImage(document)"
                        class="data-dz-thumbnail mb-1 mt-4 document wo-table-cell"
                        style="position: relative; width: 160px; height: 200px; cursor: pointer;"
                        tabindex="0"
                        role="button"
                        :aria-label="`View ${document.file.name}`"
                      >
                        <iframe
                          :src="document.url"
                          style="
                            width: 160px;
                            height: 200px;
                            border: none;
                            z-index: 1;
                            overflow: hidden;
                          "
                          scrolling="no"
                        ></iframe>

                        <!-- had to add transparent overlay to make the iFrame clickable -->
                        <div
                          style="
                            position: absolute;
                            top: 0;
                            left: 0;
                            width: 100%;
                            height: 100%;
                            background-color: transparent;
                            z-index: 2;
                          "
                        ></div>
                      </div>
                  </div>

                  <div class="d-flex justify-content-center">
                    <b-badge variant="primary" class="mb-4" style="font-size: 105%">{{
                      formattedFileName(document.file.name) }}</b-badge>
                  </div>

                  <div class="d-flex justify-content-center">
                    <div class="mb-3">
                      <b-button @click="onFileDownloaded(document)" variant="info" size="sm"
                        class="mr-2">Download</b-button>
                      <b-button @click="onFileDeleted(document, row)" variant="danger" size="sm"
                        :disabled="isWorkOrderBeingEdited || selectedWorkOrder.accepted.value === true && selectedWorkOrder.repairsApproved.value !== false">Delete
                      </b-button>
                    </div>
                  </div>

                </div>
              </div>
              <vue-dropzone v-if="selectedWorkOrder.repairs[row.index].documentStatus !== true && !isWorkOrderBeingEdited
                " id="dropzone" :class="'vue-dropzone dropzone mt-2'" :ref="`fileUploader-${row.index}`"
                :use-custom-slot="true" :options="dropzoneOptions" :include-styling="false"
                @vdropzone-file-added="(file) => onFileAdded(file, row)">
                <div class="dropzone-custom-content">
                  <i class="display-4 text-muted bx bxs-cloud-upload"></i>
                  <h4>Drop files here or click to upload.</h4>
                </div>
              </vue-dropzone>
            </template>
          </b-table>
          <div v-if="selectedWorkOrder.repairsApproved.value === false && !isWorkOrderBeingEdited">
            <div class="form-group">
              <!-- <label>Issue</label> -->
              <b-form-textarea id="VWO_addIssue_textarea" v-model="singleIssue" placeholder="Enter the issue..." cols="10"
                rows="3" max-rows="6" wrap="hard"></b-form-textarea>
            </div>
            <b-button :aria-disabled="singleIssue === null || singleIssue.trim() === ''"
              :class="(singleIssue === null || singleIssue.trim() === '') ? 'wo-footer-btn disabled-look' : 'wo-footer-btn'"
              variant="primary"
              @click="(singleIssue !== null && singleIssue.trim() !== '') && addSingleIssue()">Add Issue</b-button>
          </div>
        </div>
        <div v-if="selectedWorkOrder.repairsApproved.value">
          <i class="bx bxs-check-circle" style="color: green"></i> Approved By
          {{ selectedWorkOrder.repairsApproved.name }} on
          {{ momentInstance(selectedWorkOrder.repairsApproved.time).format('lll') }}
        </div>
      </div>
      <template #modal-footer="{ close }">
        <div class="w-100" v-if="!workOrderModalLoading">
          <b-button variant="primary" class="float-right wo-footer-btn" @click="close()">
            Close
          </b-button>
          <b-button v-if="unapproveVisible" variant="danger" class="float-right mr-3 wo-table-cell"
            @click="unapproveWorkOrder()">Unapprove Work Order</b-button>
          <b-button v-if="editVisible" variant="primary" class="float-right mr-3 wo-footer-btn"
            @click="handleEditButtonClick()">
            Edit
          </b-button>
          <div class="float-right mr-3" :id="`tooltip-target5`">
            <b-button v-if="submitVisible" :disabled="selectedWorkOrder.repairs.some((x) => x.documentStatus === null)" variant="success" class="wo-table-cell" @click="handleWorkOrderSubmit()">
              Submit Work Order
            </b-button>
            <b-tooltip :target="`tooltip-target5`" triggers="hover"
              :disabled="!selectedWorkOrder.repairs.some((x) => x.documentStatus === null)">
              All repairs must not be "Waiting for Documents" in order to submit this work order for approval
            </b-tooltip>
          </div>
          <WorkOrderPrint :selectedWorkOrder="selectedWorkOrder" :units="units"></WorkOrderPrint>
          <b-button v-if="selectedWorkOrder.accepted
            ? selectedWorkOrder.accepted.value
              ? false
              : true
            : true
            " variant="success" class="float-right mr-3" @click="acceptWorkOrder()">
            Accept Work Request
          </b-button>
          <div v-if="approveVisible === true">
            <b-button variant="success" class="float-right mr-3" @click="approveWorkOrder()">
              Approve Work Order
            </b-button>
            <b-button variant="danger" class="float-right mr-3" v-b-modal.modal_requestChanges>
              Request Changes
            </b-button>
          </div>
          <b-button v-if="unsubmitVisible" variant="danger" class="float-right mr-3" @click="unSubmitWorkOrder()">
            Unsubmit Work Order
          </b-button>
          <div v-if="submittedBadgeVisible">
            <h5>
              <b-badge variant="success" class="float-right mr-3 mt-2">
                Work Order Submitted For Review
              </b-badge>
            </h5>
          </div>
        </div>
      </template>
    </b-modal>
    <!-- /View Work Order Modal -->

    <!-- Edit Work Order Modal -->
    <b-modal id="modal_editWorkOrder" title="Edit Work Order" title-class="font-18" ok-title="Submit" size="lg"
      @ok="editWorkOrder">
      <div v-if="selectedWorkOrderEdit.date">
        <div class="mb-4">
          <div class="form-group">
            <label>Date</label>
            <date-picker v-model="selectedWorkOrderEdit.date" lang="en" :clearable="false"></date-picker>
          </div>
          <div class="form-group">
            <label>Unit</label>
            <v-select
              :options="units"
              v-model="selectedWorkOrderEdit.units"
              :reduce="unit => unit.UID"
              label="name"
              multiple
            >
              <template slot="selected-option" slot-scope="option">
                <span>{{ !option.dateDeactivated ? getUpToDateUnitName(option.UID) : `${getUpToDateUnitName(option.UID)} (Deactivated)` }}</span>
              </template>
              <template slot="option" slot-scope="option">
                <span v-if="!option.dateDeactivated">
                  {{ getUpToDateUnitName(option.UID) }}
                </span>
                <!-- don't render deactivated unit as an option -->
                <span v-else class="deactivated-option"></span>
              </template>
            </v-select>
          </div>
        </div>

        <div class="form-group">
          <label>Odometer</label>
          <b-form-input placeholder="Odometer..." v-model="selectedWorkOrderEdit.odometer" :state="submitErrorOdometerEdit === null
            ? validateEntry('odometer-edit')
            : submitErrorOdometerEdit
            "></b-form-input>
          <b-form-invalid-feedback id="input-live-feedback">
            {{ odometerEditErrorMessage }}
          </b-form-invalid-feedback>
        </div>
        <div class="form-group">
          <label>Hour Meter</label>
          <b-form-input placeholder="Hour Meter..." v-model="selectedWorkOrderEdit.hourMeter" :state="submitErrorHourMeterEdit === null
            ? validateEntry('hourMeter-edit')
            : submitErrorHourMeterEdit
            "></b-form-input>
          <b-form-invalid-feedback id="input-live-feedback">
            {{ hourMeterEditErrorMessage }}
          </b-form-invalid-feedback>
        </div>
        <div class="form-group">
          <label>Pre-Trip Deficiency</label>
          <b-form-select v-model="selectedWorkOrderEdit.preTripDeficiency" :options="ptdOptions"
            :state="true"></b-form-select>
        </div>
        <div class="form-group">
          <label>Notes</label>
          <b-form-textarea v-model.trim="selectedWorkOrderEdit.generalNotes"
            placeholder="General notes..." rows="3"
            maxlength="250"
          ></b-form-textarea>
        </div>
      </div>
    </b-modal>
    <!-- /Edit Work Order Modal -->

    <!-- Add Request Changes Modal -->
    <b-modal id="modal_requestChanges" ok-title="Submit" size="md" hide-header @ok="requestChanges">
      <div class="form-group">
        <label>Reason</label>
        <b-form-textarea placeholder="Enter a reason for the change request" autofocus v-model="changeNotes" rows="5"
          :state="submitErrorChangeNotes === null
              ? validateEntry('change-notes')
              : submitErrorChangeNotes
            "></b-form-textarea>
        <b-form-invalid-feedback id="input-live-feedback">
          {{ changeNotesErrorMessage }}
        </b-form-invalid-feedback>
        <div class="d-flex justify-content-end">
          <label class="mr-1" id="lbl_send_requested_changes_noti">Send notification email <i class="bx bxs-help-circle"></i><b-tooltip target="lbl_send_requested_changes_noti" triggers="hover">
            {{!sendNotiEmailOptionDisabled ? 'The admin setting "Work Order Requested Changes Notification" is enabled. You can optionally choose if you want the requested changes email notification to be sent for this instance.' : 'The admin setting "Work Order Requested Changes Notification" is disabled. This setting must be enabled in order to send a notification email.'}}
          </b-tooltip></label>
          <b-form-checkbox v-model="sendRequestedChangesNotiEmailOption" :disabled="sendNotiEmailOptionDisabled" :class="{ 'checkbox-disabled': sendNotiEmailOptionDisabled }"></b-form-checkbox>
        </div>
      </div>
    </b-modal>
    <!-- /Add Request Changes Modal -->

    <!-- Resubmitting work order note Modal -->
    <b-modal id="modal_resubmitNotes" ref="resubmitNotesModal" ok-title="Submit" size="md" hide-header @ok="handleResubmitWorkOrder" @hidden="handleResubmitModalClose">
      <div class="form-group">
        <label>Describe Changes Made</label>
        <b-form-textarea placeholder="Describe the changes made to this work order since its last requested changes" autofocus v-model="resubmitNotes" rows="5"
          :state="submitErrorResubmitNotes === null
              ? validateEntry('resubmit-notes')
              : submitErrorResubmitNotes
            "></b-form-textarea>
        <b-form-invalid-feedback id="input-live-feedback">
          {{ resubmitNotesErrorMessage }}
        </b-form-invalid-feedback>
      </div>
    </b-modal>
    <!-- /Resubmitting work order note Modal -->

    <!-- Empty Work Orders Modal -->
    <b-modal id="modal_emptyWorkOrders" title="Empty Work Orders" title-class="font-18" ok-title="Close" size="lg" ok-only>
      <p class="text-muted">These work orders have no issues on them. Open one to edit its details and add issues to it.</p>
      <b-table :items="emptyWorkOrders" :fields="emptyWorkOrderFields" small responsive="sm">
        <template v-slot:cell(date)="data">
          {{ data.item.date ? momentInstance(data.item.date).format('ll') : '' }}
        </template>
        <template v-slot:cell(units)="data">
          <b-badge v-for="unit in data.item.units" :key="unit" variant="primary" class="mr-1">{{ getUpToDateUnitName(unit) }}</b-badge>
        </template>
        <template v-slot:cell(open)="data">
          <b-button size="sm" variant="primary" class="wo-footer-btn" @click="openEmptyWorkOrder(data.item)">Open</b-button>
        </template>
      </b-table>
    </b-modal>
    <!-- /Empty Work Orders Modal -->

    <!-- View Issue Full Details Modal -->
    <b-modal id="modal_issueFullDetails" ok-title="Close" size="md" hide-header ok-only>
      <div class="form-group">
        <label>Issue Details</label>
        <p class="font-italic" v-html="selectedRepair.issue ? selectedRepair.issue.replace(/\n/g, '<br>') : ''"></p>
        <div>
          <ul class="issue-details-list">
            <li><span class="issue-details-field">Submitted By:</span> {{selectedRepair.submitted ? selectedRepair.submitted.name : ''}} on {{ selectedRepair.submitted ? momentInstance(selectedRepair.submitted.time).format('lll') : '' }}</li>
            <li class="d-flex"><span class="issue-details-field mr-1">Repair:</span><p v-html="selectedRepair.repair ? selectedRepair.repair.replace(/\n/g, '<br>') : ''"></p></li>
            <ul class="issue-details-list" v-if="selectedRepair.repairedBy && selectedRepair.repairedBy[0] && selectedRepair.repairedBy[0].date">
              <li v-for="(repairMan, index) in selectedRepair.repairedBy" :key="index">
                <span class="issue-details-sub-field">Repaired By:</span> {{repairMan.name}} on {{ momentInstance(repairMan.date).format('ll') }}
              </li>
            </ul>
            <ul class="issue-details-list" v-else>
              <li><span class="issue-details-sub-field">Repaired By:</span> Not repaired yet</li>
            </ul>
            <li><span class="issue-details-field">Parts Cost:</span> {{selectedRepair.repairCost}}</li>
            <li><span class="issue-details-field">Labour Cost:</span> {{selectedRepair.repairHours}}</li>
            <li v-if="$store.state.custom.workOrders.labour_hours_enabled"><span class="issue-details-field">Labour Hours:</span> {{selectedRepair.labourHours}}</li>
            <li class="d-flex"><span class="issue-details-field mr-1">Source of Parts:</span> <p v-html="selectedRepair.sourceOfParts ? selectedRepair.sourceOfParts.replace(/\n/g, '<br>') : ''"></p></li>
            <li><span class="issue-details-field">Document Status:</span> {{selectedRepair.documentStatus === false ? 'No Documents Required' : selectedRepair.documentStatus === null ? 'Waiting for Documents' : 'All Documents Received'}}</li>
            <ul class="issue-details-list" v-if="selectedRepair.documents">
              <span class="issue-details-sub-field">Documents: </span>
              <li v-for="(doc, index) in selectedRepair.documents" :key="index">
                {{doc.uploadedBy.name}} uploaded {{doc.file.name}} on {{ momentInstance(doc.date).format('ll') }}
              </li>
            </ul>
            <ul class="issue-details-list" v-else>
              <li><span class="issue-details-sub-field">Documents:</span> No documents uploaded</li>
            </ul>
            <li><span class="issue-details-field">Completed Status:</span> {{selectedRepair.completed ? `Completed by ${selectedRepair.completedBy.name} on ${momentInstance(selectedRepair.completedBy.date).format('lll')}` : 'Not Completed'}}</li>
            <li><span class="issue-details-field">Odometer:</span> {{selectedRepair.odometer ? selectedRepair.odometer : ''}}</li>
            <li><span class="issue-details-field">Hour Meter:</span> {{selectedRepair.hourMeter ? selectedRepair.hourMeter : ''}}</li>

          </ul>
        </div>
      </div>
    </b-modal>
    <!-- /View Issue Full Details Modal -->

    <!-- Show Request Changes Modal -->
    <b-modal id="modal_showRequestChanges" ok-title="Submit" size="md" hide-header hide-footer>
      <div v-for="(note, index) in requestedAndResubmitNotes" :key="`note_${index}`">
        <div class="speech-bubble mb-3">
          <span style="font-size: 85%">
            {{ note.name }}
            <span :class="note.noteType === 'resubmit' ? 'text-success' : 'text-danger'">
              {{ note.noteType === 'resubmit' ? 'resubmitted' : 'requested' }}
            </span>
            changes on {{ momentInstance(note.time).format('lll') }}
          </span>
          <h5 class="mt-1">{{ note.notes }}</h5>
        </div>
      </div>
    </b-modal>
    <!-- /Request Changes Modal -->

    <!-- Show Image Modal -->
    <b-modal id="img-modal" size="xl" hide-footer>
      <div v-if="selectedImage.file.name" style="text-align: center">
        <div class="d-block text-center">
            <iframe
            v-if="selectedImage.file.type === 'application/pdf'"
            :src="selectedImage.url"
            style="
              width: 100%;
              height: 70vh;
              border: none;
            "
          ></iframe>

          <img
            v-else
            :src="selectedImage.url"
            class="img-fluid"
            :alt="selectedImage.file.name"
          />
        </div>
        <div class="mt-3">
          <b-badge style="font-size: 150%" variant="primary">{{
            formattedFileName(selectedImage.file.name)
          }}</b-badge>
        </div>
        <div>
          <b-badge variant="success" style="font-size: 110%" class="mt-3"
            >Uploaded By:</b-badge
          >
          <p style="font-size: 110%">
            {{ selectedImage.uploadedBy.name }} on
            {{ momentInstance(selectedImage.date).format('lll') }}
          </p>
        </div>
        <div class="mt-3">
          <b-button
            @click="onFileDownloaded(selectedImage)"
            variant="info"
            class="mr-4"
            >Download</b-button
          >
          <b-button variant="light"
            ><a :href="selectedImage.url" target="_blank">View In Browser</a>
          </b-button>
        </div>
      </div>
    </b-modal>
    <!-- //Show Image Modal -->

    <!-- Add Issue to Existing Work Order Modal -->
    <b-modal id="modal_addIssueToExistingWorkOrder" ref="modal-addIssueToExistingWorkOrder" title="Add Issue to Existing Work Order" @ok="addIssueToExistingWorkOrder(selectedExistingWO)" ok-title="Submit" @hide="handleAddIssueToExistingWOModalClose()" :ok-disabled="!existingWorkOrdersWithSameUnitsAsSelectedIssues.find(x => x.selectedToReceiveIssue && x.selectedToReceiveIssue === true)" size="md">
      <label class="font-weight-bold">Select a Work Order: </label>
      <div v-if="existingWorkOrdersWithSameUnitsAsSelectedIssues.length > 0">
        <div v-for="workOrder in existingWorkOrdersWithSameUnitsAsSelectedIssues" :key="workOrder.view">
          <div v-if="workOrder.isVisible" class="card task-box">
            <div class="card-body" :class="workOrder.selectedToReceiveIssue ? 'bg-success' : selectedExistingWO && !workOrder.selectedToReceiveIssue ?'card-body-grey' : ''">
              <div class="float-right ml-2">
                <div class="d-flex">
                  <div>
                    <div>
                      <span v-b-tooltip.hover :title="workOrder.repairsComplete.value
                        ? `${workOrder.repairsComplete.name} on ${workOrder.repairsComplete.time ? momentInstance(workOrder.repairsComplete.time).format('lll') : workOrder.repairsComplete.date ? momentInstance(workOrder.repairsComplete.date).format('lll') : null}`
                        : null
                        ">
                        <h5>
                          <b-badge class="badge-shrink" :variant="!workOrder.repairsComplete.value
                            ? workOrder.repairsComplete.value === null
                              ? 'warning'
                              : 'danger'
                            : 'success'
                            ">{{
                              !workOrder.repairsComplete.value
                              ? workOrder.repairsComplete.value === null
                                ? 'Repairs Ongoing'
                                : 'Repairs Incomplete'
                              : 'Repairs Complete'
                            }}</b-badge>
                        </h5>
                      </span>
                    </div>
                    <div>
                      <h5>
                        <b-badge v-if="workOrder.repairs.length === 0" class="badge-shrink" variant="secondary">No Issues</b-badge>
                        <b-badge v-else class="badge-shrink"
                            :variant="workOrder.documentStatus === false ? 'primary' : workOrder.documentStatus === null ? 'warning' : 'success'">{{ workOrder.documentStatus === false ? 'No Documents Required' : workOrder.documentStatus === null ? 'Waiting for Documents' : 'All Documents Received' }}</b-badge>
                      </h5>
                    </div>
                    <div>
                      <span v-b-tooltip.hover :title="workOrder.repairsApproved.value
                            ? `${workOrder.repairsApproved.name} on ${momentInstance(workOrder.repairsApproved.time).format('lll')}`
                            : null
                          ">
                        <h5>
                          <b-badge class="badge-shrink" :variant="repairsApprovedBadgeLogic(
                            workOrder.repairsApproved,
                            workOrder.changeNotes
                          ).class
                            ">{{
                              repairsApprovedBadgeLogic(
                                workOrder.repairsApproved,
                                workOrder.changeNotes
                              ).text
                            }}
                          </b-badge>
                        </h5>
                      </span>
                    </div>
                  </div>
                  <b-form-checkbox :disabled="selectedExistingWO && selectedExistingWO.number !== workOrder.number" size="lg" class="float-right ml-4" @change="(e) => handleSelectedWorkOrderToReceiveIssue(e, workOrder)"></b-form-checkbox>
                </div>
              </div>
              <div>
                <h5 class="font-size-15">
                  <p :class="workOrder.selectedToReceiveIssue ? 'text-white' : 'text-dark'">{{ workOrder.number }}</p>
                </h5>
              </div>
              <p :class="workOrder.selectedToReceiveIssue ? 'text-white' : 'text-muted'">{{ momentInstance(workOrder.date).format('ll') }}</p>
              <div class="d-flex w-100">
                <p :class="workOrder.selectedToReceiveIssue ? 'text-white' : 'text-muted'">Unit</p>
                <h6>
                  <b-badge v-for="unit in workOrder.units" :key="unit" :variant="'primary'" class="ml-2">{{
                    getUpToDateUnitName(unit) !== null ? getUpToDateUnitName(unit) : `` }}</b-badge>
                </h6>
              </div>
              <div v-if="workOrder.generalNotes && workOrder.generalNotes !== ''">
                <p :class="workOrder.selectedToReceiveIssue ? 'text-white' : 'text-muted'">Note: <span class="font-italic text-wrap">{{workOrder.generalNotes}}</span></p>
              </div>
                <div class="team">
                  <p :class="workOrder.selectedToReceiveIssue ? 'wo-card-issues-text-selected text-white' : 'wo-card-issues-text'" @click.stop="handleShowWorkOrderIssueDetails(workOrder.view, null, false)">{{ `${workOrder.repairs.length} Issues` }}
                    <i v-if="workOrder.showIssueDetails" class="mdi mdi-chevron-down mr-2 tableIcons"></i>
                    <i v-else class="mdi mdi-chevron-up mr-2 tableIcons"></i>
                  </p>
                  <ul v-if="workOrder.showIssueDetails" class="list-group list-group-flush">
                    <li class="list-group-item wo-card-issues-dropdown" v-for="(issue) in workOrder.repairs" :key="issue.id">
                      <div class="row text-wrap">
                        <p class="col-6"><span class="font-italic font-weight-bold">Issue:</span> {{ issue.issue }} </p>
                        <p class="col-6"><span class="font-italic font-weight-bold">Repair:</span> {{ issue.repair }}</p>
                      </div>
                    </li>
                  </ul>
                </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else>
        <p class="font-italic">No work orders in the 'Waiting to Start', 'In Progress', or 'Waiting for Docs' columns exist with the same units as the issue(s) you have selected.</p>
      </div>
    </b-modal>
    <!-- /Add Issue to Existing Work Order Modal -->

    <!-- Repairs Changes Notification Modal -->
    <b-modal id="modal_repairsChanges" title="Work Order Repairs Updated" title-class="font-18" ok-only ok-title="Close" size="lg" @show="handleRepairsModalShow" @hide="handleRepairsModalHide">
      <div v-html="repairsChangesSummary"></div>
    </b-modal>
    <!-- /Repairs Changes Notification Modal -->
    <!-- Work Order Stale Modal -->
    <b-modal id="modal_workOrderStale" title="Work Order Updated" title-class="font-18" size="md"
      no-close-on-backdrop no-close-on-esc hide-header-close>
      <p>
        This work order was changed by
        <strong>{{ staleUpdatedByName || 'another user' }}</strong>
        since you opened it. To avoid overwriting their changes, your edit was not saved.
      </p>
      <div v-if="repairsChangesSummary" v-html="repairsChangesSummary" class="mt-2"></div>
      <p class="mt-3 mb-0">Click <strong>Refresh Work Order</strong> to load the latest version.</p>
      <template #modal-footer>
        <b-button variant="primary" @click="handleRefreshWorkOrder()">Refresh Work Order</b-button>
      </template>
    </b-modal>
    <!-- /Work Order Stale Modal -->
  </div>
</template>

<script>
import Vue from 'vue';
import Swal from 'sweetalert2';
import axios from 'axios';
import moment from 'moment';
import { firestore } from 'firebase';
import { tableSort, convertTimeStampToDate } from '../../../helper/helpers.js';
import { setWorkOrderEditingStatus, clearWorkOrderEditingStatus, checkWorkOrderEditingStatus, clearWorkOrderBeingEditedByUser, updateWorkOrderBeingEditedTimestamp, updateWorkOrderUnsavedChangesStatus, withLastUpdated, checkWoFreshness, getWorkOrderById, hydrateWorkOrderDoc, getEmptyWorkOrders } from '../../../helper/dbHelpers.js';
import VueToast from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';
import DatePicker from 'vue2-datepicker';
import vue2Dropzone from 'vue2-dropzone';
import 'vue2-dropzone/dist/vue2Dropzone.min.css';
import WorkOrderPrint from './workOrderPrint.vue';
import KanbanSearch from './kanbanSearch.vue';
import lodash from 'lodash';
import {debounce} from 'lodash';
import WorkOrderColumn from './WorkOrderColumn.vue';
import NewIssuePrint from './newIssuePrint.vue';

import {
  // getFilesForJob,
  uploadWorkOrderFile,
  // downloadFile,
  deleteWorkOrderFile,
} from '../../../helper/storageHelpers.js';

Vue.use(VueToast);

// import draggable from 'vuedraggable';

// import Layout from '../../layouts/main';
import appConfig from '@/app.config';

import {
  upcomingTasks,
  progressTasks,
  completedTasks,
} from '../../../../tasks/data-kanaban';

let hbInterval = null;

let inactivityInterval = null;
let lastWorkOrderActivityAt = null;
let onWorkOrderActivity = null;

// an open work order holds the editing lock, so close and save it once the user has gone idle
const WORK_ORDER_INACTIVITY_MS = 300000; // 5 minutes
const INACTIVITY_CHECK_MS = 5000;
const WORK_ORDER_ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'wheel', 'touchstart'];

/**
 * Kanban-board component
 */
export default {
  page: {
    title: 'workOrdersKanban',
    meta: [{ name: 'description', content: appConfig.description }],
  },

  components: {
    // draggable,
    WorkOrderPrint,
    DatePicker,
    vueDropzone: vue2Dropzone,
    KanbanSearch,
    WorkOrderColumn,
    NewIssuePrint
  },
  props: {
    workOrders: Array,
    units: Array,
    employees: Array,
    repairs: Array,
    parentDataLoaded: Boolean,
  },
  data() {
    return {
      firestoreInstance: firestore,
      momentInstance: moment,
      upcomingTasks: upcomingTasks,
      progressTasks: progressTasks,
      completedTasks: completedTasks,
      title: 'Kanban Board',
      //   items: [
      //     {
      //       text: 'Tasks',
      //       href: '/',
      //     },
      //     {
      //       text: 'Kanban Board',
      //       active: true,
      // },
      //   ],

      totalRows: 0,
      currentPage: 1,
      perPage: 10,
      pageOptions: [10, 25, 50, 100],
      filter: null,
      filterOn: [],
      sortBy: 'date',
      sortDesc: false,
      fields: [
        { key: 'view', sortable: false },
        { key: 'date', sortable: true },
        { key: 'number', label: 'Work Order #', sortable: true },
        { key: 'unit', sortable: true },
        { key: 'accepted', sortable: true },
        { key: 'repairsComplete', sortable: true },
        { key: 'documentStatus', sortable: true },
        { key: 'repairsApproved', sortable: true },
      ],
      repairFields: [
        { key: 'repairLetter', label: '', class: ['c-20', 'col-fit-content'] },
        { key: 'itemNumber', label: 'Edit', class: ['c-20', 'col-fit-content'] },
        {
          key: 'issue',
          class: ['wo-col-issue'],
          tdClass: ['word-break-column', 'c-25', 'full-height-td'],
        },
        {
          key: 'repair',
          class: ['wo-col-repair'],
          tdClass: ['word-break-column', 'c-50', 'full-height-td'],
        },
        { key: 'repairedBy', class: ['c-25', 'wo-col-repairedby', 'text-center'] },
        {
          key: 'sourceOfParts',
          class: ['wo-col-source'],
          tdClass: ['word-break-column', 'c-50', 'full-height-td', 'source-of-parts-column'],
        },
        { key: 'repairCompleted', label: 'RC', class: ['c-20', 'wo-col-rc'] },
        { key: 'documents', class: ['c-20', 'wo-col-docs'] },
      ],
      newRepairFields: [{ key: 'index', label: '' }, { key: 'issue' }],

      ptdOptions: [
        { value: true, text: 'Yes' },
        { value: false, text: 'No' },
      ],
      newIssue: null,
      singleIssueDrafts: {},
      newWorkOrderRepairs: [],
      selectedWorkOrderInitial: null,
      selectedWorkOrder: {
        date: null,
        units: [],
        preTripDeficiency: false,
        odometer: null,
        hourMeter: null,
        accepted: { time: null },
        repairs: [],
        repairedBy: [],
        repairsComplete: { time: null },
        repairsApproved: { time: null },
        generalNotes: null
      },
      selectedWorkOrderEdit: {
        date: null,
        units: [],
        preTripDeficiency: false,
        odometer: null,
        hourMeter: null,
        generalNotes: null,
        lastModified: null
      },
      selectedRepair: { issue: null, repair: null, sourceOfParts: null, odometer: null, hourMeter: null, submitted: {name: null, time: null, uid: null, value: null} },
      selectedRepairInitial: { issue: null, repair: null, sourceOfParts: null, submitted: {name: null, time: null, uid: null, value: null} },
      selectedImage: {
        url: null,
        file: { name: null, size: null, type: null },
      },
      repairIndex: null,
      documentID: null,
      workOrderStream: null,
      woStreamBaselineMs: null, // doc.lastUpdated (server ms) as of the stream's first snapshot; null = not yet baselined
      repairsChangesSummary: '',
      accumulatedChanges: [],
      isRepairsModalOpen: false,
      baselineRepairs: null,
      // concurrency control
      isOpeningWorkOrder: false, // a WO open is in flight - ignore further card clicks until it settles
      boardDataLastPull: 0, // when the board was last pulled (ms)
      woLastPull: {}, // when each WO was last pulled fresh (ms), keyed by view-id
      isWoStale: false, // open WO changed by someone else
      staleUpdatedByName: null, // name shown in the staleness modal
      workOrderModalLoading: false, // fetching a fresh WO on open
      wasInReadOnlyMode: false,
      autoUpgradeAvailable: false,
      safetyFormDetail: {
        name: null,
        date: null,
        submittedBy: null,
        fields: [],
      },
      odometerEditValid: false,
      odometerIssueEditValid: false,
      hourMeterEditValid: false,
      hourMeterIssueEditValid: false,
      odometerValid: null,
      odometerIssueValid: null,
      hourMeterValid: null,
      hourMeterIssueValid: null,
      unitValid: null,
      issueValid: false,
      editRepairValid: false,
      editIssueValid: false,
      editRepairCostValid: false,
      editRepairHoursValid: false,
      editLabourHoursValid: false,
      changeNotesValid: false,
      resubmitNotesValid: false,

      editID: null,
      submitErrorOdometerEdit: null,
      submitErrorEditOdometerIssue: null,
      submitErrorHourMeterEdit: null,
      submitErrorEditHourMeterIssue: null,
      submitErrorOdometer: null,
      submitErrorOdometerIssue: null,
      submitErrorHourMeter: null,
      submitErrorHourMeterIssue: null,
      submitErrorDate: null,
      submitErrorUnit: null,
      submitErrorUnitEdit: null,
      submitErrorIssue: null,
      submitErrorAddIssue: null,
      submitErrorEditIssue: null,
      submitErrorEditRepair: null,
      submitErrorEditRepairCost: null,
      submitErrorEditRepairHours: null,
      submitErrorChangeNotes: null,
      submitErrorResubmitNotes: null,
      submitErrorGeneralNotes: null,

      odometerEditErrorMessage: null,
      hourMeterEditErrorMessage: null,
      odometerIssueEditErrorMessage: null,
      hourMeterIssueEditErrorMessage: null,
      generalNotesErrorMessage: null,
      dateErrorMessage: null,
      unitErrorMessage: null,
      odometerErrorMessage: null,
      odometerIssueErrorMessage: null,
      hourMeterErrorMessage: null,
      hourMeterEditIssueErrorMessage: null,
      unitEditErrorMessage: null,
      issueErrorMessage: null,
      editIssueErrorMessage: null,
      editRepairErrorMessage: null,
      editRepairCostErrorMessage: null,
      editRepairHoursErrorMessage: null,
      editLabourHoursErrorMessage: null,
      changeNotesErrorMessage: null,
      resubmitNotesErrorMessage: null,
      currentUser: null,
      adminUser: false,
      selectedWorkOrderNoteEditMode: false,
      selectedWorkOrderNoteEdit: null,
      changeNotes: null,
      sendRequestedChangesNotiEmailOption: true,
      resubmitNotes: null,
      queryIDs: null,

      dropzoneOptions: {
        url: '/',
        addRemoveLinks: true,
        method: 'put',
        chunking: true,
        forceChunking: true,
        autoQueue: false,
        autoProcessQueue: false,
        thumbnailWidth: 160,
        // thumbnailHeight: 170,
        maxFilesize: 250,
        thumbnailMethod: 'contain',
        // previewsContainer: '.dropzone-previews',
        // previewTemplate: this.template(),
      },
      selectedIssuesColumnUnit: null,
      selectedInProgressUnit: null,
      selectedChangeRequestedUnit: null,
      selectedWaitingOnDocsUnit: null,
      selectedNotStartedUnit: null,
      selectedReadyForSubmitUnit: null,
      selectedCompletedUnit: null,
      repairToEdit: {
        date: null,
        issue: '',
        units: [],
        odometer: null,
        hourMeter: null
      },
      repairToEditInitial: {
        date: null,
        issue: '',
        units: [],
        odometer: null,
        hourMeter: null
      },
      repairsData: [],
      workOrdersData: [],
      emptyWorkOrders: [],
      emptyWorkOrderFields: [
        { key: 'number', label: 'Work Order' },
        { key: 'date', label: 'Date' },
        { key: 'units', label: 'Unit #' },
        { key: 'open', label: '' },
      ],
      searchFiltersUsed: false,
      unitListWithExtraOption: [],
      documentStatuses: [
        {
          text: 'None',
          title: 'No Documents Required',
          value: false,
        },
        {
          text: 'Waiting',
          title: 'Waiting For Documents',
          value: null,
        },
        {
          text: 'Received',
          title: 'All Documents Received',
          value: true,
        },
      ],
      visibleColumns: {
        all: true,
        issues: true,
        waitingToStart: true,
        inProgress: true,
        waitingForDocs: true,
        changesRequested: true,
        completed: true,
        submitted: true
      },
      selectedColumnWidth: 3,
      selectedSubmittedColumnFilter: {label: 'All', value: 'all'},
      selectedSubmittedColumnSort: 'Date Desc',
      selectedExistingWO: null,
      notStartedWorkOrders: [],
      inProgressWorkOrders: [],
      waitingOnDocsWorkOrders: [],
      changesRequestedWorkOrders: [],
      completedWorkOrders: [],
      submittedWorkOrders: [],
      existingWorkOrdersWithSameUnitsAsSelectedIssues: [],
      workOrdersNotRendered: [],
      pageLoading: true,
      orgSettings: null,
      workOrderUpdating: false,
      allowWorkOrderModalClose: false,
      workOrderEditingStatus: {
        isBeingEdited: false,
        editedBy: null
      },
      isLoadingWorkOrders: [],
      inactivityClosedWorkOrder: null,
    };
  },
  methods: {
    filterAllColumns: function(){
      this.filterNotStartedWorkOrders();
      this.filterInProgressWorkOrders();
      this.filterWaitingOnDocsWorkOrders();
      this.filterChangesRequestedWorkOrders();
      this.filterCompletedWorkOrders();
      this.filterSubmittedWorkOrders();
      },

    startHeartbeat() {
      // Clear any existing interval before starting a new one
      if (hbInterval) {
        clearInterval(hbInterval);
        hbInterval = null;
      }

      if (!this.selectedWorkOrder?.view || this.isWorkOrderBeingEdited) return;

      hbInterval = setInterval(() => {
        if (!this.selectedWorkOrder?.view || this.isWorkOrderBeingEdited) {
          this.stopHeartbeat();
          return;
        }

        updateWorkOrderBeingEditedTimestamp(this.selectedWorkOrder.view, this.currentUserHasUnsavedWorkOrderChanges);
      }, 60000); // 1 minute
    },

    stopHeartbeat() {
      if (hbInterval) {
        clearInterval(hbInterval);
        hbInterval = null;
      }
    },

    startInactivityWatch() {
      // Clear any existing watch before starting a new one
      this.stopInactivityWatch();

      // read-only viewers hold no editing lock, so never auto-close their session
      if (!this.selectedWorkOrder?.view || this.isWorkOrderBeingEdited) return;

      lastWorkOrderActivityAt = Date.now();
      onWorkOrderActivity = () => {
        lastWorkOrderActivityAt = Date.now();
      };

      WORK_ORDER_ACTIVITY_EVENTS.forEach((eventName) => {
        document.addEventListener(eventName, onWorkOrderActivity, { passive: true });
      });

      // poll instead of restarting a timer on every event, mousemove alone would fire hundreds of times
      inactivityInterval = setInterval(() => {
        if (!this.selectedWorkOrder?.view) {
          this.stopInactivityWatch();
          return;
        }

        if (Date.now() - lastWorkOrderActivityAt < WORK_ORDER_INACTIVITY_MS) {
          return;
        }

        // closing the work order out from under a child dialog would strand it, so wait that out
        if (document.querySelectorAll('.modal.show').length > 1) {
          return;
        }

        this.handleWorkOrderInactivity();
      }, INACTIVITY_CHECK_MS);
    },

    stopInactivityWatch() {
      if (inactivityInterval) {
        clearInterval(inactivityInterval);
        inactivityInterval = null;
      }

      if (onWorkOrderActivity) {
        WORK_ORDER_ACTIVITY_EVENTS.forEach((eventName) => {
          document.removeEventListener(eventName, onWorkOrderActivity);
        });
        onWorkOrderActivity = null;
      }
    },

    handleWorkOrderInactivity() {
      // stop watching first so a slow close cannot trigger this a second time
      this.stopInactivityWatch();

      this.saveOpenWorkOrderEdits();

      // clearRepairs raises the notice once the modal is actually gone, showing it here would
      // put it on top of a work order modal that is still closing. Capture what the notice needs
      // now, clearRepairs resets selectedWorkOrder on its way through.
      this.inactivityClosedWorkOrder = {
        id: this.selectedWorkOrder.view,
        label: this.selectedWorkOrder.accepted && this.selectedWorkOrder.accepted.value
          ? `Work Order #${this.selectedWorkOrder.number}`
          : `Work Request #${this.selectedWorkOrder.number}`
      };

      // checkIfWorkOrderIsUpdating holds the close until the save started above has finished,
      // and clearRepairs then releases the editing lock
      this.$bvModal.hide('modal_viewWorkOrder');
    },

    showInactivityClosedNotice() {
      const closedWorkOrder = this.inactivityClosedWorkOrder;
      this.inactivityClosedWorkOrder = null;

      // stays up until acknowledged, the user has been away and needs to see what happened
      Swal.fire({
        title: 'Closed Due To Inactivity',
        html: `
          <div class="alert alert-info mb-3">
            <strong>${closedWorkOrder.label}</strong>
          </div>
          <p class="mb-2">This work order was closed after ${WORK_ORDER_INACTIVITY_MS / 60000} minutes of inactivity.</p>
          <p class="mb-0">Your changes have been saved and the work order is free for someone else to edit.</p>
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Reopen Work Order',
        confirmButtonColor: '#3085d6',
        cancelButtonText: 'Close',
        cancelButtonColor: '#6c757d',
        allowOutsideClick: false,
        allowEscapeKey: false
      }).then((result) => {
        if (result.isConfirmed) {
          this.reopenInactivityClosedWorkOrder(closedWorkOrder);
        }
      });
    },

    reopenInactivityClosedWorkOrder(closedWorkOrder) {
      const workOrder = this.workOrdersData.find((x) => x.view === closedWorkOrder.id);

      if (!workOrder) {
        Vue.$toast.open({
          message: `${closedWorkOrder.label} could not be reopened. Please refresh the board.`,
          type: 'error',
          duration: 4000,
          position: 'top-right',
        });
        return;
      }

      // goes back through the normal open path, so the editing lock is re-checked first in case
      // someone else claimed the work order while it sat closed
      this.populateWorkOrder(workOrder);
    },

    saveOpenWorkOrderEdits() {
      // an open repair cell writes through its @blur handler, so blurring it runs the normal save path
      const activeElement = document.activeElement;
      if (activeElement && typeof activeElement.blur === 'function') {
        activeElement.blur();
      }

      // the general note saves from its check icon rather than on blur, so write it directly
      if (this.selectedWorkOrderNoteEditMode &&
          this.selectedWorkOrderNoteEdit !== (this.selectedWorkOrder.generalNotes || '')) {
        this.handleEditWorkOrderNote();
      }
    },

    getAlphabeticalLetter(index) {
      // this will return a sequential alphabetical letter starting at A based on the index of the repair
      // if the workorder has more than 25 letters it starts returning AA, BB, and so on.
      const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
      let letter = "";
      let level = 1;

      while (index >= Math.pow(26, level)) {
        index -= Math.pow(26, level);
        level++;
      }

      for (let i = 0; i < level; i++) {
        letter = alphabet[index % 26] + letter;
      }

      return letter;
    },
    checkIfWorkOrderIsUpdating(bvModalEvt) {
      // This function's purpose is to run when the modal is about to be hidden and will check if the work order is currently updating.
      // If it's updating it will set a 50ms interval and close the modal when the update is done.
      // This prevents the @blur function on modal inputs from racing the modal close.
      if (!this.allowWorkOrderModalClose) {
        bvModalEvt.preventDefault();
        if (!this.workOrderUpdating) {
          this.allowWorkOrderModalClose = true;
          // Use nextTick to avoid recursive call
          this.$nextTick(() => {
            this.$bvModal.hide('modal_viewWorkOrder');
          });
        } else {
          let elapsed = 0;
          const timeout = 5000;
          const checkInterval = setInterval(() => {
            if (!this.workOrderUpdating) {
              clearInterval(checkInterval);
              this.allowWorkOrderModalClose = true;
              this.$nextTick(() => {
                this.$bvModal.hide('modal_viewWorkOrder');
              });
            } else if(elapsed >= timeout){
              clearInterval(checkInterval);
            }
            elapsed += 50;
          }, 50);
        }
      }
    },
    waitForPendingRepairSave() {
      // A blur-save (editRepair) may still be in flight; wait for it so a whole-array
      // repairs write can't be built from, or ordered against, a concurrent save.
      // Mirrors checkIfWorkOrderIsUpdating's 50ms poll / 5s cap.
      if (!this.workOrderUpdating) return Promise.resolve();
      return new Promise((resolve) => {
        let elapsed = 0;
        const timeout = 5000;
        const checkInterval = setInterval(() => {
          elapsed += 50;
          if (!this.workOrderUpdating || elapsed >= timeout) {
            clearInterval(checkInterval);
            resolve();
          }
        }, 50);
      });
    },
    handleSelectedWorkOrderToReceiveIssue: function(e, workOrder){
      let workOrderCopy = lodash.cloneDeep(workOrder);
      workOrderCopy.selectedToReceiveIssue = e;
      let woCopyIndex = this.existingWorkOrdersWithSameUnitsAsSelectedIssues.findIndex((x)=> x.view === workOrderCopy.view);

      if (woCopyIndex !== -1) {
        this.existingWorkOrdersWithSameUnitsAsSelectedIssues.splice(woCopyIndex, 1, workOrderCopy);
      }

      if(e){
        this.selectedExistingWO = workOrder;
      } else{
        this.selectedExistingWO = null;
      }
    },
    addRepairs: async function (bvModalEvt) {
      this.validateEntry('odometer-issue', true);
      this.validateEntry('hourMeter-issue', true);
      if (
        this.selectedWorkOrder.units?.length > 0 &&
        this.selectedWorkOrder.date &&
        this.newWorkOrderRepairs.length > 0 &&
        (this.newIssue === null || this.newIssue.trim() === '')
      ) {
        let user = JSON.parse(sessionStorage.authUser);
        //format the newly added issues
        let submittedRepairs = [];
        this.newWorkOrderRepairs.forEach((repair) =>
          submittedRepairs.push({
            date: this.selectedWorkOrder.date,
            createdDate: new Date(),
            units: this.selectedWorkOrder.units,
            org: sessionStorage.org,
            issue: repair.issue,
            repair: null,
            number: null,
            workOrderID: null,
            completed: false,
            repairedBy: [
              {
                name: null,
                id: null,
                date: null,
              },
            ],
            completedBy: {
              name: null,
              id: null,
              date: null,
            },
            submitted: {
              name: user.displayName,
              time: new Date(),
              uid: user.uid,
              value: true,
            },
            sourceOfParts: null,
            repairCost: null,
            documents: null,
            documentStatus: null,
            isVisible: true,
            odometer: this.selectedWorkOrder.odometer,
            hourMeter: this.selectedWorkOrder.hourMeter
          })
        );
        let promises = [];
        let submittedIssues = [];
        try {
          let repairsCopy = lodash.cloneDeep(this.repairsData);

          submittedRepairs.forEach((repair) => {
            promises.push(
              firestore()
                .collection('workOrderRepairs')
                .add(repair)
                .then((doc) => {
                  repairsCopy.push({ ...repair, id: doc.id, selected: false });
                })
            );
            let repairUnitsNamed = [];
            repair.units.forEach((unit) => {
              repairUnitsNamed.push(this.getUpToDateUnitName(unit));
            });
            submittedIssues.push({issue: repair.issue, date: moment(repair.date).format('ll'), units: repairUnitsNamed.join(', '), odometer: repair.odometer, hourMeter: repair.hourMeter});
          });

          await Promise.all(promises).then(() => {
            this.repairsData = repairsCopy;
            if(this.searchFiltersUsed){
              this.$refs.kanbanSearch.search();
            }

            this.clearRepairs();

            Vue.$toast.open({
              message: 'Work request added successfully',
              type: 'success',
              duration: 4000,
              position: 'top-right',
            });
          });

          //reset the change notes.
          this.changeNotes = '';
        } catch (error) {
          console.log(error);
          Vue.$toast.open({
            message: 'An error was encountered adding the work request',
            type: 'error',
            duration: 4000,
            position: 'top-right',
          });
        }
      } else {
        //entry validation is different upon submit, if validation doesnt pass, submit for validation again
        // with submit set to true
        this.submitErrorOdometer = this.validateEntry('odometer-issue', true);
        this.submitErrorHourMeter = this.validateEntry('hourMeter-issue', true);
        this.submitErrorDate = this.validateEntry('date', true);
        this.submitErrorUnit = this.validateEntry('unit', true);
        this.submitErrorIssue = this.newIssue && this.newIssue.trim() !== '' ? null : this.validateEntry('issue', true);
        this.submitErrorAddIssue = this.newIssue !== null && this.newIssue.trim() !== '' ? false : null;

        bvModalEvt.preventDefault();
      }
    },
    editNewRepair: function (bvModalEvent) {
      // prevent modal from closing
      bvModalEvent.preventDefault();

      const repairId = this.repairToEdit.id;

      // check validation
      if (this.editIssueValid) {
        // check to see if anything was changed
        if (
          this.repairToEdit.date !== this.repairToEditInitial.date ||
          this.repairToEdit.units !== this.repairToEditInitial.units ||
          this.repairToEdit.issue !== this.repairToEditInitial.issue ||
          this.repairToEdit.odometer !== this.repairToEditInitial.odometer ||
          this.repairToEdit.hourMeter !== this.repairToEditInitial.hourMeter
        ) {
          // update db doc
          try {
            firestore()
              .collection('workOrderRepairs')
              .doc(repairId)
              .update({
                issue: this.repairToEdit.issue,
                units: this.repairToEdit.units,
                date: this.repairToEdit.date,
                odometer: this.repairToEdit.odometer,
                hourMeter: this.repairToEdit.hourMeter
              })
              .then(() => {
                // add the new changes to the repairs array
                let index = this.repairsData.findIndex(
                  (x) => x.id === repairId
                );
                this.repairsData[index].issue = this.repairToEdit.issue;
                this.repairsData[index].units = this.repairToEdit.units;
                this.repairsData[index].date = this.repairToEdit.date;
                this.repairsData[index].odometer = this.repairToEdit.odometer;
                this.repairsData[index].hourMeter = this.repairToEdit.hourMeter;

                // manually close the modal
                this.$bvModal.hide('modal_editNewRepair');

                Vue.$toast.open({
                  message: 'Repair edited successfully',
                  type: 'success',
                  duration: 4000,
                  position: 'top-right',
                });
              });
          } catch (error) {
            console.log('Error editing repair', error);
            Vue.$toast.open({
              message: 'An error was encountered when editing the repair',
              type: 'error',
              duration: 4000,
              position: 'top-right',
            });
          }
        }
      }
    },
    handleEditNewRepair: function (repair) {
      let repairCopy = lodash.cloneDeep(repair)
      if(repairCopy.hourMeter === undefined){
        repairCopy.hourMeter = null
      }
      if(repairCopy.odometer === undefined){
        repairCopy.odometer = null
      }
      this.repairToEdit = repairCopy;
      this.repairToEditInitial = repair;

    },
    deleteNewRepair: function (repairId) {
      Swal.fire({
        title: 'Are you sure?',
        text: 'This repair will be deleted permanently',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#34c38f',
        cancelButtonColor: '#f46a6a',
        confirmButtonText: 'Yes',
      }).then((result) => {
        if (result.value) {
          try {
            //remove doc from db
            firestore()
              .collection('workOrderRepairs')
              .doc(repairId)
              .delete()
              .then(() => {
                // remove item from repairs array
                let index = this.repairsData.findIndex(
                  (x) => x.id === repairId
                );
                this.repairsData.splice(index, 1);

                Vue.$toast.open({
                  message: 'Repair deleted successfully',
                  type: 'success',
                  duration: 4000,
                  position: 'top-right',
                });
              });
          } catch (error) {
            console.log('Error deleting repair', error);
            Vue.$toast.open({
              message: 'An error was encountered when deleting the repair',
              type: 'error',
              duration: 4000,
              position: 'top-right',
            });
          }
        }
      });
    },
    convertTimestampObjectToDate(timeStampObject) {
      let dateObject = JSON.parse(JSON.stringify(timeStampObject));
      console.log(timeStampObject, this.repairs[0].date)
      if (timeStampObject.nanoseconds) {
        dateObject = timeStampObject.toDate()
      }

      return dateObject;
    },
    handleCreateWorkOrderModalOpen: function(){
      // if just one issue is selected, set that issue's hour/odomoter values as the work orders
      if(this.selectedRepairs.length === 1){
        this.selectedWorkOrder.odometer = this.selectedRepairs[0].odometer;
        this.selectedWorkOrder.hourMeter = this.selectedRepairs[0].hourMeter;
      } else {
        // if multiple issues are selected check if they're all on the same day if so set the work order's hours/odometer fields to most recent issues values
        // also check if each selected repair has the same units
        let uniqueRepairDates = [];
        let selectedRepairsHaveSameUnits = true;

        this.selectedRepairs.forEach((repair)=>{
          let createdDay = repair.date.toLocaleDateString();

          if(!uniqueRepairDates.find((x) => x === createdDay)){
            uniqueRepairDates.push(createdDay);
          }

          if(repair.units.length === this.selectedRepairs[0].units.length){
            repair.units.forEach((unit)=>{
              if(!this.selectedRepairs[0].units.find((x) => x === unit )){
                selectedRepairsHaveSameUnits = false;
              }
            });
          }else{
            selectedRepairsHaveSameUnits = false;
          }
        });

        if(uniqueRepairDates.length === 1 && selectedRepairsHaveSameUnits){
          const repairWithMostRecentDate = this.selectedRepairs.reduce((latest, current) => {
            return (current.createdDate > latest.createdDate) ? current : latest;
          }, this.selectedRepairs[0]);

          this.selectedWorkOrder.odometer = repairWithMostRecentDate.odometer;
          this.selectedWorkOrder.hourMeter = repairWithMostRecentDate.hourMeter;
        }
        else{
          this.selectedWorkOrder.odometer = null;
          this.selectedWorkOrder.hourMeter = null;
        }
      }
    },
    async createWorkOrder(bvModalEvt) {
      this.validateEntry('odometer', true);
      this.validateEntry('hourMeter', true);
      if (
        this.selectedRepairs.length > 0 &&
        this.selectedWorkOrder.date &&
        this.odometerValid &&
        this.hourMeterValid
      ) {
        let user = JSON.parse(sessionStorage.authUser);

      let accepted = {
        name: user.displayName,
        time: new Date(),
        uid: user.uid,
        value: true,
      };
      let number;
      let lastNumberUsed;
      let settingsID;
      let workOrderID;

      //Get the last work order number used and the format from the database
      try {
        await firestore()
          .collection('organizations')
          .doc(sessionStorage.org)
          .collection('settings')
          .get()
          .then((querySnapshot) => {
            querySnapshot.forEach((doc) => {
              settingsID = doc.id;
              let numberFormat = doc.data().workOrderNumberFormat
                ? doc.data().workOrderNumberFormat
                : null;
              if (numberFormat !== null) {
                if (numberFormat.lastNumberUsed !== null) {
                  number =
                    numberFormat.prepend +
                    (Number(numberFormat.lastNumberUsed) + 1).toString() +
                    numberFormat.append;
                  lastNumberUsed = numberFormat.lastNumberUsed;
                } else {
                  number =
                    numberFormat.prepend +
                    Number(numberFormat.initalNumber).toString() +
                    numberFormat.append;
                  lastNumberUsed = numberFormat.initialNumber;
                }
              }
            });
          });
      } catch (error) {
        console.log(error);
      }
      //Next, update the work order document with the new accepted and number values
      try {
        // make a map of all the units for this workorder
        let allUnitsForThisWorkOrder = [];
        this.selectedRepairs.forEach((repair) => {
          if (repair.units) {
            repair.units.forEach((unit) => {
              if (!allUnitsForThisWorkOrder.includes(unit)) {
                allUnitsForThisWorkOrder.push(unit);
              }
            });
          }
        });

        //format work order
        let workOrder = {
          number: number,
          date: this.selectedWorkOrder.date,
          odometer: this.selectedWorkOrder.odometer,
          hourMeter: this.selectedWorkOrder.hourMeter,
          createdDate: new Date(),
          units: allUnitsForThisWorkOrder,
          org: sessionStorage.org,
          accepted: accepted,
          repairsComplete: {
            name: null,
            time: null,
            uid: null,
            value: false,
          },
          repairsApproved: {
            name: null,
            time: null,
            uid: null,
            value: false,
          },
          submitted: {
            name: user.displayName,
            time: new Date(),
            uid: user.uid,
          },
          repairs: this.selectedRepairs,
          changeNotes: [],
          preTripDeficiency: this.selectedWorkOrder.preTripDeficiency,
          documentStatus: false,
          isVisible: true,
          generalNotes: this.selectedWorkOrder.generalNotes ? this.selectedWorkOrder.generalNotes : null
        };

        //push to database
        await firestore()
          .collection('workOrders')
          .add(withLastUpdated(workOrder))
          .then((doc) => {
            // add extra fields, used this method to make addRepairmanvisible field reactive
            workOrderID = doc.id;
            workOrder['view'] = workOrderID;
            let selectedRepairsCopy = lodash.cloneDeep(this.selectedRepairs)
            this.addExtraFields({ repairs: selectedRepairsCopy });
            workOrder.repairs = selectedRepairsCopy;
            // push new workOrder to workOrders
            let workOrderCopy = lodash.cloneDeep(this.workOrdersData);
            workOrderCopy = [workOrder, ...workOrderCopy];

            this.workOrdersData = workOrderCopy;
          })
          .then(() => {
            //Update the workOrderNumberFormat doc with the new last number used
            firestore()
              .collection('organizations')
              .doc(sessionStorage.org)
              .collection('settings')
              .doc(settingsID)
              .update({
                'workOrderNumberFormat.lastNumberUsed': (
                  Number(lastNumberUsed) + 1
                ).toString(),
              });
          })
          .then(async () => {
            //Update the repairs with the work order number
            let promises = [];
            let repairsCopy = lodash.cloneDeep(this.repairsData);

            this.selectedRepairs.forEach((repair) => {
              promises.push(
                firestore()
                  .collection('workOrderRepairs')
                  .doc(repair.id)
                  .update({ number: number, workOrderID: workOrderID })
                  .then(() => {
                    let index = repairsCopy.findIndex(
                      (x) => x.id === repair.id
                    );
                    repairsCopy.splice(index, 1);
                  })
              );
            });

            await Promise.all(promises);
            this.repairsData = repairsCopy;
            if(this.searchFiltersUsed){
              this.$refs.kanbanSearch.search();
            }
            this.clearRepairs();
            this.odometerValid = true;
            this.hourMeterValid = true;

            Vue.$toast.open({
              message: 'Work request added successfully',
              type: 'success',
              duration: 4000,
              position: 'top-right',
            });

            // re-sort the work order columns
            this.filterAllColumns();
            this.filterWorkOrdersNotRendered();
          });
      } catch (error) {
        console.log('Error accepting work order', error);
        Vue.$toast.open({
          message: 'An error was encountered when accepting the work order',
          type: 'error',
          duration: 4000,
          position: 'top-right',
        });
      }
      }
      else{
        bvModalEvt.preventDefault();
        this.validateEntry('odometer', true);
        this.validateEntry('hourMeter', true);
      }
    },
    addRepairPersonButton: function (index) {
      this.selectedWorkOrder.repairs[index].addRepairmanVisible =
        !this.selectedWorkOrder.repairs[index].addRepairmanVisible;

      this.selectedWorkOrder.repairs[index].newRepairman.name = null;
      this.selectedWorkOrder.repairs[index].newRepairman.id = null;
      this.selectedWorkOrder.repairs[index].newRepairman.date = new Date();
    },
    editWorkOrder: function (bvModalEvt) {
      if (
        this.selectedWorkOrderEdit.units.length > 0 &&
        this.odometerEditValid &&
        this.hourMeterEditValid
      ) {
        //format what needs to be updated in the work order

        let workOrder = {
          date: this.selectedWorkOrderEdit.date,
          units: this.selectedWorkOrderEdit.units,
          odometer: this.selectedWorkOrderEdit.odometer,
          preTripDeficiency: this.selectedWorkOrderEdit.preTripDeficiency,
          hourMeter: this.selectedWorkOrderEdit.hourMeter,
          generalNotes: this.selectedWorkOrderEdit.generalNotes,
          lastModified: firestore.FieldValue.serverTimestamp(),
          unit: firestore.FieldValue.delete(),
        };

        //update database with version checking
        try {
            firestore()
            .collection('workOrders')
            .doc(this.documentID)
            .update(workOrder)
            .then(() => {
              this.selectedWorkOrder.date = this.selectedWorkOrderEdit.date;
              this.selectedWorkOrder.units = this.selectedWorkOrderEdit.units;
              this.selectedWorkOrder.odometer = this.selectedWorkOrderEdit.odometer;
              this.selectedWorkOrder.hourMeter = this.selectedWorkOrderEdit.hourMeter;
              this.selectedWorkOrder.preTripDeficiency = this.selectedWorkOrderEdit.preTripDeficiency;
              this.selectedWorkOrder.generalNotes = this.selectedWorkOrderEdit.generalNotes;
              this.selectedWorkOrder.lastModified = new Date();

              let index = this.workOrdersData.findIndex(
                (data) => data.view === this.documentID
              );
              this.workOrdersData.splice(index, 1, this.selectedWorkOrder);

              Vue.$toast.open({
                message: 'Work order edited successfully',
                type: 'success',
                duration: 4000,
                position: 'top-right',
              });

              this.submitErrorUnitEdit = null;
            })
            .catch((error) => {
              console.log(error);
              Vue.$toast.open({
                message: 'An error was encountered when editing the work order',
                type: 'error',
                duration: 4000,
                position: 'top-right',
              });
            });
        } catch (error) {
          console.log(error);
          Vue.$toast.open({
            message: 'An error was encountered when editing the work order',
            type: 'error',
            duration: 4000,
            position: 'top-right',
          });
        }
      } else {
        //entry validation is different upon submit, if validation doesnt pass, submit for validation again
        // with submit set to true
        this.submitErrorOdometerEdit = this.validateEntry(
          'odometer-edit',
          true
        );
        this.submitErrorHourMeterEdit = this.validateEntry(
          'hourMeter-edit',
          true
        );
        this.submitErrorUnitEdit = this.validateEntry('unit-edit', true);

        bvModalEvt.preventDefault();
      }
    },
    handleEditWorkOrderNote: function(){
      try {
          firestore()
            .collection('workOrders')
            .doc(this.selectedWorkOrder.view)
            .update({generalNotes: this.selectedWorkOrderNoteEdit})
            .then(() => {
              this.selectedWorkOrder.generalNotes = this.selectedWorkOrderNoteEdit;

              let index = this.workOrdersData.findIndex(
                (data) => data.view === this.selectedWorkOrder.view
              );

              this.workOrdersData.splice(index, 1, this.selectedWorkOrder);

              this.selectedWorkOrderNoteEditMode = false;

              Vue.$toast.open({
                message: 'Work order note edited successfully',
                type: 'success',
                duration: 4000,
                position: 'top-right',
              });
            });
        } catch (error) {
          console.log(error);
          Vue.$toast.open({
            message: 'An error was encountered when editing the work order note',
            type: 'error',
            duration: 4000,
            position: 'top-right',
          });
        }
    },
    handleShowWorkOrderIssueDetails: function(woID, workOrderColumnDataProperty, calledFromWorkOrderColumn = true){
      if(!calledFromWorkOrderColumn){
        workOrderColumnDataProperty = 'existingWorkOrdersWithSameUnitsAsSelectedIssues';
      }

      let workOrdersCopy = lodash.cloneDeep(this[workOrderColumnDataProperty]);
      const woIndex = workOrdersCopy.findIndex((x) => x.view === woID);
      if(woIndex !== -1){
        workOrdersCopy[woIndex].showIssueDetails = !workOrdersCopy[woIndex].showIssueDetails;
      }

      this[workOrderColumnDataProperty] = workOrdersCopy;
    },
    editRepair: async function (bvModalEvt) {
      this.workOrderUpdating = true;
      if (this.editIssueValid) {
        try {
          // concurrency guard
          if (!(await this.ensureWorkOrderFresh(this.documentID, this.woLastPull[this.documentID]))) {
            this.workOrderUpdating = false;
            return;
          }
          let selectedWorkOrderCopy = lodash.cloneDeep(this.selectedWorkOrder)

          const writeIdx = this.resolveRepairIndex(selectedWorkOrderCopy.repairs);
          if (writeIdx === -1) {
            this.workOrderUpdating = false;
            Vue.$toast.open({
              message: 'This repair could not be saved. Please refresh the work order and try again.',
              type: 'error',
              duration: 6000,
              position: 'top-right',
            });
            return false;
          }

          selectedWorkOrderCopy.repairs.splice(
            writeIdx,
            1,
            this.selectedRepair
          );

          await firestore()
            .collection('workOrders')
            .doc(this.documentID)
            .update(withLastUpdated({
              repairs: selectedWorkOrderCopy.repairs,
            }))
            .then(async () => {
              // keep the local array in step with what was just written
              const liveIdx = this.resolveRepairIndex(this.selectedWorkOrder.repairs);
              if (liveIdx !== -1) {
                this.selectedWorkOrder.repairs.splice(liveIdx, 1, this.selectedRepair);
              }
              this.selectedRepairInitial = lodash.cloneDeep(this.selectedRepair);

              let workOrdersCopy = lodash.cloneDeep(this.workOrdersData)
              let index = workOrdersCopy.findIndex(
                (data) => data.view === this.documentID
              );
              workOrdersCopy.splice(index, 1, this.selectedWorkOrder);
              this.workOrdersData = workOrdersCopy;

              // update the repair field in workOrderRepairs collection

              if(this.selectedRepair.id && this.selectedRepair.id.trim() !== ''){
                await firestore()
                .collection('workOrderRepairs')
                .doc(this.selectedRepair.id)
                .update({
                  repair: this.selectedRepair.repair,
                })
                .then(() => {
                  this.workOrderUpdating = false;
                  Vue.$toast.open({
                    message: 'Repair edited successfully',
                    type: 'success',
                    duration: 4000,
                    position: 'top-right',
                  });
                });
              }else{
                this.workOrderUpdating = false;
                Vue.$toast.open({
                    message: 'Repair edited successfully',
                    type: 'success',
                    duration: 4000,
                    position: 'top-right',
                  });
              }
            });
        } catch (error) {
          this.workOrderUpdating = false;
          console.log('Error editing repair', error);
          Vue.$toast.open({
            message: 'An error was encountered when editing the repair',
            type: 'error',
            duration: 4000,
            position: 'top-right',
          });
        }
      } else {
        //entry validation is different upon submit, if validation doesnt pass, submit for validation again
        // with submit set to true

        // this.submitErrorEditRepair = this.validateEntry('editRepair', true);
        this.submitErrorEditIssue = this.validateEntry('editIssue', true);

        this.workOrderUpdating = false;
        bvModalEvt.preventDefault();
      }
    },
    deleteRepair: function (index) {
      Swal.fire({
        title: 'Are you sure?',
        text: this.selectedWorkOrder.repairs.length === 1
          ? 'This is the last issue on this work order. It will be deleted permanently and the work order will be left empty.'
          : 'This item will be deleted permanently',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#34c38f',
        cancelButtonColor: '#f46a6a',
        confirmButtonText: 'Yes',
      }).then(async (result) => {
        if (result.value) {
          try {
            // concurrency guard
            if (!(await this.ensureWorkOrderFresh(this.documentID, this.woLastPull[this.documentID]))) {
              return;
            }
            this.selectedWorkOrder.repairs.splice(index, 1);
            //check the status of the repairs
            let repairsComplete = this.checkRepairStatus(
              this.selectedWorkOrder.repairs
            );
            let selectedWorkOrderCopy = this.removeExtraFields(
              lodash.cloneDeep(this.selectedWorkOrder)
            );
            //update database with deleted repairs and new repairsComplete status
            firestore()
              .collection('workOrders')
              .doc(this.documentID)
              .update(withLastUpdated({
                repairs: selectedWorkOrderCopy.repairs,
                repairsComplete: repairsComplete,
              }))
              .then(() => {
                let workOrdersCopy = lodash.cloneDeep(this.workOrdersData);
                this.selectedWorkOrder.repairsComplete = repairsComplete;
                let index = workOrdersCopy.findIndex(
                  (data) => data.view === this.documentID
                );
                workOrdersCopy.splice(index, 1, this.selectedWorkOrder);
                this.workOrdersData = workOrdersCopy

                Vue.$toast.open({
                  message: 'Repair deleted successfully',
                  type: 'success',
                  duration: 4000,
                  position: 'top-right',
                });
              });
          } catch (error) {
            console.log('Error deleting repair', error);
            Vue.$toast.open({
              message: 'An error was encountered when deleting the repair',
              type: 'error',
              duration: 4000,
              position: 'top-right',
            });
          }
        }
      });
    },
    handleBulkDeleteIssues(){
      Swal.fire({
        title: 'Are you sure?',
        text: 'The selected issue(s) will be deleted permanently',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#34c38f',
        cancelButtonColor: '#f46a6a',
        confirmButtonText: 'Yes',
      }).then(async (result) => {
        if (result.value) {
          try {
            const selectedRepairsInitialLength = this.selectedRepairs.length - 1;

            for(const [index, issue] of this.selectedRepairs.entries()){
              await firestore()
                .collection('workOrderRepairs')
                .doc(issue.id)
                .delete()
                .then(() => {
                  let repairsCopy = lodash.cloneDeep(this.repairsData);
                  const issueIndex = repairsCopy.findIndex(x => x.id === issue.id);
                  repairsCopy.splice(issueIndex, 1);
                  this.repairsData = repairsCopy;

                  if(index === selectedRepairsInitialLength){
                    Vue.$toast.open({
                      message: 'Issue(s) deleted successfully',
                      type: 'success',
                      duration: 4000,
                      position: 'top-right',
                    });
                  }
                });
            }
          } catch (error) {
            console.log('Error deleting issue(s)', error);
            Vue.$toast.open({
              message: 'An error was encountered when deleting the selected issue(s)',
              type: 'error',
              duration: 4000,
              position: 'top-right',
            });
          }
        }
      });
    },
    addIssue() {
      //pushes the issue to the temporary table in the modal
      let index = this.newWorkOrderRepairs.push({ issue: this.newIssue });
      this.newWorkOrderRepairs[index - 1] = {
        index: index - 1,
        issue: this.newIssue,
      };
      this.newIssue = null;
    },
    spliceRepairs(repair) {
      let repairsDataCopy = lodash.cloneDeep(this.repairsData);
      let index = repairsDataCopy.findIndex((x) => x.id === repair.id);
      repairsDataCopy[index].selected = repair.selected;
      this.repairsData = repairsDataCopy;

      // filter work orders to find work orders that the selected repairs can be added to
      this.filterExistingWorkOrdersWithSameUnitsAsSelectedIssues();
    },
    async addSingleIssue() {
      // concurrency guard (before creating the repair doc)
      if (!(await this.ensureWorkOrderFresh(this.documentID, this.woLastPull[this.documentID]))) {
        return;
      }
      const user = JSON.parse(sessionStorage.authUser);
      let issueToAdd = {
        date: this.selectedWorkOrder.date,
        createdDate: new Date(),
        units: this.selectedWorkOrder.units,
        org: sessionStorage.org,
        issue: this.singleIssue,
        repair: null,
        completed: false,
        repairedBy: [
          {
            name: null,
            id: null,
            date: null,
          },
        ],
        completedBy: {
          name: null,
          id: null,
          date: null,
        },
        submitted: {
          name: user.displayName,
          time: new Date(),
          uid: user.uid,
          value: true,
        },
        sourceOfParts: null,
        repairCost: null,
        documents: null,
        documentStatus: null,
        isVisible: true,
        odometer: this.selectedWorkOrder.odometer,
        hourMeter: this.selectedWorkOrder.hourMeter,
        workOrderID: this.selectedWorkOrder.view,
        number: this.selectedWorkOrder.number
      };

      try {
        // first lets add it to the workOrderRepairs collection so we can get a unique id for it
        firestore()
            .collection('workOrderRepairs')
            .add(issueToAdd)
            .then((doc)=>{
              let selectedWorkOrderCopy = this.removeExtraFields(
                lodash.cloneDeep(this.selectedWorkOrder)
              );

              // remove wo fields and add workOrderRepair doc id
              delete issueToAdd.workOrderID;
              delete issueToAdd.number;
              issueToAdd['id'] = doc.id;

              selectedWorkOrderCopy.repairs.push(issueToAdd);

              //check the status of the repairs
              let repairsComplete = this.checkRepairStatus(
                selectedWorkOrderCopy.repairs
              );

              firestore()
                .collection('workOrders')
                .doc(this.documentID)
                .update(withLastUpdated({
                  repairs: selectedWorkOrderCopy.repairs,
                  repairsComplete: repairsComplete,
                }))
                .then(() => {
                  let selectedWorkOrderCopyExtraFields = lodash.cloneDeep(this.selectedWorkOrder);

                  // add extra fields to the issue
                  issueToAdd['addRepairmanVisible'] = false;
                  issueToAdd['newRepairman'] = { name: null, id: null, date: null };
                  issueToAdd['editEnabled'] = false;
                  if (
                    this.$store.state.custom.workOrders.repair_hours_enabled &&
                    issueToAdd['repairHours'] == undefined
                  ) {
                    issueToAdd['repairHours'] = null;
                  }
                  if (
                    this.$store.state.custom.workOrders.repair_cost_enabled &&
                    issueToAdd['repairCost'] == undefined
                  ) {
                    issueToAdd['repairHours'] = null;
                  }
                  if (
                    this.$store.state.custom.workOrders.labour_hours_enabled &&
                    issueToAdd['labourHours'] == undefined
                  ) {
                    issueToAdd['labourHours'] = null;
                  }

                  selectedWorkOrderCopyExtraFields.repairs.push(issueToAdd);
                  this.selectedWorkOrder = selectedWorkOrderCopyExtraFields

                  this.singleIssue = null;
                  selectedWorkOrderCopyExtraFields.repairsComplete = repairsComplete;
                  this.selectedWorkOrder = selectedWorkOrderCopyExtraFields
                  let index = this.workOrdersData.findIndex(
                    (data) => data.view === this.documentID
                  );
                  let workOrdersDataCopy = lodash.cloneDeep(this.workOrdersData);
                  workOrdersDataCopy.splice(index, 1, selectedWorkOrderCopyExtraFields);
                  this.workOrdersData = workOrdersDataCopy;

                  //once the issue is added, put focus back in the issue text box.
                  document.getElementById('VWO_addIssue_textarea').focus();
                });
            });
      } catch (error) {
        console.log(error);
      }
    },
    async changeDocumentStatus(workOrderRepairData, isManual) {
      // concurrency guard
      if (!(await this.ensureWorkOrderFresh(this.documentID, this.woLastPull[this.documentID]))) {
        return;
      }
      if(isManual){
        // doc status is being manually changed, set the selectedRepair
        this.selectedRepair = workOrderRepairData;
      } else {
        this.checkDocumentStatus();
      }
      let selectedWorkOrderCopy = this.removeExtraFields(
        lodash.cloneDeep(this.selectedWorkOrder)
      );
      try {
        firestore()
          .collection('workOrders')
          .doc(this.documentID)
          .update(withLastUpdated({
            repairs: selectedWorkOrderCopy.repairs,
          }))
          .then(() => {
            let index = this.workOrdersData.findIndex(
              (data) => data.view === this.documentID
            );
            this.workOrdersData.splice(index, 1, this.selectedWorkOrder);
            Vue.$toast.open({
              message: 'Document status changed successfully',
              type: 'success',
              duration: 4000,
              position: 'top-right',
            });
          });
      } catch (error) {
        console.log(error);
        Vue.$toast.open({
          message: 'An error was encountered when changing the document status',
          type: 'error',
          duration: 4000,
          position: 'top-right',
        });
      }
    },
    // every empty work order in the org, independent of the board's search filters
    async loadEmptyWorkOrders() {
      try {
        const querySnapshot = await getEmptyWorkOrders();
        const settings = this.$store.state.custom.workOrders;
        const emptyWorkOrders = [];
        querySnapshot.forEach((doc) => {
          emptyWorkOrders.push(
            hydrateWorkOrderDoc(doc.data(), doc.id, this.units, settings.repair_hours_enabled, settings.repair_cost_enabled, settings.labour_hours_enabled)
          );
        });
        this.emptyWorkOrders = emptyWorkOrders.sort((a, b) => this.extractNumber(String(b.number)) - this.extractNumber(String(a.number)));
      } catch (error) {
        console.log('Error loading empty work orders', error);
      }
    },
    openEmptyWorkOrder(workOrder) {
      // saves look the work order up in workOrdersData, so it has to be on the board before it is opened
      if (!this.workOrdersData.find((x) => x.view === workOrder.view)) {
        this.workOrdersData.push(workOrder);
        this.filterAllColumns();
        this.filterWorkOrdersNotRendered();
      }
      this.$bvModal.hide('modal_emptyWorkOrders');
      this.populateWorkOrder(workOrder);
    },
    updateSearchedData(filteredWorkOrders, filteredRepairs, filtersApplied){
      this.workOrdersData = filteredWorkOrders;
      this.repairsData = filteredRepairs;
      this.searchFiltersUsed = filtersApplied

      this.filterAllColumns();
      this.filterWorkOrdersNotRendered();
    },
    handleResubmitModalClose(){
      this.resubmitNotes = null;
    },
    handleWorkOrderSubmit(){
      // if the work order has had requested changes then open the resubmit notes modal, else just submit the work order as normal
      const changesRequestedWorkOrder = this.selectedWorkOrder.changeNotes && this.selectedWorkOrder.changeNotes.length > 0;
      if(changesRequestedWorkOrder){
        this.$refs.resubmitNotesModal.show();
      }
      else{
        this.submitWorkOrder();
      }
    },
    handleResubmitWorkOrder(bvModalEvt){
      if(this.resubmitNotesValid){
        this.submitWorkOrder(true);
      } else{
        bvModalEvt.preventDefault(); //prevent the modal from closing.
      }
    },
    submitWorkOrder(workOrderResubmit = false) {
      let resubmitNotesList = this.selectedWorkOrder.resubmitNotes ? this.selectedWorkOrder.resubmitNotes : [];
      if(workOrderResubmit){
        resubmitNotesList.push({name: this.currentUser.displayName, notes: this.resubmitNotes, time: new Date(), uid: this.currentUser.auth});
      }

      let repairsApproved = {
        name: this.currentUser.displayName,
        time: new Date(),
        uid: this.currentUser.auth,
        value: null,
      };

      try {
        firestore()
          .collection('workOrders')
          .doc(this.documentID)
          .update({
            repairsApproved,
            resubmitNotes: resubmitNotesList
          })
          .then(() => {
            this.selectedWorkOrder.repairsApproved = repairsApproved;
            this.selectedWorkOrder.resubmitNotes = resubmitNotesList;
            let index = this.workOrdersData.findIndex(
              (data) => data.view === this.documentID
            );
            this.workOrdersData.splice(index, 1, this.selectedWorkOrder);

            if(workOrderResubmit){
              this.$refs.resubmitNotesModal.hide();
            }
          });
      } catch (error) {
        console.log('Error submitting work order', error);
        Vue.$toast.open({
          message: 'An error was encountered when submitting the work order',
          type: 'error',
          duration: 4000,
          position: 'top-right',
        });
      }
    },

    async checkCurrentEditingStatus(workOrderId) {
      try {
        const status = await checkWorkOrderEditingStatus(workOrderId);
        this.workOrderEditingStatus = status;

        // Show confirmation dialog if another user is editing and it's not the current user
        if (status.isBeingEdited && status.editedBy &&
            status.editedBy.uid !== this.currentUser?.uid) {

        // call out unsaved work so the user knows taking over would actually destroy something
        const unsavedChangesWarning = status.hasUnsavedChanges
          ? `<div class="alert alert-danger mt-3 mb-0 text-left">
               <i class="bx bxs-error-circle"></i>
               <strong>${status.editedBy.name} has unsaved changes.</strong>
               Any work they have not saved will be lost if you take over editing.
             </div>`
          : '';

        const result = await Swal.fire({
          title: 'Work Order Currently Being Edited',
          html: `
            <p class="mb-0">${status.editedBy.name} is currently editing this work order. Do you want to open it anyway?</p>
            ${unsavedChangesWarning}
          `,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'View Only',
          cancelButtonText: 'Cancel',
        //   showDenyButton: true,
        //   denyButtonText: 'Take Over Editing'
        });

        // if (result.isDenied) {
        //   // User chose to take over editing
        //   await this.setUserAsEditing(workOrderId);
        //   return true;
        // }
        if (result.isConfirmed) {
          // User chose to open in view only mode
          this.wasInReadOnlyMode = true;
          this.autoUpgradeAvailable = true;
          return true;
        }
        if (result.isDismissed) {
          // User cancelled
          return false;
        }
    }
        await this.setUserAsEditing(workOrderId);
        return true;
      } catch (error) {
        console.log('Error checking editing status:', error);
        return true; // On error, allow opening
      }
    },

    async setUserAsEditing(workOrderId) {
      if (workOrderId && this.$store.state.custom.currentUserData) {
        try {
          await setWorkOrderEditingStatus(workOrderId, this.$store.state.custom.currentUserData);
            this.workOrderEditingStatus = {
              isBeingEdited: true,
              editedBy: this.$store.state.custom.currentUserData
            };

          // an unsent issue draft can already exist for this work order, so seed the flag here.
          // the watcher only fires on a change, which would never happen for a draft typed earlier.
          const issueDraft = this.singleIssueDrafts[workOrderId];
          if (issueDraft && issueDraft.trim() !== '') {
            await updateWorkOrderUnsavedChangesStatus(workOrderId, true);
          }
        } catch (error) {
          console.log('Error setting editing status:', error);
        }
      }
    },
    async showTakeOverModal() {
      // re-read the editing status so the unsaved-changes warning reflects typing that started
      // after this user went view-only (the cached status is from when they opened the modal)
      let status = this.workOrderEditingStatus;
      try {
        const freshStatus = await checkWorkOrderEditingStatus(this.documentID);
        if (freshStatus && freshStatus.isBeingEdited && freshStatus.editedBy) {
          status = freshStatus;
        }
      } catch (error) {
        console.log('Error checking editing status:', error);
      }

      // call out unsaved work specifically when we know it exists, so the user knows
      // taking over would actually destroy something
      const unsavedChangesWarning = status.hasUnsavedChanges
        ? `<div class="alert alert-danger text-left">
             <i class="bx bxs-error-circle"></i>
             <strong>${status.editedBy?.name || 'Unknown User'} has unsaved changes.</strong>
             Any work they have not saved will be lost if you take over editing.
           </div>`
        : `<p class="text-danger">
             <strong>If you take over editing, the other user may lose any unsaved changes.</strong>
           </p>`;

      // Use SweetAlert for confirmation to avoid modal conflicts
      const result = await Swal.fire({
        title: 'Take Over Editing',
        html: `
          <div class="text-center">
            <i class="bx bxs-warning text-warning" style="font-size: 48px;"></i>
            <h5 class="mt-3 mb-3">Warning: Take Over Editing</h5>
            <p>This work order is currently being edited by:</p>
            <div class="alert alert-info">
              <strong>${status.editedBy?.name || 'Unknown User'}</strong><br>
              <small class="text-muted">
                Started editing: ${status.editedBy?.timestamp?.toDate ?
                  new Date(status.editedBy.timestamp.toDate()).toLocaleString() : 'Unknown time'}
              </small>
            </div>
            ${unsavedChangesWarning}
            <p>Are you sure you want to take over editing of this work order?</p>
          </div>
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Take Over Editing',
        cancelButtonText: 'Cancel'
      });

      if (result.isConfirmed) {
        await this.handleTakeOverEditing();
      }
    },
    async handleTakeOverEditing() {
      try {
        // Take over editing rights by setting current user as editor
        await this.setUserAsEditing(this.documentID);

        // Exit view-only mode
        this.wasInReadOnlyMode = false;
        this.autoUpgradeAvailable = false;
        this.startHeartbeat();
        this.startInactivityWatch();

        // Show success notification
        this.$bvToast.toast('You have successfully taken over editing of this work order.', {
          title: 'Editing Taken Over',
          variant: 'success',
          solid: true,
          autoHideDelay: 5000,
          toaster: 'b-toaster-top-right'
        });

      } catch (error) {
        console.error('Error taking over editing:', error);
        this.$bvToast.toast('Failed to take over editing. Please try again.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000,
          toaster: 'b-toaster-top-right'
        });
      }
    },
    async clearUserAsEditing() {
      if (this.documentID && this.workOrderEditingStatus?.editedBy?.uid === this.currentUser?.uid) {
        try {
          await clearWorkOrderEditingStatus(this.documentID);
        } catch (error) {
          console.log('Error clearing editing status:', error);
        }
      }
    },
    async handleEditWorkOrder() {
      this.populateEditWorkOrder();
      return true;
    },
    async handleEditButtonClick() {
      const canEdit = await this.handleEditWorkOrder();
      if (canEdit) {
        this.$bvModal.show('modal_editWorkOrder');
      }
    },

    checkRepairStatus(repairs) {
      //check the status of the repairs
      let user = JSON.parse(sessionStorage.authUser);
      let count = 0;
      let repairsComplete;
      repairs.forEach((repair) => {
        if (repair.completed === true) {
          count++;
        }
      });
      if (count === 0) {
        repairsComplete = {
          name: null,
          time: null,
          uid: null,
          value: false,
        };
      } else if (count === repairs.length) {
        repairsComplete = {
          name: user.displayName,
          time: new Date(),
          uid: user.uid,
          value: true,
        };
      } else {
        repairsComplete = {
          name: null,
          time: null,
          uid: null,
          value: null,
        };
      }
      return repairsComplete;
    },
    canMarkAsComplete(index) {
        if(this.isWorkOrderBeingEdited){
            return false;
        }
      let data = this.selectedWorkOrder.repairs[index];
      if(data){
        if (this.selectedWorkOrder.repairsApproved.value !== false) {
          return false;
        } else if (data.completed) {
          return true;
        } else {
          if (
            data.repair === null ||
            data.repair.trim() === '' ||
            data.repairedBy[0].id === null
          ) {
            return false;
          } else {
            return true;
          }
        }
      }
    },
    async changeCompletionStatus(index) {
      // let an in-flight blur-save (editRepair) finish before reading repair state
      // and building the completion write
      await this.waitForPendingRepairSave();
      // concurrency guard
      if (!(await this.ensureWorkOrderFresh(this.documentID, this.woLastPull[this.documentID]))) {
        return;
      }
      let user = JSON.parse(sessionStorage.authUser);
      let date = new Date();
      let completed = null;
      let completedBy = null;
      let repairID = this.selectedWorkOrder.repairs[index].id

      if (this.selectedWorkOrder.repairs[index].completed) {
        this.selectedWorkOrder.repairs[index].completed = false;
        this.selectedWorkOrder.repairs[index].completedBy = {
          date: null,
          id: null,
          name: null,
        };
        this.selectedRepair.completed = false;
        this.selectedRepair.completedBy = {
          date: null,
          id: null,
          name: null,
        };
        completed = false;
        completedBy = {
          date: null,
          id: null,
          name: null,
        };
      } else {
        this.selectedWorkOrder.repairs[index].completed = true;
        this.selectedWorkOrder.repairs[index].completedBy = {
          date: date,
          id: user.uid,
          name: user.displayName,
        };
        this.selectedRepair.completed = true;
        this.selectedRepair.completedBy = {
          date: date,
          id: user.uid,
          name: user.displayName,
        };
        completed = true;
        completedBy = {
          date: date,
          id: user.uid,
          name: user.displayName,
        };
      }

      let repairsComplete = this.checkRepairStatus(
        this.selectedWorkOrder.repairs
      );
      let selectedWorkOrderCopy = this.removeExtraFields(
        lodash.cloneDeep(this.selectedWorkOrder)
      );
      try {
        firestore()
          .collection('workOrders')
          .doc(this.documentID)
          .update(withLastUpdated({
            repairs: selectedWorkOrderCopy.repairs,
            repairsComplete: repairsComplete,
          }))
          .then(async () => {
            this.selectedWorkOrder.repairsComplete = repairsComplete;
            // update the completed fields of the workOrderRepair doc associated to this work order
            await firestore()
              .collection('workOrderRepairs')
              .doc(repairID)
              .update({
                completed: completed,
                completedBy: completedBy
              });


            let index = this.workOrdersData.findIndex(
              (data) => data.view === this.documentID
            );
            this.workOrdersData.splice(index, 1, this.selectedWorkOrder);
            Vue.$toast.open({
              message: 'Repair status changed successfully',
              type: 'success',
              duration: 4000,
              position: 'top-right',
            });
          });
      } catch (error) {
        console.log(error);
        Vue.$toast.open({
          message:
            'An error was encountered when changing the completion status',
          type: 'error',
          duration: 4000,
          position: 'top-right',
        });
      }
    },
    async deleteRepairman(repairData, repairIndex, repairmanIndex) {
      this.populateRepair(repairData, repairIndex);

      let result = await Swal.fire({
        title: 'Are you sure?',
        text: 'This item will be deleted permanently',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#34c38f',
        cancelButtonColor: '#f46a6a',
        confirmButtonText: 'Yes',
      });
      if (result.value) {
        // concurrency guard
        if (!(await this.ensureWorkOrderFresh(this.documentID, this.woLastPull[this.documentID]))) {
          return;
        }
        if (
          this.selectedWorkOrder.repairs[repairIndex].repairedBy.length === 1
        ) {
          this.selectedWorkOrder.repairs[repairIndex].repairedBy.splice(
            repairmanIndex,
            1,
            {
              date: null,
              id: null,
              name: null,
            }
          );
          this.selectedRepair.repairedBy.splice(
            repairmanIndex,
            1,
            {
              date: null,
              id: null,
              name: null,
            }
          );
        } else {
          this.selectedWorkOrder.repairs[repairIndex].repairedBy.splice(
            repairmanIndex,
            1
          );
          this.selectedRepair.repairedBy.splice(
            repairmanIndex,
            1
          );
        }

        let selectedWorkOrderCopy = this.removeExtraFields(
          lodash.cloneDeep(this.selectedWorkOrder)
        );

        try {
          firestore()
            .collection('workOrders')
            .doc(this.documentID)
            .update(withLastUpdated({
              repairs: selectedWorkOrderCopy.repairs,
            }))
            .then(() => {
              this.selectedWorkOrder.repairs[
                repairIndex
              ].addRepairmanVisible = false;
              this.selectedWorkOrder.repairs[
                repairIndex
              ].addRepairmanVisible = false;

              let workOrdersIndex = this.workOrdersData.findIndex(
                (data) => data.view === this.documentID
              );
              this.workOrdersData.splice(
                workOrdersIndex,
                1,
                this.selectedWorkOrder
              );

              Vue.$toast.open({
                message: 'Repair person deleted successfully',
                type: 'success',
                duration: 4000,
                position: 'top-right',
              });
            });
        } catch (error) {
          console.log(error);
          Vue.$toast.open({
            message:
              'An error was encountered when deleting a user from repaired by',
            type: 'error',
            duration: 4000,
            position: 'top-right',
          });
        }
      }
    },
    async addRepairman(index) {
      // concurrency guard
      if (!(await this.ensureWorkOrderFresh(this.documentID, this.woLastPull[this.documentID]))) {
        return;
      }
      let repairmanToAdd = {
        name: this.selectedWorkOrder.repairs[index].newRepairman.id.name,
        id: this.selectedWorkOrder.repairs[index].newRepairman.id.id,
        date: this.selectedWorkOrder.repairs[index].newRepairman.date,
      };

      let selectedWorkOrderCopy = lodash.cloneDeep(this.selectedWorkOrder)
      let selectedRepairCopy = lodash.cloneDeep(this.selectedRepair)

      if (this.selectedWorkOrder.repairs[index].repairedBy[0].name) {
        selectedWorkOrderCopy.repairs[index].repairedBy.push(repairmanToAdd);
        selectedRepairCopy.repairedBy.push(repairmanToAdd);
      } else {
        selectedWorkOrderCopy.repairs[index].repairedBy[0] = repairmanToAdd;
        selectedRepairCopy.repairedBy[0] = repairmanToAdd;
      }

      try {
        firestore()
          .collection('workOrders')
          .doc(this.documentID)
          .update(withLastUpdated({
            repairs: selectedWorkOrderCopy.repairs,
          }))
          .then(() => {
            selectedWorkOrderCopy.repairs[index].newRepairman = {
              name: null,
              id: null,
              date: null,
            };
            selectedWorkOrderCopy.repairs[index].addRepairmanVisible = false;

            let woIndex = this.workOrdersData.findIndex(
              (data) => data.view === this.documentID
            );
            this.workOrdersData.splice(woIndex, 1, selectedWorkOrderCopy);
            this.selectedWorkOrder.repairs = selectedWorkOrderCopy.repairs;
            this.selectedRepair = selectedRepairCopy;

            this.sortRepairsByDate(index);
          });
      } catch (error) {
        console.log(error);
        Vue.$toast.open({
          message:
            'An error was encountered when adding a user to the repaired by field',
          type: 'error',
          duration: 4000,
          position: 'top-right',
        });
      }
    },
    approveWorkOrder() {
      let user = JSON.parse(sessionStorage.authUser);

      let repairsApproved = {
        name: user.displayName,
        time: new Date(),
        uid: user.uid,
        value: true,
      };
      try {
        firestore()
          .collection('workOrders')
          .doc(this.documentID)
          .update({
            repairsApproved,
          })
          .then(() => {
            this.selectedWorkOrder.repairsApproved = repairsApproved;
            let workOrdersIndex = this.workOrdersData.findIndex(
              (data) => data.view === this.documentID
            );
            this.workOrdersData.splice(
              workOrdersIndex,
              1,
              this.selectedWorkOrder
            );
          });
      } catch (error) {
        console.log('Error approving work order', error);
        Vue.$toast.open({
          message: 'An error was encountered when approving the work order',
          type: 'error',
          duration: 4000,
          position: 'top-right',
        });
      }
    },
    unapproveWorkOrder() {
      let repairsApproved = {
        name: null,
        time: null,
        uid: null,
        value: null,
      };
      try {
        firestore()
          .collection('workOrders')
          .doc(this.documentID)
          .update({
            repairsApproved,
          })
          .then(() => {
            this.selectedWorkOrder.repairsApproved = repairsApproved;
            let workOrdersIndex = this.workOrdersData.findIndex(
              (data) => data.view === this.documentID
            );
            this.workOrdersData.splice(workOrdersIndex, 1, this.selectedWorkOrder);
          });
      } catch (error) {
        console.log('Error unapproving work order', error);
        Vue.$toast.open({
          message: 'An error was encountered when unapproving the work order',
          type: 'error',
          duration: 4000,
          position: 'top-right',
        });
      }
    },
    requestChanges(bvModalEvt) {
      //if there are errors on the form, don't update the work order or close the modal.
      if (this.changeNotesValid) {
        let repairsApproved = {
          name: null,
          time: null,
          uid: null,
          value: false,
        };

        //get the current user from the session storage.
        let user = JSON.parse(sessionStorage.authUser);

        //user and time stamp with the notes.
        let changeNotes = this.selectedWorkOrder.changeNotes;
        changeNotes.push({
          name: user.displayName,
          time: new Date(),
          uid: user.uid,
          notes: this.changeNotes,
          sendEmailNotification: this.sendRequestedChangesNotiEmailOption,
        });

        try {
          firestore()
            .collection('workOrders')
            .doc(this.documentID)
            .update({
              repairsApproved,
              changeNotes,
            })
            .then(() => {
              this.selectedWorkOrder.repairsApproved = repairsApproved;
              let index = this.workOrdersData.findIndex(
                (data) => data.view === this.documentID
              );
              this.workOrdersData.splice(index, 1, this.selectedWorkOrder);
            });
        } catch (error) {
          console.log('Error requesting changes', error);
          Vue.$toast.open({
            message: 'An error was encountered when requesting changes',
            type: 'error',
            duration: 4000,
            position: 'top-right',
          });
        }
      } else {
        bvModalEvt.preventDefault(); //prevent the modal from closing.
      }
    },
    unSubmitWorkOrder() {
      let repairsApproved = {
        name: null,
        time: null,
        uid: null,
        value: false,
      };

      try {
        firestore()
          .collection('workOrders')
          .doc(this.documentID)
          .update({
            repairsApproved,
          })
          .then(() => {
            this.selectedWorkOrder.repairsApproved = repairsApproved;
            let index = this.workOrdersData.findIndex(
              (data) => data.view === this.documentID
            );
            this.workOrdersData.splice(index, 1, this.selectedWorkOrder);
          });
      } catch (error) {
        console.log('Error unsubmitting work order', error);
        Vue.$toast.open({
          message: 'An error was encountered when unsubmitting work order',
          type: 'error',
          duration: 4000,
          position: 'top-right',
        });
      }
    },
    removeExtraFields: function (data) {
      delete data.view;
      data.repairs.forEach((repair) => {
        delete repair.itemNumber;
        delete repair.addRepairmanVisible;
        delete repair.newRepairman;
        delete repair.editEnabled;
        delete repair._showDetails;
        if (repair.workOrderID === null) {
          delete repair.workOrderID;
        }
      });

      // let formattedRepairs = this.formatRepairDatesForFirestore(data.repairs);
      // data.repairs = formattedRepairs;

      return data;
    },
    addExtraFields: function (data) {
      data.repairs.forEach((repair, index) => {
        repair['itemNumber'] = index;
        repair['addRepairmanVisible'] = false;
        repair['newRepairman'] = { name: null, id: null, date: null };
        repair['editEnabled'] = false;
        if (
          this.$store.state.custom.workOrders.repair_hours_enabled &&
          repair['repairHours'] == undefined
        ) {
          repair['repairHours'] = null;
        }
        if (
          this.$store.state.custom.workOrders.repair_cost_enabled &&
          repair['repairCost'] == undefined
        ) {
          repair['repairHours'] = null;
        }
        if (
          this.$store.state.custom.workOrders.labour_hours_enabled &&
          repair['labourHours'] == undefined
        ) {
          repair['labourHours'] = null;
        }
      });
      return data;
    },
    sortRepairsByDate(index) {
      // sort this repair's repairmen by date (shared with the live-stream comparison path)
      this.sortRepairedByByDate(this.selectedWorkOrder.repairs[index].repairedBy);
    },
    // Sort a repair's repairedBy array by date ascending, in place.
    // Used by BOTH the open path (populateWorkOrder -> sortRepairsByDate) and the live-stream
    // comparison (processWorkOrderForComparison -> convertRepairsTimestampsToJSDates). Keeping the
    // order identical stops lodash.isEqual from reporting a false "repairs changed" when only the
    // stored order of the repairmen differs -- the mismatch that caused the stale/refresh modal to
    // re-trigger in an infinite loop.
    sortRepairedByByDate(repairedBy) {
      if (!Array.isArray(repairedBy)) return repairedBy;
      repairedBy.sort((a, b) => {
        if (new Date(a.date) > new Date(b.date)) {
          return 1;
        }
        if (new Date(a.date) < new Date(b.date)) {
          return -1;
        }
        return 0;
      });
      return repairedBy;
    },
    populateImage(document) {
      this.selectedImage = lodash.cloneDeep(document);
      this.$bvModal.show('img-modal');
    },
    populateEditWorkOrder() {
      this.selectedWorkOrderEdit.date = new Date(this.selectedWorkOrder.date);
      this.selectedWorkOrderEdit.units = this.selectedWorkOrder.units
      this.selectedWorkOrderEdit.odometer = this.selectedWorkOrder.odometer;
      this.selectedWorkOrderEdit.hourMeter = this.selectedWorkOrder.hourMeter;
      this.selectedWorkOrderEdit.preTripDeficiency = this.selectedWorkOrder.preTripDeficiency;
      this.selectedWorkOrderEdit.generalNotes = this.selectedWorkOrder.generalNotes ? this.selectedWorkOrder.generalNotes : null;
      this.selectedWorkOrderEdit.lastModified = this.selectedWorkOrder.lastModified || null;
    },
    async populateWorkOrder(data) {
      // ignore clicks while another work order is already opening - prevents the
      // write target (documentID) and the displayed WO (selectedWorkOrder) from
      // being set by two different overlapping opens
      if (this.isOpeningWorkOrder) return;
      this.isOpeningWorkOrder = true;
      try {
        this.isLoadingWorkOrders.push(data.view);

      this.documentID = data.view;
      this.isWoStale = false;
      this.staleUpdatedByName = null;
      this.inactivityClosedWorkOrder = null;

        // Check current editing status from database first
        const canOpen = await this.checkCurrentEditingStatus(data.view);

        if (!canOpen) {
          // User canceled, don't open the work order
          const cancelIndex = this.isLoadingWorkOrders.indexOf(data.view);
          if (cancelIndex > -1) this.isLoadingWorkOrders.splice(cancelIndex, 1);
          this.documentID = null; // never leave the declined WO as a write target
          return;
        }

        // open the modal with a spinner, then load a fresh copy of this WO
        this.selectedWorkOrderNoteEditMode = false;
        this.workOrderModalLoading = true;
        this.allowWorkOrderModalClose = false;
        this.$bvModal.show('modal_viewWorkOrder');
        const loadingIndex = this.isLoadingWorkOrders.indexOf(data.view);
        if (loadingIndex > -1) this.isLoadingWorkOrders.splice(loadingIndex, 1);

        // fetch the fresh WO
        let freshWorkOrder = null;
        try {
          const snap = await getWorkOrderById(data.view);
          if (snap && snap.exists) {
            freshWorkOrder = hydrateWorkOrderDoc(
              snap.data(),
              snap.id,
              this.units,
              this.$store.state.custom.workOrders.repair_hours_enabled,
              this.$store.state.custom.workOrders.repair_cost_enabled,
              this.$store.state.custom.workOrders.labour_hours_enabled
            );
          }
        } catch (error) {
          console.log('Error fetching fresh work order', error);
        }

        let index = this.workOrdersData.findIndex((x) => x.view === data.view);
        if (freshWorkOrder) {
          // refresh the cache entry + freshness baseline
          if (index !== -1) {
            this.workOrdersData.splice(index, 1, lodash.cloneDeep(freshWorkOrder));
          } else {
            this.workOrdersData.push(lodash.cloneDeep(freshWorkOrder));
          }
          this.woLastPull[data.view] = Date.now();
        } else {
          // fallback: keep the board cache
          this.woLastPull[data.view] = this.boardDataLastPull;
        }

        index = this.workOrdersData.findIndex((x) => x.view === data.view);
        let workOrderCopy = lodash.cloneDeep(this.workOrdersData[index]);
        this.selectedWorkOrder = workOrderCopy;

        this.selectedWorkOrder.repairs.forEach((repair, i) => {
          repair.editEnabled = false;
          this.sortRepairsByDate(i);
        });

        this.selectedWorkOrderInitial = JSON.parse(
          JSON.stringify(this.workOrdersData[index])
        );

        this.workOrderModalLoading = false;

        // Set up Firebase stream for repairs field changes
        this.setupWorkOrderStream(data.view);

        // start 60 second interval to keep updating timestamp of currentlyEditedBy object
        this.startHeartbeat()

        // close + autosave the work order once the user has gone idle, releasing the editing lock
        this.startInactivityWatch()
      } finally {
        // always release the gate, even if the open throws, so the board never locks up
        this.isOpeningWorkOrder = false;
      }
    },
    // returns true if safe to write; otherwise shows the staleness modal and returns false
    async ensureWorkOrderFresh(workOrderId, lastPull) {
      try {
        const uid =
          this.$store.state.custom.currentUserData &&
          this.$store.state.custom.currentUserData.uid;
        const { fresh, lastUpdatedBy } = await checkWoFreshness(
          workOrderId,
          lastPull == null ? 0 : lastPull,
          uid
        );
        if (!fresh) {
          this.staleUpdatedByName =
            (lastUpdatedBy && lastUpdatedBy.name) || 'another user';
          // text-only here; the stream path adds the diff
          this.repairsChangesSummary = '';
          this.isWoStale = true;
          this.$bvModal.show('modal_workOrderStale');
          return false;
        }
        return true;
      } catch (error) {
        console.log('Error checking work order freshness', error);
        return true; // on error, don't block the user
      }
    },
    // Refresh Work Order button: drop the edit session and re-pull the board, re-opening this WO
    async handleRefreshWorkOrder() {
      // capture the WO to re-open before tearing down modal state
      const woNumber = this.selectedWorkOrder && this.selectedWorkOrder.number;
      const woDate = this.selectedWorkOrder && this.selectedWorkOrder.date;

      this.isWoStale = false;
      this.staleUpdatedByName = null;
      this.clearUserAsEditing();
      this.stopHeartbeat();
      if (this.workOrderStream) {
        this.workOrderStream();
        this.workOrderStream = null;
      }
      this.$bvModal.hide('modal_workOrderStale');
      this.allowWorkOrderModalClose = true;
      this.$bvModal.hide('modal_viewWorkOrder');
      this.$bvModal.hide('modal_addIssueToExistingWorkOrder');

      // set route params so the existing URL-params handling re-opens this WO after the re-query
      if (woNumber) {
        try {
          await this.$router.push({
            name: 'workOrders',
            params: {
              wonumber: woNumber,
              wodate: woDate ? new Date(woDate).toISOString() : undefined,
            },
          });
        } catch (e) {
          // ignore NavigationDuplicated
        }
      }

      this.$emit('refresh-work-orders');
    },
    setupWorkOrderStream(workOrderId) {
      // Clean up any existing stream
      if (this.workOrderStream) {
        this.workOrderStream();
        this.workOrderStream = null;
      }

      // re-baseline on every (re)subscribe; the first snapshot records the new baseline
      this.woStreamBaselineMs = null;

      // Set up new stream for the work order
      this.workOrderStream = firestore()
        .collection('workOrders')
        .doc(workOrderId)
        .onSnapshot((doc) => {
          if (doc.exists) {
            const updatedWorkOrder = doc.data();

            // Monitor currentlyEditedBy changes for auto-upgrade functionality
            this.handleEditingStatusChange(updatedWorkOrder.currentlyEditedBy);

            // Compare repairs field with current selectedWorkOrder (ignoring currentlyEditedBy changes)
            if (updatedWorkOrder.repairs && this.selectedWorkOrder.repairs) {
              // Only run the repairs conflict check when a stamped repairs write (withLastUpdated)
              // actually landed after we subscribed. Heartbeat / lock / unsaved-flag writes never
              // touch lastUpdated, so they can no longer false-flag the modal while a mechanic types.
              const ts = updatedWorkOrder.lastUpdated;
              const docLastUpdatedMs = ts && typeof ts.toMillis === 'function' ? ts.toMillis() : null;

              if (this.woStreamBaselineMs === null) {
                // first snapshot after subscribing: populateWorkOrder just loaded this exact state
                this.woStreamBaselineMs = docLastUpdatedMs == null ? 0 : docLastUpdatedMs;
                return;
              }

              if (
                doc.metadata.hasPendingWrites ||             // local echo of our own write
                docLastUpdatedMs == null ||                  // own serverTimestamp still resolving / never-stamped doc
                docLastUpdatedMs <= this.woStreamBaselineMs  // no repairs save since we loaded
              ) {
                return;
              }

              // a stamped repairs write landed since we subscribed - advance the baseline, then diff
              this.woStreamBaselineMs = docLastUpdatedMs;

              // Apply all the same transformations as getInitialWorkOrders
              const processedUpdatedWorkOrder = this.processWorkOrderForComparison(updatedWorkOrder);

              // Check if repairs field has changed using lodash deep comparison
              if (!lodash.isEqual(this.selectedWorkOrder.repairs, processedUpdatedWorkOrder.repairs)) {

                // Only show notification if someone else made the changes
                if (this.isWorkOrderBeingEdited) {
                  if (this.isRepairsModalOpen) {
                    // Modal is already open - accumulate changes
                    const changesBatch = {
                      timestamp: new Date(),
                      changes: this.generateRepairsChangesSummary(this.selectedWorkOrder.repairs, processedUpdatedWorkOrder.repairs),
                      fromState: lodash.cloneDeep(this.selectedWorkOrder.repairs),
                      toState: lodash.cloneDeep(processedUpdatedWorkOrder.repairs)
                    };
                    this.accumulatedChanges.push(changesBatch);

                    // Update modal content with all accumulated changes
                    this.repairsChangesSummary = this.generateCumulativeChangesSummary();
                  } else {
                    // Modal is not open - show fresh modal
                    this.accumulatedChanges = [{
                      timestamp: new Date(),
                      changes: this.generateRepairsChangesSummary(this.selectedWorkOrder.repairs, processedUpdatedWorkOrder.repairs),
                      fromState: lodash.cloneDeep(this.selectedWorkOrder.repairs),
                      toState: lodash.cloneDeep(processedUpdatedWorkOrder.repairs)
                    }];
                    this.repairsChangesSummary = this.generateCumulativeChangesSummary();
                    this.$bvModal.show('modal_repairsChanges');
                  }

                  // Update the local selectedWorkOrder with the changes so they show in the UI
                  this.selectedWorkOrder.repairs = processedUpdatedWorkOrder.repairs;

                  // Also update the workOrdersData array to keep everything in sync
                  const workOrderIndex = this.workOrdersData.findIndex(wo => wo.view === this.documentID);
                  if (workOrderIndex !== -1) {
                    this.workOrdersData[workOrderIndex].repairs = processedUpdatedWorkOrder.repairs;
                  }

                  // re-sync the edit buffers to the fresh values + advance the baseline,
                  // so a stale selectedRepair clone can't overwrite the other user's change
                  this.selectedWorkOrder.repairs.forEach((r) => { r.editEnabled = false; });
                  if (this.selectedRepair && this.selectedRepair.id) {
                    const freshMatch = this.selectedWorkOrder.repairs.find((r) => r.id === this.selectedRepair.id);
                    if (freshMatch) {
                      this.selectedRepair = lodash.cloneDeep(freshMatch);
                      this.selectedRepairInitial = lodash.cloneDeep(freshMatch);
                    } else {
                      // the selected repair was removed by the other user
                      this.selectedRepair = { issue: null, repair: null, sourceOfParts: null, submitted: { name: null, time: null, uid: null, value: null } };
                      this.selectedRepairInitial = lodash.cloneDeep(this.selectedRepair);
                    }
                  }
                  if (this.documentID) {
                    this.woLastPull[this.documentID] = Date.now();
                  }
                } else {
                  // we hold the lock; the stream also fires for our own writes, so only act on someone else's
                  const myUid = this.$store.state.custom.currentUserData && this.$store.state.custom.currentUserData.uid;
                  if (updatedWorkOrder.lastUpdatedBy && updatedWorkOrder.lastUpdatedBy.uid && updatedWorkOrder.lastUpdatedBy.uid !== myUid) {
                    // warn (don't overwrite in-progress edits); they drop only on Refresh
                    this.staleUpdatedByName = updatedWorkOrder.lastUpdatedBy.name || 'another user';
                    this.repairsChangesSummary = this.generateRepairsChangesSummary(this.selectedWorkOrder.repairs, processedUpdatedWorkOrder.repairs);
                    this.isWoStale = true;
                    this.$bvModal.show('modal_workOrderStale');
                  }
                }
              }
            }
          }
        }, (error) => {
          console.error('Error listening to work order changes:', error);
        });
    },
    generateRepairsChangesSummary(currentRepairs, updatedRepairs) {
      let summary = '<div><p><strong>The repairs for this work order have been updated by another user:</strong></p>';
      // Check for added repairs
      const addedRepairs = updatedRepairs.filter(updated =>
        !currentRepairs.some(current => current.id === updated.id)
      );

      // Check for removed repairs
      const removedRepairs = currentRepairs.filter(current =>
        !updatedRepairs.some(updated => updated.id === current.id)
      );

      // Check for modified repairs
      const modifiedRepairs = updatedRepairs.filter(updated => {
        const current = currentRepairs.find(current => current.id === updated.id);
        return current && !lodash.isEqual(current, updated);
      });

      if (addedRepairs.length > 0) {
        summary += '<div class="mt-3"><strong class="text-success">Added Repairs:</strong><ul>';
        addedRepairs.forEach(repair => {
          const issueText = repair.issue ? repair.issue.substring(0, 60) + (repair.issue.length > 60 ? '...' : '') : 'No issue description';
          summary += `<li class="text-success">${issueText}</li>`;
        });
        summary += '</ul></div>';
      }

      if (removedRepairs.length > 0) {
        summary += '<div class="mt-3"><strong class="text-danger">Removed Repairs:</strong><ul>';
        removedRepairs.forEach(repair => {
          const issueText = repair.issue ? repair.issue.substring(0, 60) + (repair.issue.length > 60 ? '...' : '') : 'No issue description';
          summary += `<li class="text-danger">${issueText}</li>`;
        });
        summary += '</ul></div>';
      }

      if (modifiedRepairs.length > 0) {
        summary += '<div class="mt-3"><strong class="text-warning">Modified Repairs:</strong>';
        modifiedRepairs.forEach(repair => {
          const current = currentRepairs.find(current => current.id === repair.id);
          const changes = this.getDetailedRepairChanges(current, repair);
          const issueText = repair.issue ? repair.issue.substring(0, 60) + (repair.issue.length > 60 ? '...' : '') : 'No issue description';
          summary += `<div class="mt-2 p-2 border rounded">
            <strong>${issueText}</strong>
            <ul class="mt-1 mb-0">`;
          changes.forEach(change => {
            summary += `<li><strong>${change.field}:</strong>
              <span class="text-danger">${change.oldValue}</span> →
              <span class="text-success">${change.newValue}</span>
            </li>`;
          });
          summary += '</ul></div>';
        });
        summary += '</div>';
      }

      if (addedRepairs.length === 0 && removedRepairs.length === 0 && modifiedRepairs.length === 0) {
        summary += '<p>Changes detected in repair details or order.</p>';
      }

      summary += '</div>';
      return summary;
    },
    generateCumulativeChangesSummary() {
      let summary = '<div><p><strong>Work Order Changes</strong></p>';

      if (this.accumulatedChanges.length === 0) {
        summary += '<p>No changes detected.</p>';
      } else if (this.accumulatedChanges.length === 1) {
        // Single change - show the regular summary
        summary += this.accumulatedChanges[0].changes;
      } else {
        // Multiple changes - show chronological list
        summary += `<p><em>Multiple changes have been made while you were viewing this work order:</em></p>`;

        this.accumulatedChanges.forEach((changeBatch, index) => {
          const timeStr = changeBatch.timestamp.toLocaleTimeString();
          summary += `<div class="mt-3 p-2 border-left border-primary" style="border-left: 3px solid #007bff !important;">
            <h6 class="text-primary">Change #${index + 1} - ${timeStr}</h6>
            ${changeBatch.changes}
          </div>`;
        });

        summary += `<div class="mt-3 alert alert-info">
          <small><i class="mdi mdi-information-outline"></i>
          Total of ${this.accumulatedChanges.length} change batches detected.
          All changes have been applied to your view.</small>
        </div>`;
      }

      summary += '</div>';
      return summary;
    },
    async handleEditingStatusChange(currentlyEditedByFromStream) {
      const isNowFree = !currentlyEditedByFromStream;
      const currentUserId = this.$store.state.auth.currentUser?.uid;

      // Check if current user was editing and someone else took over
      if (currentlyEditedByFromStream &&
          this.workOrderEditingStatus?.editedBy?.uid === currentUserId &&
          currentlyEditedByFromStream.uid !== currentUserId) {

        // Update local editing status to reflect the new editor
        this.workOrderEditingStatus = {
          isBeingEdited: true,
          editedBy: {
            uid: currentlyEditedByFromStream.uid,
            name: currentlyEditedByFromStream.name,
            email: currentlyEditedByFromStream.email,
            timestamp: currentlyEditedByFromStream.timestamp
          }
        };

        // Set view-only mode flags
        this.wasInReadOnlyMode = true;
        this.autoUpgradeAvailable = true;

        // Show notification about takeover
        this.$bvToast.toast(`${currentlyEditedByFromStream.name} has taken over editing of this work order. You are now in view-only mode.`, {
          title: 'Editing Taken Over',
          variant: 'warning',
          solid: true,
          autoHideDelay: 7000,
          toaster: 'b-toaster-top-right'
        });

        return;
      }

      // Check if user was in read-only mode and editing user has left (existing auto-upgrade logic)
      if (this.wasInReadOnlyMode && this.autoUpgradeAvailable) {
        if (this.isWorkOrderBeingEdited && isNowFree) {
          this.wasInReadOnlyMode = false;
          this.autoUpgradeAvailable = false;

          // Also update Firebase to claim editing status
          await this.setUserAsEditing(this.documentID);
          this.startHeartbeat();
          this.startInactivityWatch();

          // Show notification about auto-upgrade
          this.$bvToast.toast('The work order is now available for editing. You have been automatically upgraded to edit mode.', {
            title: 'Auto-Upgraded to Edit Mode',
            variant: 'success',
            solid: true,
            autoHideDelay: 5000,
            toaster: 'b-toaster-top-right'
          });

          console.log('User auto-upgraded to edit mode - editing user left');
        }
      }
    },
    getDetailedRepairChanges(currentRepair, updatedRepair) {
      const changes = [];
      const fieldsToCheck = [
        'issue', 'repair', 'sourceOfParts', 'repairHours', 'repairCost', 'labourHours',
        'completed', 'documentStatus'
      ];

      fieldsToCheck.forEach(field => {
        const currentValue = currentRepair[field];
        const updatedValue = updatedRepair[field];

        if (!lodash.isEqual(currentValue, updatedValue)) {
          changes.push({
            field: this.getFieldDisplayName(field),
            oldValue: this.formatFieldValue(currentValue, field),
            newValue: this.formatFieldValue(updatedValue, field)
          });
        }
      });

      // Check repairedBy array changes
      const currentRepairedBy = currentRepair.repairedBy || [];
      const updatedRepairedBy = updatedRepair.repairedBy || [];
      if (!lodash.isEqual(currentRepairedBy, updatedRepairedBy)) {
        changes.push({
          field: 'Repaired By',
          oldValue: currentRepairedBy.map(r => r.name).join(', ') || 'None',
          newValue: updatedRepairedBy.map(r => r.name).join(', ') || 'None'
        });
      }

      // Check completedBy changes
      const currentCompletedBy = currentRepair.completedBy;
      const updatedCompletedBy = updatedRepair.completedBy;
      if (!lodash.isEqual(currentCompletedBy, updatedCompletedBy)) {
        changes.push({
          field: 'Completed By',
          oldValue: currentCompletedBy?.name || 'None',
          newValue: updatedCompletedBy?.name || 'None'
        });
      }

      // Check document changes
      const currentDocuments = currentRepair.documents || [];
      const updatedDocuments = updatedRepair.documents || [];
      if (!lodash.isEqual(currentDocuments, updatedDocuments)) {
        // Find added documents
        const addedDocuments = updatedDocuments.filter(updatedDoc =>
          !currentDocuments.some(currentDoc =>
            currentDoc.url === updatedDoc.url ||
            (currentDoc.file?.name === updatedDoc.file?.name && currentDoc.uploadedBy?.id === updatedDoc.uploadedBy?.id)
          )
        );

        // Find removed documents
        const removedDocuments = currentDocuments.filter(currentDoc =>
          !updatedDocuments.some(updatedDoc =>
            updatedDoc.url === currentDoc.url ||
            (updatedDoc.file?.name === currentDoc.file?.name && updatedDoc.uploadedBy?.id === currentDoc.uploadedBy?.id)
          )
        );

        // Generate document changes description
        let documentsChangeDesc = '';
        if (addedDocuments.length > 0) {
          documentsChangeDesc += addedDocuments.map(doc => `• Added: ${this.formatDocumentInfo(doc)}`).join('<br>');
        }
        if (removedDocuments.length > 0) {
          if (documentsChangeDesc) documentsChangeDesc += '<br>';
          documentsChangeDesc += removedDocuments.map(doc => `• Removed: ${this.formatDocumentInfo(doc)}`).join('<br>');
        }

        if (documentsChangeDesc) {
          changes.push({
            field: 'Documents',
            oldValue: currentDocuments.length === 0 ? 'None' : `${currentDocuments.length} document(s)`,
            newValue: documentsChangeDesc
          });
        }
      }

      return changes;
    },
    getFieldDisplayName(field) {
      const fieldNames = {
        'issue': 'Issue',
        'repair': 'Repair',
        'sourceOfParts': 'Source of Parts',
        'repairHours': 'Repair Hours',
        'repairCost': 'Repair Cost',
        'labourHours': 'Labour Hours',
        'completed': 'Completed Status',
        'documentStatus': 'Document Status'
      };
      return fieldNames[field] || field;
    },
    formatFieldValue(value, field) {

      if(field === 'documentStatus') {
        return value === false ? 'No Documents Required' : value === true ? 'All Documents Received' : 'Waiting for Documents';
      }
      if (value === null || value === undefined) {
        return 'None';
      }
      if (typeof value === 'boolean'){
        return value ? 'Yes' : 'No';
      }
      if (typeof value === 'string' && value.length > 50) {
        return value.substring(0, 50) + '...';
      }
      return String(value);
    },
    formatDocumentInfo(document) {
      if (!document) return 'Unknown document';

      const fileName = document.file?.name || 'Unknown file';
      const uploadedBy = document.uploadedBy?.name || 'Unknown user';
      const uploadDate = document.date;

      let displayName = fileName;
      if (displayName.length > 30) {
        displayName = displayName.substring(0, 27) + '...';
      }

      let timeStr = '';
      if (uploadDate) {
        try {
          const date = uploadDate instanceof Date ? uploadDate : new Date(uploadDate);
          timeStr = ` at ${date.toLocaleTimeString()}`;
        } catch (e) {
          timeStr = '';
        }
      }

      return `${displayName} (uploaded by ${uploadedBy}${timeStr})`;
    },
    convertRepairsTimestampsToJSDates(repairs) {
      // Create a deep copy to avoid modifying the original - use lodash to preserve Firebase Timestamp objects
      const convertedRepairs = lodash.cloneDeep(repairs);

      // Apply all the same transformations as in getInitialWorkOrders
      convertedRepairs.forEach((repair, index) => {
        // Convert timestamps to JS dates
        if (repair.date && repair.date instanceof firestore.Timestamp) {
          repair.date = repair.date.toDate();
        }
        if (repair.createdDate && repair.createdDate instanceof firestore.Timestamp) {
          repair.createdDate = repair.createdDate.toDate();
        }
        if (repair.submitted && repair.submitted.time && repair.submitted.time instanceof firestore.Timestamp) {
          repair.submitted.time = repair.submitted.time.toDate();
        }
        if (repair.documents && repair.documents.length > 0) {
          repair.documents.forEach((doc) => {
            if (doc.date && doc.date instanceof firestore.Timestamp) {
              doc.date = doc.date.toDate();
            }
          });
        }

        // Convert timestamps for completedBy and repairedBy BEFORE field normalization (like in original code)
        if (repair.completedBy && repair.completedBy.date && repair.completedBy.date instanceof firestore.Timestamp) {
          repair.completedBy.date = repair.completedBy.date.toDate();
        }
        if (repair.repairedBy && repair.repairedBy.length > 0) {
          repair.repairedBy.forEach((repBy) => {
            if (repBy && repBy.date && repBy.date instanceof firestore.Timestamp) {
              repBy.date = repBy.date.toDate();
            }
          });
        }

        // Apply the same field additions as in getInitialWorkOrders
        repair['itemNumber'] = index;
        repair['addRepairmanVisible'] = false;
        repair['editEnabled'] = false;

        // Add completedBy to each repair if it is not present
        if (!repair.completedBy) {
          repair['completedBy'] = { date: null, id: null, name: null };
        }

        // Turn repairedBy into an array if it is not already
        if (!Array.isArray(repair.repairedBy)) {
          repair.repairedBy = [repair.repairedBy];
        }

        // Match the open path (populateWorkOrder -> sortRepairsByDate): keep repairedBy sorted by
        // date so the deep-compare in the stream doesn't flag an unchanged repair as "updated"
        // purely because Firestore stored the repairmen in a different order (root cause of the
        // stale-modal refresh loop).
        this.sortRepairedByByDate(repair.repairedBy);

        // Add the newRepairman field
        repair['newRepairman'] = { name: null, id: null, date: null };

        // Add additional fields if enabled (repair hours and cost)
        if (this.$store.state.custom.workOrders.repair_hours_enabled && repair['repairHours'] == undefined) {
          repair.repairHours = null;
        }
        if (this.$store.state.custom.workOrders.repair_cost_enabled && repair['repairCost'] == undefined) {
          repair.repairCost = null;
        }
        if (this.$store.state.custom.workOrders.labour_hours_enabled && repair['labourHours'] == undefined) {
          repair.labourHours = null;
        }
      });

      return convertedRepairs;
    },
    processWorkOrderForComparison(workOrder) {
      // Create a deep copy to avoid modifying the original - use lodash to preserve Firebase Timestamp objects
      const processedWorkOrder = lodash.cloneDeep(workOrder);

      // Apply all the same transformations as in getInitialWorkOrders
      processedWorkOrder.isVisible = true;

      // Add changeNotes field if it doesn't exist
      if (processedWorkOrder.changeNotes === undefined) {
        processedWorkOrder.changeNotes = [];
      }

      // Set datesFormatted flag
      if (processedWorkOrder.datesFormatted) {
        processedWorkOrder.datesFormatted = false;
      }

      // Add resubmitNotes field if it doesn't exist
      if (processedWorkOrder.resubmitNotes === undefined) {
        processedWorkOrder.resubmitNotes = [];
      }

      // Process repairs with all transformations
      if (processedWorkOrder.repairs) {
        processedWorkOrder.repairs = this.convertRepairsTimestampsToJSDates(processedWorkOrder.repairs);
      }

      // Add documentStatus field based on repairs
      if (processedWorkOrder.repairs.length > 0) {
        if (processedWorkOrder.repairs.find((x) => x.documentStatus === null)) {
          processedWorkOrder['documentStatus'] = null;
        } else if (processedWorkOrder.repairs.find((x) => x.documentStatus === true) && !processedWorkOrder.repairs.find((x) => x.documentStatus === null)) {
          processedWorkOrder['documentStatus'] = true;
        } else {
          processedWorkOrder['documentStatus'] = false;
        }
      }

      return processedWorkOrder;
    },
    handleRepairsModalShow() {
      // Modal is opening - set flag and capture baseline
      this.isRepairsModalOpen = true;
      this.baselineRepairs = lodash.cloneDeep(this.selectedWorkOrder.repairs);
      // Don't clear accumulated changes here - they should already be set when modal opens
    },
    handleRepairsModalHide() {
      // Modal is closing - reset state
      this.isRepairsModalOpen = false;
      this.accumulatedChanges = [];
      this.baselineRepairs = null;
    },
    clearRepairs: function () {
      // Clear editing status when modal closes
      this.clearUserAsEditing();

      this.stopHeartbeat();
      this.stopInactivityWatch();

      // the modal is fully hidden by the time this runs, so the notice lands on the board
      if (this.inactivityClosedWorkOrder) {
        this.showInactivityClosedNotice();
      }

      // reset loading flag
      this.workOrderModalLoading = false;

      // Clean up Firebase stream when modal closes
      if (this.workOrderStream) {
        this.workOrderStream();
        this.workOrderStream = null;
      }

      // resort the work orders into their appropriate columns
      this.filterAllColumns();
      this.filterWorkOrdersNotRendered();
      this.loadEmptyWorkOrders();

      this.selectedWorkOrder = {
        date: null,
        unit: [],
        preTripDeficiency: false,
        odometer: null,
        hourMeter: null,
        accepted: { time: null },
        repairs: [],
        repairedBy: [],
        repairsComplete: { time: null },
        repairsApproved: { time: null },
        generalNotes: null,
      };

      this.selectedWorkOrderInitial = {
        unit: null,
        preTripDeficiency: false,
        odometer: null,
        hourMeter: null,
        accepted: null,
        repairs: [],
        repairsComplete: null,
        repairsApproved: null,
      };

      this.selectedRepair = { issue: null, repair: null, sourceOfParts: null, submitted: {name: null, time: null, uid: null, value: null} };
      this.selectedRepairInitial = {
        issue: null,
        repair: null,
        sourceOfParts: null,
      };
      this.repairIndex = null;

      this.showRepairs = false;

      this.newWorkOrderRepairs = [];
      this.newIssue = null;
      this.documentID = null;

      this.odometerValid = false;
      this.hourMeterValid = false;
      this.unitValid = false;
      this.issueValid = false;
      this.editIssueValid = false;
      this.editRepairValid = false;

      this.submitErrorOdometerEdit = null;
      this.submitErrorHourMeterEdit = null;
      this.submitErrorOdometer = null;
      this.submitErrorHourMeter = null;
      this.submitErrorDate = null;
      this.submitErrorUnit = null;
      this.submitErrorIssue = null;
      this.submitErrorAddIssue = null;
      this.submitErrorEditIssue = null;
      this.submitErrorEditRepair = null;
      this.submitErrorEditRepairCost = null;

      this.dateErrorMessage = null;
      this.unitErrorMessage = null;
      this.odometerErrorMessage = null;
      this.hourMeterErrorMessage = null;
      this.issueErrorMessage = null;
      this.editIssueErrorMessage = null;
      this.editRepairErrorMessage = null;
    },
    // resolves which repairs slot selectedRepair belongs to; -1 when it can't be determined
    resolveRepairIndex: function (repairs) {
      const repairId = this.selectedRepair && this.selectedRepair.id && this.selectedRepair.id.trim() !== ''
        ? this.selectedRepair.id
        : null;
      let idx = this.repairIndex;
      if (repairId !== null && !(repairs[idx] && repairs[idx].id === repairId)) {
        idx = repairs.findIndex((r) => r.id === repairId);
      }
      if (typeof idx !== 'number' || idx < 0 || idx >= repairs.length) {
        return -1;
      }
      return idx;
    },
    populateRepair: function (data, index) {
      // must be set even when the early return below skips the re-clone
      this.repairIndex = index;
        //check if the selected repair is already being edited
      if (this.selectedRepair && this.selectedRepair.id && this.selectedRepair.id === data.id) {
        return;
      }
      this.selectedRepair = lodash.cloneDeep(data);
      this.selectedRepairInitial = lodash.cloneDeep(data);
    },
    validateEntry(entry, submitted) {
      //validates entry differently based upon whether submitted is set to true or not
      if (entry === 'date') {
        if (!this.selectedWorkOrder.date) {
          this.dateErrorMessage = 'Please select a date';
          return submitted ? false : null;
        }
        return true;
      }
      if (entry === 'unit') {
        if (!this.selectedWorkOrder.units || this.selectedWorkOrder.units.length === 0) {
          this.unitValid = false;
          this.unitErrorMessage = 'Please select a unit';
          return submitted ? false : null;
        }
        this.unitValid = true;
        return true;
      }
      if (entry === 'odometer') {
        if (
          this.selectedWorkOrder.odometer === null ||
          this.selectedWorkOrder.odometer.trim() === ''
        ) {
          if (submitted) {
            this.odometerValid = false;
            this.odometerErrorMessage = 'Please enter a number';
            return false;
          } else {
            this.odometerValid = false;
            this.odometerErrorMessage = 'Please enter a number';
            return null;
          }
        } else {
          this.odometerValid = true;
          return true;
        }
      }
      if(entry === 'odometer-issue'){
        if (
          this.selectedWorkOrder.odometer === null ||
          this.selectedWorkOrder.odometer.trim() === ''
        ) {
          if (submitted) {
            this.odometerIssueValid = false;
            this.odometerIssueErrorMessage = 'Please enter a number';
            return false;
          } else {
            this.odometerIssueValid = false;
            this.odometerIssueErrorMessage = 'Please enter a number';
            return null;
          }
        } else {
          this.odometerIssueValid = true;
          return true;
        }
      }
      if (entry === 'hourMeter') {
        if (
          this.selectedWorkOrder.hourMeter === null ||
          this.selectedWorkOrder.hourMeter.trim() === ''
        ) {
          if (submitted) {
            this.hourMeterValid = false;
            this.hourMeterErrorMessage = 'Please enter a number';
            return false;
          } else {
            this.hourMeterValid = false;
            this.hourMeterErrorMessage = 'Please enter a number';
            return null;
          }
        } else {
          this.hourMeterValid = true;
          return true;
        }
      }
      if(entry === 'hourMeter-issue'){
        if (
          this.selectedWorkOrder.hourMeter === null ||
          this.selectedWorkOrder.hourMeter.trim() === ''
        ) {
          if (submitted) {
            this.hourMeterIssueValid = false;
            this.hourMeterIssueErrorMessage = 'Please enter a number';
            return false;
          } else {
            this.hourMeterIssueValid = false;
            this.hourMeterIssueErrorMessage = 'Please enter a number';
            return null;
          }
        } else {
          this.hourMeterIssueValid = true;
          return true;
        }
      }
      if (entry === 'issue') {
        if (this.newWorkOrderRepairs.length < 1) {
          if (submitted) {
            if (
              this.selectedWorkOrder.repairs !== null &&
              this.selectedWorkOrder.repairs !== undefined
            ) {
              if (this.selectedWorkOrder.repairs.length > 1) {
                this.issueValid = true;
                return null;
              } else {
                this.issueValid = false;
                this.issueErrorMessage = 'Please input at least one issue';
                return false;
              }
            } else {
              this.issueValid = false;
              this.issueErrorMessage = 'Please input at least one issue';
              return false;
            }
          } else {
            this.issueValid = false;
            this.issueErrorMessage = 'Please input at least one issue';
            return null;
          }
        } else {
          this.issueValid = true;
          return null;
        }
      }
      if (entry === 'editIssue') {
        if (
          this.selectedRepair.issue === null ||
          this.selectedRepair.issue.trim() === ''
        ) {
          if (submitted) {
            this.editIssueValid = false;
            this.editIssueErrorMessage = 'Please enter an issue';
            return false;
          } else {
            this.editIssueValid = false;
            this.editIssueErrorMessage = 'Please enter an issue';
            return null;
          }
        } else {
          this.editIssueValid = true;
          return true;
        }
      }
      if (entry === 'editNewIssue') {
        if (
          this.repairToEdit.issue === null ||
          this.repairToEdit.issue.trim() === ''
        ) {
          if (submitted) {
            this.editIssueValid = false;
            this.editIssueErrorMessage = 'Please enter an issue';
            return false;
          } else {
            this.editIssueValid = false;
            this.editIssueErrorMessage = 'Please enter an issue';
            return false;
          }
        } else {
          this.editIssueValid = true;
          return true;
        }
      }
      if (entry === 'editRepair') {
        if (
          this.selectedRepair.repair === null ||
          this.selectedRepair.repair.trim() === ''
        ) {
          if (submitted) {
            this.editRepairValid = false;
            this.editRepairErrorMessage = 'Please enter a repair';
            return false;
          } else {
            this.editRepairValid = false;
            this.editRepairErrorMessage = 'Please enter a repair';
            return null;
          }
        } else {
          this.editRepairValid = true;
          return true;
        }
      }
      if (entry === 'odometer-edit') {
        if (
          this.selectedWorkOrderEdit.odometer === null ||
          this.selectedWorkOrderEdit.odometer.trim() === ''
        ) {
          if (submitted) {
            this.odometerEditValid = false;
            this.odometerEditErrorMessage = 'Please enter a number';
            return false;
          } else {
            this.odometerEditValid = false;
            this.odometerEditErrorMessage = 'Please enter a number';
            return null;
          }
        } else {
          this.odometerEditValid = true;
          return true;
        }
      }
      if(entry === 'odometer-issue-edit'){
        if (
          this.repairToEdit.odometer === null ||
          this.repairToEdit.odometer.trim() === ''
        ) {
          if (submitted) {
            this.odometerIssueEditValid = false;
            this.odometerIssueEditErrorMessage = 'Please enter a number';
            return false;
          } else {
            this.odometerIssueEditValid = false;
            this.odometerIssueEditErrorMessage = 'Please enter a number';
            return null;
          }
        } else {
          this.odometerIssueEditValid = true;
          return true;
        }
      }
      if (entry === 'hourMeter-edit') {
        if (
          this.selectedWorkOrderEdit.hourMeter === null ||
          this.selectedWorkOrderEdit.hourMeter.trim() === ''
        ) {
          if (submitted) {
            this.hourMeterEditValid = false;
            this.hourMeterEditErrorMessage = 'Please enter a number';
            return false;
          } else {
            this.hourMeterEditValid = false;
            this.hourMeterEditErrorMessage = 'Please enter a number';
            return null;
          }
        } else {
          this.hourMeterEditValid = true;
          return true;
        }
      }
      if(entry === 'hourMeter-issue-edit'){
        if (
          this.repairToEdit.hourMeter === null ||
          this.repairToEdit.hourMeter.trim() === ''
        ) {
          if (submitted) {
            this.hourMeterIssueEditValid = false;
            this.hourMeterIssueEditErrorMessage = 'Please enter a number';
            return false;
          } else {
            this.hourMeterIssueEditValid = false;
            this.hourMeterIssueEditErrorMessage = 'Please enter a number';
            return null;
          }
        } else {
          this.hourMeterEditValid = true;
          return true;
        }
      }
      if (entry === 'unit-edit') {
        if (this.selectedWorkOrderEdit.units.length <= 0) {
          if (submitted) {
            this.unitEditValid = false;
            this.unitEditErrorMessage = 'Please select a unit';
            return false;
          } else {
            this.unitEditValid = false;
            this.unitEditErrorMessage = 'Please select a unit';
            return null;
          }
        } else {
          this.unitEditValid = true;
          return true;
        }
      }
      if (entry === 'repairCost') {
        //if the value isn't a number, then don't update the repair.
        if (isNaN(Number(this.selectedRepair.repairCost))) {
          this.editRepairCostValid = false;
          this.editRepairCostErrorMessage = 'Please enter a number';
          return false;
        } else {
          this.editRepairCostValid = true;
          return true;
        }
      }
      if (entry === 'repairHours') {
        //if the value isn't a number, then don't update the repair.
        if (isNaN(Number(this.selectedRepair.repairHours))) {
          this.editRepairHoursValid = false;
          this.editRepairHoursErrorMessage = 'Please enter a number';
          return false;
        } else {
          this.editRepairHoursValid = true;
          return true;
        }
      }
      if (entry === 'labourHours') {
        //if the value isn't a number, then don't update the repair.
        if (isNaN(Number(this.selectedRepair.labourHours))) {
          this.editLabourHoursValid = false;
          this.editLabourHoursErrorMessage = 'Please enter a number';
          return false;
        } else {
          this.editLabourHoursValid = true;
          return true;
        }
      }
      if (entry === 'change-notes') {
        //value cannot be empty.
        if (this.changeNotes === null || this.changeNotes.trim() === '') {
          this.changeNotesErrorMessage = 'Please enter a value';
          this.changeNotesValid = false;
          return false;
        } else {
          this.changeNotesValid = true;
          return true;
        }
      }
      if (entry === 'resubmit-notes') {
        //value cannot be empty.
        if (this.resubmitNotes === null || this.resubmitNotes.trim() === '') {
          this.resubmitNotesErrorMessage = 'Please enter a value';
          this.resubmitNotesValid = false;
          return false;
        } else {
          this.resubmitNotesValid = true;
          return true;
        }
      }
    },
    tableSort: tableSort,
    convertTimeStampToDate: convertTimeStampToDate,
    getDocumentButtonVariant(data) {
      if (data.detailsShowing) {
        return 'danger';
      } else {
        if (data.item.documentStatus === false) {
          return 'primary';
        } else if (data.item.documentStatus === null) {
          return 'warning';
        } else if (data.item.documentStatus === true) {
          return 'success';
        }
      }
    },
    repairRowClicked: function (rowData, index, field) {
      //populate selectedRepair
      if(this.isWorkOrderBeingEdited || this.submitErrorEditIssue === false || this.submitErrorEditRepairCost === false || this.submitErrorEditRepairHours === false) {
        return;
      }
      this.populateRepair(rowData, index);
      //set all other rows as not editable.
      for (let i = 0; i <= this.selectedWorkOrder.repairs.length - 1; i++) {
        this.selectedWorkOrder.repairs[i].editEnabled = false;
        this.selectedWorkOrder.repairs.splice(
          i,
          1,
          this.selectedWorkOrder.repairs[i]
        );
      }
      if (this.selectedRepair.completed === false) {
        this.selectedRepair.editEnabled = true;
        this.selectedWorkOrder.repairs.splice(index, 1, this.selectedRepair);
        window.setTimeout(
          () => document.getElementById(`txt_${field}_${index}`).focus(),
          0
        );
      }
    },
    handleRepairRowEditButton: function(rowData, index){
      if(this.editIssueValid && this.editRepairCostValid && this.editRepairHoursValid && this.editLabourHoursValid) {
        this.populateRepair(rowData, index);
        //set all other rows as not editable.
        for (let i = 0; i <= this.selectedWorkOrder.repairs.length - 1; i++) {
          this.selectedWorkOrder.repairs[i].editEnabled = false;
          this.selectedWorkOrder.repairs.splice(
            i,
            1,
            this.selectedWorkOrder.repairs[i]
          );
        }
        this.selectedRepair.editEnabled = true;
        this.selectedWorkOrder.repairs.splice(
          index,
          1,
          this.selectedRepair
        );
      }
    },
    hideAllEdits: async function (event) {
      //check validity of inputs
      if (
        this.editIssueValid &&
        this.editRepairCostValid &&
        this.editRepairHoursValid &&
        this.editLabourHoursValid
      ) {
        //check if it is clicking on another one of the textarea inputs for the repair
        if (
          event.relatedTarget === null ||
          event.relatedTarget.localName !== 'textarea' ||
          event.relatedTarget.id === 'VWO_addIssue_textarea'
        ) {
          //if it is not, check that the values are different from what they were originally, if they are not, do nothing, if they are submit the edits to the database
          if (
            this.selectedRepairInitial.issue !== this.selectedRepair.issue ||
            this.selectedRepairInitial.repair !== this.selectedRepair.repair ||
            this.selectedRepairInitial.sourceOfParts !==
            this.selectedRepair.sourceOfParts ||
            this.selectedRepairInitial.repairCost !==
            this.selectedRepair.repairCost ||
            this.selectedRepairInitial.repairHours !==
            this.selectedRepair.repairHours ||
            this.selectedRepairInitial.labourHours !==
            this.selectedRepair.labourHours
          ) {
           // an aborted save leaves the row in edit mode so the typed text survives
           const saved = await this.editRepair(event);
           if (saved === false) {
             return;
           }
          }
          //disable all inputs from editing
          let selectedWoCopy = lodash.cloneDeep(this.selectedWorkOrder);
          for (let i = 0; i <= this.selectedWorkOrder.repairs.length - 1; i++) {
            selectedWoCopy.repairs[i].editEnabled = false;
            selectedWoCopy.repairs.splice(
              i,
              1,
              selectedWoCopy.repairs[i]
            );
          }

          this.selectedWorkOrder = selectedWoCopy;
        }
      } else {
        this.submitErrorEditIssue = this.validateEntry('editIssue', true);
      }
    },
    onFileAdded: async function (file, data) {
      if (file.manuallyAdded === true) {
        return;
      }
      // concurrency guard
      if (!(await this.ensureWorkOrderFresh(this.selectedWorkOrder.view, this.woLastPull[this.selectedWorkOrder.view]))) {
        return;
      }
      let selectedWorkOrderCopy = this.removeExtraFields(
        lodash.cloneDeep(this.selectedWorkOrder)
      );
      let success = await uploadWorkOrderFile(
        file,
        this.selectedWorkOrder.view,
        selectedWorkOrderCopy.repairs,
        data.index,
        selectedWorkOrderCopy.repairs[data.index].documents
      );
      if (success[0] === true) {
        let selectedWoCopy = lodash.cloneDeep(this.selectedWorkOrder);
        let selectedRepairCopy = lodash.cloneDeep(this.selectedRepair);
        if (!selectedWoCopy.repairs[data.index].documents) {
          selectedWoCopy.repairs[data.index].documents = [];
        }
        if (!selectedRepairCopy.documents) {
          selectedRepairCopy.documents = [];
        }
        selectedWoCopy.repairs[data.index].documents.push(success[3]);
        selectedRepairCopy.documents.push(success[3]);
        this.selectedWorkOrder = selectedWoCopy;
        this.selectedRepair = selectedRepairCopy;

        let workOrdersDataCopy = lodash.cloneDeep(this.workOrdersData);
        const woIndex = workOrdersDataCopy.findIndex((x) => x.view === this.documentID);

        if (!workOrdersDataCopy[woIndex].repairs[data.index].documents) {
          workOrdersDataCopy[woIndex].repairs[data.index].documents = [];
        }
        workOrdersDataCopy[woIndex].repairs[data.index].documents.push(success[3]);
        this.workOrdersData = workOrdersDataCopy;

        this.$refs[`fileUploader-${data.index}`].removeFile(file);
        Vue.$toast.open({
          message: 'Document uploaded successfully',
          type: 'success',
          duration: 4000,
          position: 'top-right',
        });
      } else {
        Vue.$toast.open({
          message: 'An error was encountered uploading the document',
          type: 'error',
          duration: 4000,
          position: 'top-right',
        });
      }
    },
    onFileDeleted: async function (file, data) {
      let result = await Swal.fire({
        title: 'Are you sure?',
        text: 'This file will be deleted permanently',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#34c38f',
        cancelButtonColor: '#f46a6a',
        confirmButtonText: 'Yes',
      });
      if (result.value) {
        // concurrency guard
        if (!(await this.ensureWorkOrderFresh(this.selectedWorkOrder.view, this.woLastPull[this.selectedWorkOrder.view]))) {
          return;
        }
        let selectedWorkOrderCopy = this.removeExtraFields(
          lodash.cloneDeep(this.selectedWorkOrder)
        );
        let success = await deleteWorkOrderFile(
          file.file.name,
          this.selectedWorkOrder.view,
          selectedWorkOrderCopy.repairs,
          data.index,
          selectedWorkOrderCopy.repairs[data.index].documents
        );
        if (success[0] === true) {
          // Update the selected work order (current view)
          this.selectedWorkOrder.repairs[data.index].documents = success[1];
          this.selectedRepair.documents = success[1];

          // Also update the source data
          const workOrderIndex = this.workOrdersData.findIndex(wo => wo.view === this.documentID);
          if (workOrderIndex !== -1) {
            this.workOrdersData[workOrderIndex].repairs[data.index].documents = success[1];
          }

          Vue.$toast.open({
            message: 'Document deleted successfully',
            type: 'success',
            duration: 4000,
            position: 'top-right',
          });

          // if the doc status was "All Docs Received" then change the doc status to "Waiting for documents"
          if (data.item.documentStatus === true) {
            this.selectedWorkOrder.repairs[data.index].documentStatus = null;
            // Also update the source data for document status
            if (workOrderIndex !== -1) {
              this.workOrdersData[workOrderIndex].repairs[data.index].documentStatus = null;
            }
            this.changeDocumentStatus(null, false);
          }
        } else {
          Vue.$toast.open({
            message: 'An error was encountered deleting the document',
            type: 'error',
            duration: 4000,
            position: 'top-right',
          });
        }
      }
    },
    checkDocumentStatus() {
      if (this.selectedWorkOrder.repairs.find((x) => x.documentStatus === null)) {
        this.selectedWorkOrder.documentStatus = null;
        this.selectedRepair.documentStatus = null;
      } else if (this.selectedWorkOrder.repairs.find((x) => x.documentStatus === true) && !this.selectedWorkOrder.repairs.find((x) => x.documentStatus === null)) {
        this.selectedWorkOrder.documentStatus = true;
        this.selectedRepair.documentStatus = true;
      }
      else{
        this.selectedWorkOrder.documentStatus = false;
        this.selectedRepair.documentStatus = false;
      }
    },
    onFileDownloaded: async function (file) {
      axios
        .get(file.url, { responseType: 'blob' })
        .then((response) => {
          const blob = new Blob([response.data], { type: 'application/pdf' });
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = this.formattedFileName(file.file.name);
          link.click();
          URL.revokeObjectURL(link.href);
        })
        .catch((error) => {
          console.log(error);
          Vue.$toast.open({
            message: 'Error downloading document',
            type: 'error',
            duration: 4000,
            position: 'top-right',
          });
        });
    },
    formattedFileName: function (fileName) {
      return fileName.substring(fileName.indexOf('-') + 1);
    },
    async getCurrentUser() {
      try {
        let userID = JSON.parse(sessionStorage.authUser).uid;
        let user = await firestore().collection('employees').doc(userID).get();
        this.currentUser = user.data();
        this.currentUser.uid = userID;

        if (this.currentUser) {
          if (this.currentUser.position.includes('Admin')) {
            this.adminUser = true;
          }

          // set visible columns settings
          if(this.currentUser.visibleWorkOrderColumns){
            this.visibleColumns = this.currentUser.visibleWorkOrderColumns;
          }

          // set columns per screen setting
          if(this.currentUser.workOrderColumnsPerScreen){
            this.selectedColumnWidth = this.currentUser.workOrderColumnsPerScreen;
          }
        }
      } catch (error) {
        console.log(error);
      }
    },
    getUpToDateUnitName: function (unitUid) {
      let upToDateUnitName = null;
      const unitIndex = this.units.findIndex((x) => x.UID === unitUid);
      if (unitIndex !== -1) {
        upToDateUnitName = this.units[unitIndex].name;
      }
      return upToDateUnitName;
    },
    showAddRepairPersonFields: function (data) {
      if(this.submitErrorEditIssue === null && this.submitErrorEditRepairCost === null && this.submitErrorEditRepairHours === null){
        try {
          this.populateRepair(data.item, data.index);
          let selectedWoCopy = lodash.cloneDeep(this.selectedWorkOrder);

          selectedWoCopy.repairs[data.index].addRepairmanVisible = !this
            .selectedWorkOrder.repairs[data.index].addRepairmanVisible;

            selectedWoCopy.repairs[data.index].newRepairman.name = null;
            selectedWoCopy.repairs[data.index].newRepairman.id = null;
            selectedWoCopy.repairs[
            data.index
          ].newRepairman.date = new Date(selectedWoCopy.date);

          this.selectedWorkOrder = selectedWoCopy;

          //after showing the repair person drop down, put focus in the search bar. Need a timeout because of the v-show
          setTimeout(() => {
            this.$refs[`ddl_repairPerson_${data.index}`].$refs.search.focus();
          }, '250');
        } catch (err) {
          console.log('Error displaying repair persons:');
          console.log(err);
          Swal.fire('Error!', 'Error displaying repair persons', 'error');
        }
      }
    },
    checkMaintenanceSettings: function () {
      if (this.$store.state.custom.workOrders.repair_hours_enabled) {
        this.repairFields.splice(4, 0, {
          key: 'repair_hours',
          label: 'Labour Cost',
          class: ['wo-col-optional'],
          thClass: ['text-nowrap'],
          tdClass: ['c-20', 'full-height-td', 'optional-cell'],
        });
      } else {
        this.editRepairHoursValid = true; //if hours are not enabled, then we won't block the repair update validation
      }
      if (this.$store.state.custom.workOrders.labour_hours_enabled) {
        this.repairFields.splice(4, 0, {
          key: 'labour_hours',
          label: 'Labour Hours',
          class: ['wo-col-optional'],
          thClass: ['text-nowrap'],
          tdClass: ['c-20', 'full-height-td', 'optional-cell'],
        });
      } else {
        this.editLabourHoursValid = true;
      }
      if (this.$store.state.custom.workOrders.repair_cost_enabled) {
        this.repairFields.splice(4, 0, {
          key: 'repair_cost',
          label: 'Parts Cost',
          class: ['wo-col-optional'],
          thClass: ['text-nowrap'],
          tdClass: ['c-20', 'full-height-td', 'optional-cell'],
        });
      } else {
        this.editRepairCostValid = true; //if hours are not enabled, then we won't block the repair update validation
      }
    },
    repairsApprovedBadgeLogic: function (repairsApproved, changeNotes) {
      let retValue = {
        class: null,
        text: null,
      };
      if (!repairsApproved.value) {
        //if the repairs are not approved then check if they are waiting
        if (repairsApproved.value === null) {
          retValue.text = 'Awaiting Review';
          retValue.class = 'warning';

          // check if the work order was resubmitted
          if(changeNotes.length >= 1){
            retValue.text = 'Resubmitted for Review';
          }
        } else {
          //if we aren't waiting, then the repairs are not approved.
          //if we aren't waiting and there are change notes, then we have requested changes.
          if (changeNotes.length >= 1) {
            retValue.text = 'Changes Requested';
            retValue.class = 'primary';
          } else {
            retValue.text = 'Not Submitted';
            retValue.class = 'danger';
          }
        }
      } else {
        //repairs are approved.
        retValue.text = 'Approved';
        retValue.class = 'success';
      }

      return retValue;
    },
    async getSettings() {
      try {
        const querySnapshot = await firestore()
          .collection('organizations')
          .doc(sessionStorage.getItem('org'))
          .collection('settings')
          .get();

        querySnapshot.forEach((doc) => {
          this.orgSettings = doc.data();
        });
      } catch (error) {
        Swal.fire('Error!', 'Error getting the settings.', 'error');
      }
    },
    async columnDisplayCheckState(columnKey, newValue) {
      const columnKeys = [
        'issues',
        'waitingToStart',
        'inProgress',
        'waitingForDocs',
        'changesRequested',
        'completed',
        'submitted'
      ];

      // update the specific column with the new value
      this.visibleColumns[columnKey] = newValue;

      if (columnKey === 'all') {
        // if the "Select All" checkbox is clicked, set all columns to the new value
        columnKeys.forEach(key => {
          this.visibleColumns[key] = newValue;
        });
      } else {
        // check if all individual columns are selected
        const allSelected = columnKeys.every(key => this.visibleColumns[key]);

        // update the "Select All" checkbox based on the state of individual columns
        this.visibleColumns.all = allSelected;
      }

      // update user data
      try {
        await firestore()
          .collection('employees')
          .doc(this.currentUser.auth)
          .update({
            visibleWorkOrderColumns: this.visibleColumns,
          });
      } catch (err) {
        console.error(err);
        Swal.fire('Error!', 'Error updating the user\'s visible columns setting', 'error');
      }
    },
    handleSelectedColumnWidth(){
      // update user data
      try {
          firestore()
          .collection('employees')
          .doc(this.currentUser.auth)
          .update({
            workOrderColumnsPerScreen: this.selectedColumnWidth,
          });
      }
      catch (err) {
        console.log(err);
        Swal.fire('Error!', 'Error updating the users work order columns per screen setting', 'error');
      }
    },
    extractNumber(str){
        // function to extract numbers from work order string
        // reg expression to find numbers in the string
        let num = str.match(/\d+/);
        // parse the found number, return NaN if no number found
        return num ? parseInt(num[0]) : NaN;
    },
    async addIssueToExistingWorkOrder(data){
      // block if the WO is currently open/being edited by someone else
      try {
        const editStatus = await checkWorkOrderEditingStatus(data.view);
        if (editStatus.isBeingEdited && editStatus.editedBy &&
            editStatus.editedBy.uid !== (this.currentUser && this.currentUser.uid)) {
          Swal.fire({
            title: 'Work Order In Use',
            html: `This work order is currently being edited by <strong>${editStatus.editedBy.name || 'another user'}</strong>. You can't add issues while it's open. Please try again once they're finished.`,
            icon: 'warning',
            confirmButtonColor: '#34c38f',
          });
          return;
        }
      } catch (error) {
        console.log('Error checking work order editing status', error);
        // on error, fall through (fetch-fresh-and-append still protects the data)
      }

      this.selectedWorkOrderNoteEditMode = false;
      let index = this.workOrdersData.findIndex((x) => x.view === data.view);
      this.selectedWorkOrder = this.workOrdersData[index];
      this.selectedWorkOrder.repairs.forEach((repair, index) => {
        repair.editEnabled = false;
        this.sortRepairsByDate(index);
      });

      this.documentID = data.view;
      this.selectedWorkOrderInitial = lodash.cloneDeep(this.workOrdersData[index]);

      // make string to display selected issues
      let selectedIssuesString = '';
      this.selectedRepairs.forEach((rep, index)=>{
        if(index === 0){
          selectedIssuesString = `- ${rep.issue}`;
        } else{
          selectedIssuesString += `<br>- ${rep.issue}`;
        }
      });

      // add issue(s) to the selected work order
      Swal.fire({
        title: 'Are you sure?',
        html: `The following selected issue(s) will be added to work order ${this.selectedWorkOrder.number}<br><br>${selectedIssuesString}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#34c38f',
        cancelButtonColor: '#f46a6a',
        confirmButtonText: 'Yes',
      }).then(async (result) => {
        if (result.value) {
          try {
            const hoursEnabled = this.$store.state.custom.workOrders.repair_hours_enabled;
            const costEnabled = this.$store.state.custom.workOrders.repair_cost_enabled;
            const labourHoursEnabled = this.$store.state.custom.workOrders.labour_hours_enabled;

            // fetch-fresh-and-append: re-read current issues then append (never clobbers)
            const snap = await getWorkOrderById(this.selectedWorkOrder.view);
            const freshData = (snap && snap.exists) ? snap.data() : null;
            const freshRepairs = (freshData && Array.isArray(freshData.repairs))
              ? freshData.repairs
              : lodash.cloneDeep(this.selectedWorkOrder.repairs);

            const mergedRepairs = freshRepairs.concat(this.selectedRepairs);
            const repairsCompleteStatus = this.checkRepairStatus(mergedRepairs);

            await firestore()
                .collection('workOrders')
                .doc(this.selectedWorkOrder.view)
                .update(withLastUpdated({
                  repairs: mergedRepairs,
                  repairsComplete: repairsCompleteStatus
                }))
                .then(async () => {
                  let promises = [];
                  let repairsCopy = lodash.cloneDeep(this.repairsData);

                  this.selectedRepairs.forEach((repair) => {
                    promises.push(
                      firestore()
                        .collection('workOrderRepairs')
                        .doc(repair.id)
                        .update({ number: this.selectedWorkOrder.number, workOrderID: this.selectedWorkOrder.view })
                        .then(() => {
                          let idx = repairsCopy.findIndex((x) => x.id === repair.id);
                          if (idx !== -1) repairsCopy.splice(idx, 1);
                        })
                    );
                  });

                  await Promise.all(promises);

                  // refresh the board cache entry with a hydrated copy of the merged WO
                  const mergedRaw = {
                    ...(freshData || lodash.cloneDeep(this.selectedWorkOrder)),
                    repairs: mergedRepairs,
                    repairsComplete: repairsCompleteStatus,
                    view: this.selectedWorkOrder.view,
                  };
                  const hydratedMerged = hydrateWorkOrderDoc(
                    mergedRaw, this.selectedWorkOrder.view, this.units, hoursEnabled, costEnabled, labourHoursEnabled
                  );
                  hydratedMerged.selectedToReceiveIssue = false;

                  let woIndex = this.workOrdersData.findIndex((x) => x.view === this.selectedWorkOrder.view);
                  if (woIndex !== -1) this.workOrdersData.splice(woIndex, 1, hydratedMerged);
                  else this.workOrdersData.push(hydratedMerged);
                  this.repairsData = repairsCopy;
                  this.woLastPull[this.selectedWorkOrder.view] = Date.now();

                  this.$refs['modal-addIssueToExistingWorkOrder'].hide();
                  Vue.$toast.open({
                    message: `Issue(s) successfully added to work order ${this.selectedWorkOrder.number}`,
                    type: 'success',
                    duration: 4000,
                    position: 'top-right',
                  });

                  // re-sort the work order columns
                  this.filterAllColumns();
                  this.filterWorkOrdersNotRendered();
                });
          } catch (error) {
            console.log(`Error adding issues to work order ${this.selectedWorkOrder.number}`, error);
            Vue.$toast.open({
              message: `An error was encountered when adding the selected issue(s) to work order ${this.selectedWorkOrder.number}`,
              type: 'error',
              duration: 4000,
              position: 'top-right',
            });
          }
        }
      });
    },
    handleAddIssueToExistingWOModalClose(){
      // if a work order is selected, de-select it
      if(this.selectedExistingWO){
        let workOrdersCopy = lodash.cloneDeep(this.workOrdersData);
        let woIndex = workOrdersCopy.findIndex((x) => x.number === this.selectedExistingWO.number);
        if(woIndex !== -1){
          workOrdersCopy[woIndex].selectedToReceiveIssue = false;
          this.workOrdersData = workOrdersCopy;
        }

        this.selectedExistingWO = null;
      }
    },
    removeIssueFromWorkOrder(data, index){
      this.selectedRepair = lodash.cloneDeep(data);
      if (
        this.selectedRepairInitial.itemNumber !== this.selectedRepair.itemNumber
      ) {
        this.selectedRepairInitial = lodash.cloneDeep(data);
      }

      Swal.fire({
        title: 'Are you sure?',
        text: 'Any fields filled out after the issue was added to the WO will be deleted and it will be added back to the "New Issues" column.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#34c38f',
        cancelButtonColor: '#f46a6a',
        confirmButtonText: 'Yes',
      }).then(async (result) => {
        if(result.value){
          // concurrency guard
          if (!(await this.ensureWorkOrderFresh(this.selectedWorkOrder.view, this.woLastPull[this.selectedWorkOrder.view]))) {
            return;
          }
          let upToDateIssue = lodash.cloneDeep(this.selectedRepair.issue);
          // remove issue from the work order
          let workOrderCopy = this.removeExtraFields(lodash.cloneDeep(this.selectedWorkOrder));

          // an id-less repair matches the wrong row by id, so fall back to the clicked row
          let repairIndex = this.selectedRepair.id
            ? workOrderCopy.repairs.findIndex((x) => x.id === this.selectedRepair.id)
            : index;
          if (repairIndex < 0 || repairIndex >= workOrderCopy.repairs.length) {
            repairIndex = index;
          }

          workOrderCopy.repairs.splice(repairIndex, 1);
          this.selectedWorkOrder.repairs.splice(index, 1);

          //check the status of the repairs
          let repairsComplete = this.checkRepairStatus(
            this.selectedWorkOrder.repairs
          );

          try{
            firestore()
              .collection('workOrders')
              .doc(this.selectedWorkOrder.view)
              .update(withLastUpdated({
                repairs: workOrderCopy.repairs,
                repairsComplete: repairsComplete
              }))
              .then(()=>{
                // remove issue from local work order data
                const workOrderCopyWithExtraFields = this.addExtraFields(workOrderCopy);
                this.selectedWorkOrder.repairsComplete = repairsComplete;
                let woDataCopy = lodash.cloneDeep(this.workOrdersData);
                const woDataIndex = woDataCopy.findIndex((x) => x.view === this.selectedWorkOrder.view);
                woDataCopy[woDataIndex].repairs = workOrderCopyWithExtraFields.repairs;
                this.workOrdersData = woDataCopy;

                // now add the issue back to new issues by updating its workOrderID and number fields to null
                // if it was an issue added via the work order modal then it won't have a workOrderRepair doc made so create one
                if(this.selectedRepair.id && this.selectedRepair.id.trim() !== ''){
                  firestore()
                  .collection('workOrderRepairs')
                  .doc(this.selectedRepair.id)
                  .update({
                    number: null, workOrderID: null, repair: null, issue: upToDateIssue
                  })
                  .then(()=>{
                    // add the repair to our local repairs data, first we have to retreive it from the newWorkOrderRepairs collection
                    firestore()
                      .collection('workOrderRepairs')
                      .doc(this.selectedRepair.id)
                      .get()
                      .then((doc)=>{
                        if(doc.exists){
                          let repairsDataCopy = lodash.cloneDeep(this.repairsData);
                          let workOrderRepair = doc.data();
                          workOrderRepair['id'] = doc.id;
                          workOrderRepair['isVisible'] = true;
                          repairsDataCopy.push(workOrderRepair);
                          this.repairsData = repairsDataCopy;

                          // because we got the repair from firestore we have to reformat the timestamp dates for it
                          this.$emit('format-repair-data-timestamps', repairsDataCopy, true);

                          Vue.$toast.open({
                            message: `The issue was successfully moved back to New Issues`,
                            type: 'success',
                            duration: 4000,
                            position: 'top-right',
                          });
                        }
                        else{
                          console.log(`An error occurred while trying to retrieve this work order issue from the database`);
                          Vue.$toast.open({
                            message: `An error occurred while trying to retrieve this work order issue from the database`,
                            type: 'error',
                            duration: 4000,
                            position: 'top-right',
                          });
                        }
                      });
                  });
                } else{
                  // create new workOrderRepair doc in firestore
                  let newIssue = {
                    date: this.selectedRepair.date ? this.selectedRepair.date : this.selectedWorkOrder.date,
                    createdDate: this.selectedRepair.createdDate ? this.selectedRepair.createdDate : this.selectedWorkOrder.createdDate,
                    units: this.selectedWorkOrder.units,
                    org: sessionStorage.org,
                    issue: this.selectedRepair.issue,
                    repair: null,
                    number: null,
                    workOrderID: null,
                    completed: false,
                    repairedBy: [
                      {
                        name: null,
                        id: null,
                        date: null,
                      },
                    ],
                    completedBy: {
                      name: null,
                      id: null,
                      date: null,
                    },
                    submitted: this.selectedRepair.submitted ? this.selectedRepair.submitted : this.selectedWorkOrder.submitted,
                    sourceOfParts: null,
                    repairCost: null,
                    documents: null,
                    documentStatus: null,
                    isVisible: true,
                    odometer: this.selectedRepair.odometer ? this.selectedRepair.odometer : this.selectedWorkOrder.odometer,
                    hourMeter: this.selectedRepair.hourMeter ? this.selectedRepair.hourMeter : this.selectedWorkOrder.hourMeter,
                  };

                  firestore()
                  .collection('workOrderRepairs')
                  .add(newIssue)
                  .then((docRef)=>{
                    let repairsDataCopy = lodash.cloneDeep(this.repairsData);
                    newIssue['id'] = docRef.id;
                    repairsDataCopy.push(newIssue);
                    this.repairsData = repairsDataCopy;

                    Vue.$toast.open({
                      message: `The issue was successfully moved back to New Issues`,
                      type: 'success',
                      duration: 4000,
                      position: 'top-right',
                    });
                  })
                  .catch((err)=>{
                    console.log(`A database error occurred while trying to move this issue back to New Issues: ${err}`);
                    Vue.$toast.open({
                      message: `A database error occurred while trying to move this issue back to New Issues`,
                      type: 'error',
                      duration: 4000,
                      position: 'top-right',
                    });
                  });
                }
              });
          }
          catch(err){
            console.log(`An error occurred while trying to move the issue back to New Issues: `, err);
            Vue.$toast.open({
              message: `An error occurred while trying to move the issue back to New Issues`,
              type: 'error',
              duration: 4000,
              position: 'top-right',
            });
          }
        }
      });

    },
    updateColumnUnitFilter(selectedUnit, columnDataProp){
      // when a work order column's unti filter is changed it emits an event from the workOrderColumn child to
      // here and passes the unit filter selection and a string of the data property that should be changed
      this[columnDataProp] = selectedUnit;

      // re-sort the work order columns
      this.filterAllColumns();
    },
    updateSubmittedColumnFilter(filter, filterValue){
      if(filter === 'status'){
        this.selectedSubmittedColumnFilter = filterValue;
      } else{
        this.selectedSubmittedColumnSort = filterValue;
      }

      // re-sort the submitted column
      this.filterSubmittedWorkOrders();
    },
    filterNotStartedWorkOrders: debounce(function(){
      this.notStartedWorkOrders = this.selectedNotStartedUnit
        ? this.workOrdersData.filter(
          (x) =>
            x.repairsApproved.value === false &&
            (!x.changeNotes || x.changeNotes.length === 0) &&
            !x.repairs.find(x => x.repairedBy[0]?.id !== null || (x.repair !== null && x.repair !== "")) &&
            !x.repairsComplete.value &&
            x.units.includes(this.selectedNotStartedUnit)
        )
        : this.workOrdersData.filter(
          (x) =>
            x.repairsApproved.value === false &&
            (!x.changeNotes || x.changeNotes.length === 0) &&
            !x.repairs.some(repair => repair.repairedBy[0]?.id !== null || (repair.repair !== null && repair.repair !== "")) &&
            !x.repairsComplete.value
        );
    }, 0),
    filterInProgressWorkOrders: debounce(function(){
      this.inProgressWorkOrders = this.selectedInProgressUnit
        ? this.workOrdersData.filter(
          (x) =>
            x.repairsApproved.value === false &&
            (!x.changeNotes || x.changeNotes.length === 0) &&
            !x.repairsComplete.value &&
            x.repairs.some(
            repair => (repair.repair && repair.repair !== "") ||
                      (repair.repairedBy && repair.repairedBy[0] && repair.repairedBy[0].id)
            ) &&
            x.units.includes(this.selectedInProgressUnit) &&
            !(x.repairs.every(
            repair => (repair.repair && repair.repair !== "") &&
                      (repair.repairedBy && repair.repairedBy[0] && repair.repairedBy[0].id)
            ) && x.repairs.find(x => x.documentStatus === null))
        )
        : this.workOrdersData.filter(
          (x) =>
            x.repairsApproved.value === false &&
            (!x.changeNotes || x.changeNotes.length === 0) &&
            !x.repairsComplete.value &&
            x.repairs.some(
            repair => (repair.repair && repair.repair !== "") ||
                      (repair.repairedBy && repair.repairedBy[0] && repair.repairedBy[0].id)
            ) &&
            !(x.repairs.every(
            repair => (repair.repair && repair.repair !== "") &&
                      (repair.repairedBy && repair.repairedBy[0] && repair.repairedBy[0].id)
            ) && x.repairs.find(x => x.documentStatus === null))
        );
    }, 0),
    filterWaitingOnDocsWorkOrders: debounce(function(){
      this.waitingOnDocsWorkOrders = this.selectedWaitingOnDocsUnit
        ? this.workOrdersData.filter(
          (x) =>
            x.repairsApproved.value === false &&
            x.repairs.find(x => x.documentStatus === null) &&
            (!x.changeNotes || x.changeNotes.length <= 0 ) &&
            x.units.includes(this.selectedWaitingOnDocsUnit) &&
            x.repairs.every(
            repair => (repair.repair && repair.repair !== "") &&
                      (repair.repairedBy && repair.repairedBy[0] && repair.repairedBy[0].id)
            )
        )
        : this.workOrdersData.filter(
          (x) =>
            x.repairsApproved.value === false &&
            x.repairs.find(x => x.documentStatus === null) &&
            (!x.changeNotes || x.changeNotes.length <= 0 ) &&
            x.repairs.every(
            repair => (repair.repair && repair.repair !== "") &&
                      (repair.repairedBy && repair.repairedBy[0] && repair.repairedBy[0].id)
            )
        );
    }, 0),
    filterChangesRequestedWorkOrders: debounce(function(){
      this.changesRequestedWorkOrders = this.selectedChangeRequestedUnit
        ? this.workOrdersData.filter(
          (x) =>
            x.repairsApproved.value === false &&
            x.changeNotes &&
            x.changeNotes.length > 0 &&
            x.units.includes(this.selectedChangeRequestedUnit)
        )
        : this.workOrdersData.filter(
          (x) =>
            x.repairsApproved.value === false &&
            x.changeNotes &&
            x.changeNotes.length > 0
        );
    }, 0),
    filterCompletedWorkOrders: debounce(function(){
      this.completedWorkOrders = this.selectedReadyForSubmitUnit
        ? this.workOrdersData.filter(
          (x) =>
            x.repairsApproved.value === false &&
            x.repairsComplete.value === true &&
            x.units.includes(this.selectedReadyForSubmitUnit) &&
            (!x.changeNotes || x.changeNotes.length <= 0) &&
            !x.repairs.find(x => x.documentStatus === null)
        )
        : this.workOrdersData.filter(
          (x) =>
            x.repairsApproved.value === false &&
            x.repairsComplete.value === true &&
            (!x.changeNotes || x.changeNotes.length <= 0) &&
            !x.repairs.find(x => x.documentStatus === null)
        );
    }, 0),
    filterSubmittedWorkOrders: debounce(function(){
      let filteredData = this.selectedCompletedUnit
        ? this.workOrdersData.filter(
          (x) =>
            x.repairsApproved.value !== false &&
            this.selectedSubmittedColumnFilter.label !== 'All' ? x.repairsApproved.value === this.selectedSubmittedColumnFilter.value : null &&
            x.units.includes(this.selectedCompletedUnit)
        )
        : this.workOrdersData.filter(
          (x) =>
            x.repairsApproved.value !== false &&
            (this.selectedSubmittedColumnFilter.label !== 'All' ? x.repairsApproved.value === this.selectedSubmittedColumnFilter.value : (x.repairsApproved.value === true || x.repairsApproved.value === null))
        );

        // check if awaiting review or resubmitted for review was selected
        if(this.selectedSubmittedColumnFilter.label === 'Awaiting Review'){
          filteredData = filteredData.filter((x) => x.changeNotes.length === 0);
        } else if(this.selectedSubmittedColumnFilter.label === 'Resubmitted for Review'){
          filteredData = filteredData.filter((x) => x.changeNotes.length > 0);
        }

        let sortedData = [];

        if(this.selectedSubmittedColumnSort === 'WO # Asc'){
          sortedData = filteredData.sort((a, b) => {
            // extract numbers from a.number and b.number and compare them
            let numA = this.extractNumber(a.number);
            let numB = this.extractNumber(b.number);

            // compare the extracted numbers
            return numA - numB;
          });
        }
        else if(this.selectedSubmittedColumnSort === 'WO # Desc'){
          sortedData = filteredData.sort((a, b) => {
            // extract numbers from a.number and b.number and compare them
            let numA = this.extractNumber(a.number);
            let numB = this.extractNumber(b.number);

            // compare the extracted numbers
            return numB - numA;
          });
        }
        else if(this.selectedSubmittedColumnSort === 'Unit Asc'){
          sortedData = filteredData.sort((a, b) => {
            const unitA = this.getUpToDateUnitName(a.units[0]);
            const unitB = this.getUpToDateUnitName(b.units[0]);
            if (unitA < unitB) {
              return -1;
            }
            if (unitA > unitB) {
              return 1;
            }
            return 0;
          });
        }
        else if(this.selectedSubmittedColumnSort === 'Unit Desc'){
          sortedData = filteredData.sort((a, b) => {
            const unitA = this.getUpToDateUnitName(a.units[0]);
            const unitB = this.getUpToDateUnitName(b.units[0]);
            if (unitB < unitA) {
              return -1;
            }
            if (unitB > unitA) {
              return 1;
            }
            return 0;
          });
        }
        else if(this.selectedSubmittedColumnSort === 'Date Asc'){
          sortedData = filteredData.sort((a, b) => {
              return new Date(a.date) - new Date(b.date);
          });
        }
        else{
          // sort by date desc
          sortedData = filteredData.sort((a, b) => {
              return new Date(b.date) - new Date(a.date);
          });
        }

        this.submittedWorkOrders = sortedData;
    }, 0),
    filterExistingWorkOrdersWithSameUnitsAsSelectedIssues: debounce(function(){
      // this filters out existing workorder options that the user can add the selected issue(s) to
      let workOrders = [];
      let sortedWorkOrders = [];

      let workOrdersDataCopy = lodash.cloneDeep(this.workOrdersData);

      if(this.selectedRepairsUnitsConsistent){
        workOrders = workOrdersDataCopy.filter((x) => {
          if(x.units && this.selectedRepairs.length > 0 && x.repairsApproved.value === false){
            // get all unique units from all selected repairs
            const allSelectedUnits = [...new Set(this.selectedRepairs.flatMap(r => r.units))];
            // check if work order contains all units from all selected repairs
            const allUnitsIncluded = allSelectedUnits.every(unit => x.units.includes(unit));
            if (!allUnitsIncluded) return false;
            return true;
          }
        });
      }

      sortedWorkOrders = workOrders.sort((a, b) => {
        // extract numbers from a.number and b.number and compare them
        let numA = this.extractNumber(a.number);
        let numB = this.extractNumber(b.number);

        // compare the extracted numbers
        return numB - numA;
      });

      this.existingWorkOrdersWithSameUnitsAsSelectedIssues = sortedWorkOrders;
    }, 300),
    filterWorkOrdersNotRendered: debounce(function(){
      let nonRenderedWOs = [];

      if((this.notStartedWorkOrders.length + this.inProgressWorkOrders.length + this.waitingOnDocsWorkOrders.length + this.changesRequestedWorkOrders.length + this.completedWorkOrders.length + this.submittedWorkOrders.length) !== this.workOrdersData.length){
        // this means our logic is not covering a scenario for displaying a workorder in one of our columns and it's not being rendered
        nonRenderedWOs = lodash.cloneDeep(this.workOrdersData).filter((x) =>
          !this.notStartedWorkOrders.find((wo) => wo.number === x.number) &&
          !this.inProgressWorkOrders.find((wo) => wo.number === x.number) &&
          !this.waitingOnDocsWorkOrders.find((wo) => wo.number === x.number) &&
          !this.completedWorkOrders.find((wo) => wo.number === x.number) &&
          !this.changesRequestedWorkOrders.find((wo) => wo.number === x.number) &&
          (this.selectedSubmittedColumnFilter.label === 'All' && !this.submittedWorkOrders.find((wo) => wo.number === x.number))
        );
      }

      this.workOrdersNotRendered = nonRenderedWOs;
    }, 0),
    getUnitLabel(selectedOption) {
      let unit = null;

      unit = this.units.find((unit) => unit.UID === selectedOption.UID);

      return unit ? unit.name : 'Unit not found';
    },
  },
  async mounted() {
    await this.getCurrentUser();

    //check to see if the hours and cost fields are enabled.
    this.checkMaintenanceSettings();

    await this.getSettings()

    // check if the org has the requested changes notification setting enabled
    if(this.orgSettings && this.orgSettings.work_orders && this.orgSettings.work_orders.requestedChangesNotificationEmailEnabled !== undefined){
      this.sendRequestedChangesNotiEmailOption = this.orgSettings.work_orders.requestedChangesNotificationEmailEnabled;
    } else{
      // the org has never enabled the setting so set to false
      this.sendRequestedChangesNotiEmailOption = false;
    }

    this.unitListWithExtraOption = JSON.parse(JSON.stringify(this.activeUnits));
    this.unitListWithExtraOption.unshift({ UID: null, name: 'All Units' });

    await clearWorkOrderBeingEditedByUser(this.currentUser.uid);

    // update loading spinner state in parent
    this.$emit('update-loading-state', false);
    this.pageLoading = false;

  },
  beforeUnmount() {
    this.stopHeartbeat();
  },
  created: function () {
    //get the query strings from the route, if there are any.
    if (
      this.$route.query != undefined &&
      this.$route.query.workOrder != undefined
    ) {
      //parse the work order ids here. If we have ids, then we will sort the
      //work order array, so this id is at the top.
      this.queryIDs = this.$route.query.workOrder.split(',');
    }
  },
  beforeDestroy() {
    this.stopHeartbeat();
    this.stopInactivityWatch();

    // Clean up Firebase stream when component is destroyed
    if (this.workOrderStream) {
      this.workOrderStream();
      this.workOrderStream = null;
    }
  },
  computed: {
    singleIssue: {
      get() {
        if (!this.documentID) return null;
        const draft = this.singleIssueDrafts[this.documentID];
        return draft === undefined ? null : draft;
      },
      set(value) {
        if (!this.documentID) return;
        this.$set(this.singleIssueDrafts, this.documentID, value);
      },
    },
    // duplicateWorkOrders: function(){
    //   // Use this for testing the work order columns
    //   let dupWorkOrders = [];

    //   lodash.cloneDeep(this.workOrdersData).forEach((wo) => {
    //     let count = 0;

    //     if(this.notStartedWorkOrders.find((x) => x.number === wo.number)){
    //       count++;
    //     }

    //     if(this.inProgressWorkOrders.find((x) => x.number === wo.number)){
    //       count++;
    //     }

    //     if(this.waitingOnDocsWorkOrders.find((x) => x.number === wo.number)){
    //       count++;
    //     }

    //     if(this.completedWorkOrders.find((x) => x.number === wo.number)){
    //       count++;
    //     }

    //     if(this.changesRequestedWorkOrders.find((x) => x.number === wo.number)){
    //       count++;
    //     }

    //     if(this.submittedWorkOrders.find((x) => x.number === wo.number)){
    //       count++;
    //     }

    //     if(count > 1){
    //       dupWorkOrders.push(wo);
    //     }
    //   });

    //   return dupWorkOrders;
    // },
    isWorkOrderBeingEdited() {
      // This function checks if the work order is being edited by the current user
      return this.workOrderEditingStatus.isBeingEdited && this.workOrderEditingStatus.editedBy && this.workOrderEditingStatus.editedBy.uid !== this.currentUser?.uid
    },
    currentUserHoldsEditingLock() {
      // true while the current user is the one recorded in currentlyEditedBy for the open work order
      return !!this.documentID &&
        this.workOrderEditingStatus.isBeingEdited &&
        this.workOrderEditingStatus.editedBy &&
        this.workOrderEditingStatus.editedBy.uid === this.currentUser?.uid;
    },
    currentUserHasUnsavedWorkOrderChanges() {
      // "unsaved" means typed into the view work order modal but not written to the database yet.
      // repair cells save on blur, so only a cell that is still open with changed text counts.
      const repairs = this.selectedWorkOrder.repairs || [];
      const repairCellOpen = repairs.some((repair) => repair.editEnabled);
      const repairCellChanged = repairCellOpen && (
        this.selectedRepairInitial.issue !== this.selectedRepair.issue ||
        this.selectedRepairInitial.repair !== this.selectedRepair.repair ||
        this.selectedRepairInitial.sourceOfParts !== this.selectedRepair.sourceOfParts ||
        this.selectedRepairInitial.repairCost !== this.selectedRepair.repairCost ||
        this.selectedRepairInitial.repairHours !== this.selectedRepair.repairHours ||
        this.selectedRepairInitial.labourHours !== this.selectedRepair.labourHours
      );

      const generalNoteChanged = this.selectedWorkOrderNoteEditMode &&
        this.selectedWorkOrderNoteEdit !== (this.selectedWorkOrder.generalNotes || '');

      const issueDraftEntered = this.singleIssue !== null && this.singleIssue.trim() !== '';

      return repairCellChanged || generalNoteChanged || issueDraftEntered;
    },

    activeUnits(){
      return this.units.filter(unit => !unit.dateDeactivated);
    },
    //here we compute whether the buttons in the view work order modal should be visible or not, we can change these based upon
    //position in the next phase
    submitVisible: function () {
      if (
        this.selectedWorkOrder.repairsComplete &&
        this.selectedWorkOrder.repairsApproved
      ) {
        if (
          this.selectedWorkOrder.repairsComplete.value &&
          this.selectedWorkOrder.repairsApproved.value === false
        ) {
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    },
    approveVisible: function () {
      if (this.selectedWorkOrder.repairsApproved) {
        //check to see if all repairs have been approved.
        //if repairs have been submitted, check user level to determine if the approve and request changes buttons are visible
        if (this.adminUser) {
          //only show the approve button if the user is an admin
          if (this.selectedWorkOrder.repairsApproved.value === null) {
            return true;
          } else {
            return false;
          }
        } else {
          return null;
        }
      } else {
        return false;
      }
    },
    unapproveVisible: function () {
      if (this.selectedWorkOrder.repairsApproved) {
        //if repairs have been submitted, check user level to determine if visible
        if (this.adminUser) {
          if (this.selectedWorkOrder.repairsApproved.value === true) {
            return true;
          } else {
            return false;
          }
        } else {
          return false;
        }
      } else {
        return false;
      }
    },
    unsubmitVisible: function () {
      if (this.selectedWorkOrder.repairsApproved) {
        //unsubmit is the same as request changes except for unsubmit is visible to non admin users
        //and request changes is visible to admin users, you also have to submit a reason when clicking request changes
        //whereas unsubmit is just for users to rescind the approval request if they submitted it accidentally
        //or see a mistake that they want to fix

        //check to see if all repairs have been approved.
        //if repairs have been submitted, check user level to determine if the approve and request changes buttons are visible
        if (!this.adminUser) {
          if (this.selectedWorkOrder.repairsApproved.value === null) {
            return true;
          } else {
            return false;
          }
        } else {
          return null;
        }
      } else {
        return false;
      }
    },
    editVisible: function () {
        if(this.isWorkOrderBeingEdited) {
          return false;
        }
      if (this.selectedWorkOrder.repairsApproved) {
        if (this.selectedWorkOrder.repairsApproved.value !== false) {
          return false;
        } else {
          return true;
        }
      } else {
        return true;
      }
    },
    submittedBadgeVisible: function () {
      return !this.selectedWorkOrder.repairsApproved.value
        ? this.selectedWorkOrder.repairsApproved.value === null
          ? true
          : false
        : false;
    },
    selectedRepairsUnitsConsistent: function (){
      // this is used to disable the "Add to Existing WO" button if the selected repairs do not have any matching units
      if(this.selectedRepairs && this.selectedRepairs.length > 0){
        // ensure all selected repairs have units defined
        return this.selectedRepairs.every(rep => rep.units && rep.units.length > 0);
      }
      return false;
    },
    filteredRepairs() {
      let data = []
      // check if the column's unit filter is selected and if any issues in the column are selected
      if (this.selectedIssuesColumnUnit) {
        // filter so we only have issues with a unit that matches the column's selected unit filter
        data = this.repairsData.filter(x =>
          x.units.some(unit => unit === this.selectedIssuesColumnUnit)
        );

        // filter the issues again if at least one issue is selected so we only have issues that share at least one unit
        if(this.selectedRepairs.length > 0){
          data = data.filter(x =>
            this.selectedRepairs[0].units.some(unit => x.units.includes(unit))
          );
        }
      } else if (this.selectedRepairs.length > 0) {
        // filter the issues so we only have issues that share at least one unit
        data = this.repairsData.filter(x =>
          this.selectedRepairs[0].units.some(unit => x.units.includes(unit))
        );
      } else {
        // no column unit filter selected and no repairs are selected
        data = this.repairsData;
      }

      return data.sort((a, b) => {
        const dateA = new Date(a.createdDate);
        const dateB = new Date(b.createdDate);
        return dateA - dateB;
      });
    },
    selectedRepairs() {
      return this.repairsData.filter((x) => x.selected === true);
    },
    requestedAndResubmitNotes(){
      //combine the selectedWorkOrder's changeNotes and resubmitNotes into one array
      let mergedNotes = [];

      if(this.selectedWorkOrder.changeNotes && this.selectedWorkOrder.changeNotes.length > 0){
        mergedNotes = this.selectedWorkOrder.changeNotes.map(note => ({
                        ...note,
                        noteType: 'request'
                      }));
      }

      if(this.selectedWorkOrder.resubmitNotes && this.selectedWorkOrder.resubmitNotes.length > 0){
        const resubmitNotes = this.selectedWorkOrder.resubmitNotes.map(note => ({
                                ...note,
                                noteType: 'resubmit'
                              }));
        mergedNotes.push(...resubmitNotes);
      }

      // sort the notes by date descending
      mergedNotes.sort((a, b) => b.time - a.time);

      return mergedNotes;
    },
    sendNotiEmailOptionDisabled(){
      // this will determine if the "Send notification email" checkbox is enabled or not on the request changes modal
      if(this.orgSettings && this.orgSettings.work_orders && this.orgSettings.work_orders.requestedChangesNotificationEmailEnabled !== undefined){
        return !this.orgSettings.work_orders.requestedChangesNotificationEmailEnabled;
      } else{
        return true;
      }
    }
  },
  watch: {
    parentDataLoaded(newValue, oldValue){
      if(newValue !== oldValue && newValue === true){
        this.workOrdersData = lodash.cloneDeep(this.workOrders);
        this.repairsData = lodash.cloneDeep(this.repairs);
        // board freshness baseline
        this.boardDataLastPull = Date.now();
        this.woLastPull = {};
        // sort work orders into appropriate columns
        this.filterAllColumns();
        this.filterWorkOrdersNotRendered();
        this.loadEmptyWorkOrders();

        // check if a work order number was passed as a paramater in the url
        if (this.$route.params.wonumber) {
          // set the work order as the selected work order and open the modal to view it
          let workOrdersDataCopy = lodash.cloneDeep(this.workOrdersData);
          const specificWorkOrderIndex = workOrdersDataCopy.findIndex((x) => x.number === this.$route.params.wonumber);
          this.populateWorkOrder(workOrdersDataCopy[specificWorkOrderIndex]);
        }
      }
    },
    currentUserHasUnsavedWorkOrderChanges(newValue, oldValue) {
      // publish the flag so anyone opening this work order can see there is work at risk
      if (newValue === oldValue || !this.currentUserHoldsEditingLock) {
        return;
      }

      updateWorkOrderUnsavedChangesStatus(this.documentID, newValue);
    },
    'selectedWorkOrder.date': function () {
      if (this.submitErrorDate !== null) {
        this.submitErrorDate = null;
      }
    },
    'selectedWorkOrder.units': function () {
      if (this.submitErrorUnit !== null) {
        this.submitErrorUnit = null;
      }
    },
    'selectedWorkOrder.odometer': function () {
      if (this.submitErrorOdometer !== null) {
        this.submitErrorOdometer = null;
      }
    },
    'selectedWorkOrder.hourMeter': function () {
      if (this.submitErrorHourMeter !== null) {
        this.submitErrorHourMeter = null;
      }
    },
    newIssue() {
      if (this.submitErrorIssue !== null) {
        this.submitErrorIssue = null;
      }
      if (this.submitErrorAddIssue !== null) {
        this.submitErrorAddIssue = null;
      }
    },
    'selectedRepair.issue': function () {
      if (this.submitErrorEditIssue !== null) {
        this.submitErrorEditIssue = null;
      }
    },
    'selectedRepair.repair': function () {
      if (this.submitErrorEditRepair !== null) {
        this.submitErrorEditRepair = null;
      }
    },
    'selectedWorkOrderEdit.odometer': function () {
      if (this.submitErrorOdometerEdit !== null) {
        this.submitErrorOdometerEdit = null;
      }
    },
    'selectedWorkOrderEdit.hourMeter': function () {
      if (this.submitErrorHourMeterEdit !== null) {
        this.submitErrorHourMeterEdit = null;
      }
    },
    'selectedRepair.repairCost': function () {
      if (this.submitErrorEditRepairCost !== null) {
        this.submitErrorEditCost = null;
      }
    },
  }
};

if (module && module.hot) {
  // this is to clear our interval when the module is replaced during development
  // if we don't do this and we are developing this component, the intervals can persist until the page is closed
  // or the page is refreshed
  module.hot.dispose(() => {
    if (hbInterval) {
      clearInterval(hbInterval);
      hbInterval = null;
    }
  });
}
</script>
<style lang="scss">
.wrapText {
  word-break: break-all;
  hyphens: auto;
}

.test {
  width: auto;
}

.collapsed {
  background-color: #556ee6;
}

.collapsed::after {
  background-color: #556ee6;
  content: 'View';
}

.not-collapsed {
  background-color: #f46a6a;
}

.not-collapsed::after {
  background-color: #f46a6a;
  content: 'Hide';
}

.speech-bubble {
  background-color: #eff2f7;
  padding: 1rem;
  border-radius: 0.75rem;
}

.workorder-notes{
  background-color: #e8ebf8;
  border-radius: 3px;
  height: min-content;
  padding: 0.5rem;
}

.edit-note-icon{
  color:#495057;
  font-size: 15px;
}

.edit-note-icon:hover{
  cursor: pointer;
  color:#556ee6;
  transition: 0.2s ease-in;
}

.wo-card-issues-text:hover{
  cursor: pointer;
  color: #34c38f;
}

.wo-card-issues-text-selected:hover{
  cursor: pointer;
  color: #ececec;
}

.wo-card-issues-dropdown{
  background-color: #dcfde3ca;
  border-radius: 5px !important;
}

.issue-details-list{
  list-style: none;
  li{
    margin-bottom: 1rem;
  }
}

.issue-details-field{
  color: #556ee6;
}

.issue-details-sub-field{
  color: #7f8bc9;
}

.scroll-container{
  overflow-x: scroll;
  white-space: nowrap;
  scroll-snap-align: start;
}

.scroll-column{
  display: inline-block;
  margin-right: 2rem;
}

.column-width-2{
  width: calc(50.95% - 2rem);
}

.column-width-3{
  width: calc(34.1% - 2rem);
}

.wo-column-card-container{
  height: 60vh;
  overflow-y: scroll;
}

.card-body-grey{
  background-color: #ccc;
}

.remove-view-button-title::after {
  content: none !important;
}

.checkbox-border{
  padding-bottom: 0.3rem;
  border-bottom: 1px solid black;
}

.badge-shrink{
  font-size: 55% !important;
}

@media (min-width: 1390px) {
    // xlarge viewport
    .badge-shrink{
      font-size: 65% !important;
    }
 }

 @media (min-width: 1600px) {
  // xlarge viewport
  .badge-shrink{
    font-size: 75% !important;
  }
}

::-webkit-scrollbar {
  width: 6px;
}

::-webkit-scrollbar-track {
  border-radius: 12px;
  background-color: #edebeb00;
  border: 1px solid #ffffff00;
  box-shadow: inset 0 0 6px rgba(89, 89, 89, 0);
}

::-webkit-scrollbar-thumb {
  border-radius: 12px;
  background-color: #556de6ca;
}

::-webkit-scrollbar-thumb:hover {
  cursor: pointer;
}

.checkbox-disabled {
  .custom-control-input:disabled ~ .custom-control-label::before {
    background-color: #d3d3d3 !important;
    border-color: #b0b0b0 !important;
  }
}

.is-invalid-datepicker .mx-input,
.is-invalid-datepicker .mx-input:focus {
  border-color: #dc3545 !important;
}

.is-invalid-vselect .vs__dropdown-toggle {
  border-color: #dc3545;
}

.vs__dropdown-menu .vs__dropdown-option:has(.deactivated-option) {
  display: none !important;
}

@supports not (-moz-appearance: none) {
  /* These styles will apply to all browsers besides Firefox */
  .full-height-td {
    height: 1px;
  }
}

@supports (-moz-appearance: none) {
  /* These styles will only apply on Firefox, Firefox requires seperate styles for the table because setting height 1px for the div within a td doesn't act properly */
  .full-height-td > div{
    min-height: 3rem;
    background-color: #f3f3f3;
    border-radius: 3px;
    padding: 0.5rem;
  }
}

.source-of-parts-column{
  max-width: 250px;
  overflow: hidden;
}

.optional-cell{
  max-width: 100px;
}

/* Blue outline for all interactive table cell elements */
.wo-table-cell:focus {
  outline: 2px solid #556ee6;
  outline-offset: 2px;
  border-radius: 3px;
}

i.wo-table-cell:focus {
  outline-offset: 3px;
  border-radius: 2px;
}

button.wo-table-cell:focus,
.btn.wo-table-cell:focus {
  outline: 2px solid #556ee6 !important;
  outline-offset: 2px !important;
  box-shadow: 0 0 0 3px rgba(85, 110, 230, 0.3) !important;
}

/* Green outline for blue (primary) footer buttons */
.wo-footer-btn:focus,
.wo-footer-btn:focus-visible {
  outline: 2px solid #34c38f !important;
  outline-offset: 2px;
  box-shadow: 0 0 0 3px rgba(52, 195, 143, 0.3) !important;
}

.disabled-look {
  opacity: 0.65;
  cursor: not-allowed;
}

/* Shrink a table column to exactly fit its content on one line. width: 1% +
   white-space: nowrap collapses the column to its content width in an auto-layout
   table (used by the letter column and the Edit/actions column). */
.col-fit-content {
  width: 1%;
  white-space: nowrap;
}

/* Render the two Edit-column action buttons (view details + actions dropdown)
   as equal-size squares side by side. */
.wo-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 31px;
  height: 31px;
  padding: 0 !important;
  line-height: 1;
}
.wo-action-btn i {
  font-size: 1.1rem;
}

/* Make the work-order issue table fill the modal on large screens and steer the
   extra width toward the long-text columns. These are width hints for the auto
   table layout (so columns still flex to their content), and they have higher
   specificity than the global .word-break-column (10%) / .source-of-parts-column
   caps, so they win here without affecting any other table. */
.wo-repairs-table {
  width: 100%;
}
.wo-repairs-table .wo-col-issue { width: 22%; }
.wo-repairs-table .wo-col-repair { width: 22%; }
.wo-repairs-table .wo-col-source { width: 18%; max-width: none; }
.wo-repairs-table .wo-col-repairedby { width: 12%; }
.wo-repairs-table .wo-col-docs { width: 1%; }
/* a 100%-wide select adds nothing to the column's width, so give it room for its longest label */
.wo-repairs-table .wo-col-docs select { min-width: 6.5rem; }
.wo-repairs-table .wo-col-optional { width: 1%; }
.wo-repairs-table .wo-col-rc { width: 5%; }

</style>